// Libraries
import {
  // Functions
  functions,
  httpsCallable,
  getDoc,
  getDocs,
  query,
  collection,
  where,
  increment,
  serverTimestamp,
  writeBatch,
  doc,
  firestore,
  deleteDoc,
  limit,
  updateDoc,
  setDoc,
  runTransaction,
  // Storage
  storage,
  ref,
  getDownloadURL,
  getMetadata,
  getBytes,
} from "@/firebase/init";
import moment from "moment-timezone";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import { Buffer } from "buffer";
import LeaflinkClient from "@/utils/LeaflinkClient";
import { buildOrderEmailHtml } from "@/utils/orderEmailHtml";
import {
  createActivity,
  createInventoryActivity,
} from "@/services/activityService";

import store from "@/store/index.js";
import { addBreadcrumb, logWorkflowWarning } from "@/plugins/sentry";

let capitalize = (string) => {
  return string.replace(/\b\w/g, (c) => c.toUpperCase());
};

class SalesOrderClient {
  constructor(orderID, { skipInit = false } = {}) {
    this.orderID = orderID;
    this.externalAPI = httpsCallable(functions, "externalAPI");
    this.order = null;
    this.lineItems = [];
    this.payments = [];
    this.categories = [];
    this.priceOverrides = [];
    this.fulfillmentLocations = [];
    this.orderIsExternal = false;
    this.externalOrderType = null;
    this.externalUpdates = {
      leaflink: [],
    };

    if (!skipInit) {
      console.log("Sales Order Client Initialized");
      this.getOrder();
      this.getLineItems();
      this.getPayments();
    }
  }

  _sumAppliedPayments(payments = this.payments) {
    return payments
      .filter(
        (payment) =>
          !payment.flags?.reversed
      )
      .reduce(
        (sum, payment) =>
          sum + Number(payment.amount) - Number(payment.creditsApplied ?? 0),
        0,
      );
  }

  async _checkBalanceAnomaly(action, extra = {}) {
    await this.getPayments();

    const hasDueOverride =
      extra.balanceDue !== undefined || extra.newBalance !== undefined;
    const hasTotalOverride =
      extra.balanceTotal !== undefined || extra.newTotal !== undefined;
    if (!hasDueOverride || !hasTotalOverride) {
      await this.getOrder();
    }

    const due = parseFloat(
      extra.balanceDue ?? extra.newBalance ?? this.order?.balance?.due,
    );
    const total = parseFloat(
      extra.balanceTotal ?? extra.newTotal ?? this.order?.balance?.total,
    );
    const status = this.order?.status;
    if (isNaN(due) || isNaN(total)) return;

    const contextBase = {
      orderId: this.orderID,
      balance: { ...this.order?.balance, due, total },
      status,
      ...extra,
    };

    // Negative due is never valid regardless of status.
    if (due < 0) {
      logWorkflowWarning("Negative balance.due detected", {
        workflow: "sales-order",
        action,
        context: contextBase,
      });
      return;
    }

    const paymentsApplied = this._sumAppliedPayments();
    const impliedPaid = total - due;
    if (Math.abs(paymentsApplied - impliedPaid) > 0.01) {
      logWorkflowWarning("Balance/payment mismatch detected", {
        workflow: "sales-order",
        action,
        context: {
          ...contextBase,
          paymentsApplied,
          impliedPaid,
          delta: parseFloat((impliedPaid - paymentsApplied).toFixed(2)),
        },
      });
    }
  }

  async getOrder() {
    try {
      const orderDoc = await getDoc(
        doc(firestore, "salesOrders", this.orderID),
      );
      this.order = {
        id: orderDoc.id,
        ...orderDoc.data(),
      };

      if (!this.order.account?.id?.length > 0) {
        const orderDoc = await getDoc(
          doc(firestore, "salesOrders-draft", this.orderID),
        );
        this.order = {
          id: orderDoc.id,
          ...orderDoc.data(),
        };
      }

      if (this.order.flags?.external) {
        this.orderIsExternal = true;
        this.externalOrderType = null;

        const payableToRef = collection(firestore, "payableTo");
        const state = where(
            "location.state.stateID",
            "==",
            this.order.location.shipping.state.stateID,
          ),
          brandWhere = where("brandID", "==", this.order.brand.id);
        const q = query(payableToRef, state, brandWhere);
        const response = await getDocs(q, limit(1));

        if (response.empty) {
          logWorkflowWarning("getOrder: payableTo not found for external order", {
            workflow: "sales-order",
            action: "get-order-payable-to",
            context: {
              orderId: this.orderID,
              stateID: this.order.location.shipping.state.stateID,
              brandID: this.order.brand.id,
            },
          });
          return this.order;
        }

        let payableTo = {
          id: response.docs[0].id,
          ...response.docs[0].data(),
        };

        if (
          payableTo?.flags?.external &&
          payableTo?.externalPlatform === "leaflink" &&
          this.order.externalIDs?.leaflink?.length
        ) {
          this.externalOrderType = "leaflink";
          let defaultUpdates = [
            "orderCreated",
            "orderUpdated",
            "inventoryUpdated",
            "statusUpdated",
            "paymentsApplied",
          ];
          this.externalUpdates.leaflink =
            payableTo?.integrations?.leaflink?.hasOwnProperty("pushUpdates")
              ? payableTo?.integrations?.leaflink?.pushUpdates
              : defaultUpdates;
        }

        if (
          this.externalOrderType === "leaflink" &&
          this.order.externalIDs.leaflink
        ) {
          this.leaflinkClient = new LeaflinkClient(
            this.order.externalIDs.leaflink,
            this.order,
          );
        }
      }
    } catch (error) {
      throw error;
    }

    return this.order;
  }

  // GET
  async getLineItems() {
    try {
      const lineItemDocs = await getDocs(
        query(
          collection(firestore, "salesOrders_lineItems"),
          where("relatedID", "==", this.orderID),
        ),
      );

      let lineItems = lineItemDocs.docs.map((doc) => {
        return {
          id: doc.id,
          ...doc.data(),
        };
      });

      let productLots = [];

      await Promise.all(
        lineItems.flatMap((lineItem) =>
          (lineItem.productLots ?? []).map(async (productLot) => {
            const productLotDoc = doc(firestore, "productLots", productLot.id);
            const productLotResponse = await getDoc(productLotDoc);
            productLot.locationData = productLotResponse.data().locationData;

            let availableAmount = Number(
              productLotResponse.data().available ??
                productLotResponse.data().units,
            );

            if (!productLot.locationData?.length) {
              const locationDataDocs = await getDocs(
                collection(
                  firestore,
                  "productLots",
                  productLot.id,
                  "locationData",
                ),
              );

              let locationData = locationDataDocs.docs.map((doc) => {
                return {
                  id: doc.id,
                  ...doc.data(),
                };
              });

              let transfer = 0;

              if (productLot.locationData?.length) {
                transfer = productLot.locationData.reduce(
                  (acc, data) => acc + (data.transfer ?? 0),
                  0,
                );
              }

              productLots.push({
                id: productLot.id,
                available: availableAmount - (transfer ?? 0),
                originalAvailable: availableAmount - (transfer ?? 0),
                total: Number(productLotResponse.data().units),
                product: productLotResponse.data().product.name,
                productID: productLotResponse.data().product.id,
                productCategory: productLotResponse.data().productCategory.name,
                skuID: productLotResponse.data().skuID,
                trackAndTraceID: productLotResponse.data().trackAndTraceID,
                fileType: productLotResponse.data().fileType,
                units: Number(productLotResponse.data().units),
                expiration: moment(
                  productLotResponse.data().expiration.seconds * 1000,
                ).toDate(),
                path:
                  productLotResponse.data().fileType === "upload" &&
                  productLotResponse.data().path &&
                  typeof productLotResponse.data().path === "string"
                    ? await getDownloadURL(
                        ref(storage, productLotResponse.data().path),
                      ).catch(() => null)
                    : productLotResponse.data().path ?? null,
                relatedLineItems:
                  productLotResponse.data().relatedLineItems ?? [],
                locationData,
              });
            }
          })
        )
      );

      if (!lineItems.length > 0) {
        logWorkflowWarning("getLineItems: fresh query returned no results, falling back to order.lineItems", {
          workflow: "sales-order",
          action: "get-line-items-fallback",
          context: { orderId: this.orderID, fallbackCount: this.order.lineItems?.length ?? 0 },
        });
        lineItems = this.order.lineItems;
      }

      this.productLots = productLots;
      this.lineItems = lineItems;

      return lineItems;
    } catch (error) {
      throw error;
    }
  }
  async getPayments() {
    try {
      const payments = await getDocs(
        query(
          collection(firestore, "salesOrders_payments"),
          where("relatedID", "==", this.orderID),
        ),
      );

      this.payments = payments.docs.map((payment) => {
        return {
          id: payment.id,
          ...payment.data(),
        };
      });
      return this.payments;
    } catch (error) {
      throw error;
    }
  }
  async getRouteDetails(origin, destination) {
    try {
      const { data } = await this.externalAPI({
        method: "GET",
        url: `https://maps.googleapis.com/maps/api/directions/json?key=${process.env.VUE_APP_GOOGLE}&origin=${origin}&destination=${destination}`,
      });

      if (data?.status?.toLowerCase() === "ok") {
        return data.routes[0].legs[0].steps
          .map((step) => {
            return `${step.html_instructions.replace(/<[^>]+>/g, "")} (${
              step.distance.text
            })`;
          })
          .join(". ");
      } else {
        throw new Error("Status");
      }
    } catch (error) {
      console.error(error);
      logWorkflowWarning(`getRouteDetails: Google Maps API failed — ${error.message}`, {
        workflow: "sales-order",
        action: "google-maps-directions",
        context: { orderId: this.orderID, origin, destination },
      });
      return null;
    }
  }
  async getProductCategories() {
    this.categories = [];

    const productsRef = collection(firestore, "productCategories"),
      forSale = where("flags", "array-contains", "forSale"),
      active = where("active", "==", true),
      brand = where("brandID", "==", this.order.brand.id);

    const q = query(productsRef, active, brand, forSale);
    const response = await getDocs(q);

    if (!response.empty) {
      this.categories = response.docs
        .filter(
          (category) =>
            category
              .data()
              .states?.includes(this.order.location.shipping.state.stateID) &&
            category.data().visibility !== "hidden",
        )
        .map((categories) => {
          let caseQTY = categories.data().caseQTY;

          if (
            categories.data().flags.includes("variableCases") &&
            categories
              .data()
              .variableCases?.find(
                (item) =>
                  item.stateID === this.order.location.shipping.state.stateID,
              )
          ) {
            caseQTY = categories
              .data()
              .variableCases.find(
                (item) =>
                  item.stateID === this.order.location.shipping.state.stateID,
              ).qty;
          }

          return {
            id: categories.id,
            name: categories.data().name,
            flags: categories.data().flags,
            caseQTY,
            price: categories.data().price,
            variablePrice: categories.data().variablePrice,
            listPrice: {
              cod: categories.data().listPrice?.cod ?? null,
              net: categories.data().listPrice?.net ?? null,
            },
          };
        })
        .sort((a, b) => (a.name > b.name ? 1 : -1));
    }
  }
  async getPriceOverrides() {
    this.priceOverrides = [];

    const priceOverridesRef = collection(firestore, "priceOverrides");
    const state = where(
      "state.stateID",
      "==",
      this.order.location.shipping.state.stateID,
    );
    const brand = where("brand.id", "==", this.order.brand.id);
    const active = where("active", "==", true);
    const q = query(priceOverridesRef, state, active, brand);
    const response = await getDocs(q);

    if (!response.empty) {
      this.priceOverrides = response.docs.flatMap((priceOverride) => {
        let category = this.categories.find(
          (category) => category.id === priceOverride.data().category.id,
        );

        if (!category) return [];

        let categoryName = category.name;
        let pricing = category.flags.includes("hasVariablePricing")
          ? "Variable"
          : "Static";
        let accountSpecific = priceOverride.data().hasOwnProperty("account");
        let productSpecific = priceOverride.data().hasOwnProperty("product");

        return {
          id: priceOverride.id,
          ...priceOverride.data(),
          category,
          pricing,
          accountSpecific,
          productSpecific,
          listPrice: {
            cod: priceOverride.data().listPrice?.cod ?? null,
            net: priceOverride.data().listPrice?.net ?? null,
          },
        };
      });
    }
  }
  getPriceOverrideForProduct(product) {
    let overriddenCategoryIDs = this.priceOverrides.map(
      (override) => override.category.id,
    );
    if (overriddenCategoryIDs.includes(product.categoryID)) {
      //  Price Override
      let override;
      if (
        this.priceOverrides
          .filter(
            (item) =>
              item.category.id === product.categoryID &&
              item.product &&
              item.product.id === product.productID,
          )
          .find(
            (option) =>
              option.productSpecific && option.product.id === product.productID,
          )
      ) {
        // SKU Specific Price Override
        override = this.priceOverrides
          .filter((item) => item.category.id === product.categoryID)
          .find(
            (option) =>
              option.productSpecific && option.product.id === product.productID,
          );
      } else {
        // Payable To Price Override
        override = this.priceOverrides.find(
          (item) =>
            item.category.id === product.categoryID &&
            item.accountSpecific === false &&
            item.productSpecific === false,
        );
      }
      if (override) {
        return override;
      } else {
        return null;
      }
    }
  }
  async getFulfillmentLocations() {
    this.fulfillmentLocations = [];
    const stateDoc = doc(
      firestore,
      "states",
      this.order.location.shipping.state.stateID,
    );
    const stateResponse = await getDoc(stateDoc);
    let fulfillmentLocations = [];
    fulfillmentLocations.push({
      locationID: stateResponse.data().fulfillmentLocation,
      mainFulfillmentLocation: true,
    });

    let additionalFulfillmentLocations =
      stateResponse.data().additionalFulfillmentLocations ?? [];
    additionalFulfillmentLocations = additionalFulfillmentLocations.map(
      (locationID) => {
        return {
          locationID,
          mainFulfillmentLocation: false,
        };
      },
    );
    fulfillmentLocations = fulfillmentLocations.concat(
      additionalFulfillmentLocations,
    );

    let capitalize = (word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    };

    fulfillmentLocations = await Promise.all(
      fulfillmentLocations.flatMap(async (location) => {
        if (!location.locationID) return;
        const locationDoc = doc(firestore, "locations", location.locationID);
        const response = await getDoc(locationDoc);
        if (!response.exists()) return [];

        let streetNumber = response.data().location?.street?.number ?? "";
        let streetName = capitalize(
          response.data().location?.street?.name ?? "",
        );
        let city = capitalize(response.data().location?.city ?? "");
        let state = response.data().location?.state?.abbreviation ?? "";
        state = state.toUpperCase();
        let zipCode = response.data().location?.zipCode ?? "";

        let locationAddress = `${streetNumber} ${streetName}, ${city}, ${state}  ${zipCode}`;

        if (
          !this.order.fulfillmentLocation &&
          location.mainFulfillmentLocation
        ) {
          this.order.fulfillmentLocation = {
            id: location.locationID,
            name: capitalize(response.data().name),
          };
        }

        return {
          ...location,
          manager: response.data().manager ?? null,
          phoneNumber: response.data().phoneNumber ?? null,
          name: capitalize(response.data().name),
          address: locationAddress,
          type:
            store.getters.getLocationTypes.find(
              (item) => item.value === response.data().type,
            )?.text ?? "",
        };
      }),
    );

    this.fulfillmentLocations = fulfillmentLocations;
  }

  // POST
  async transitionOrderStatus(newStatus) {
    addBreadcrumb("transitionOrderStatus: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, toStatus: newStatus },
    });
    try {
      // Get current order data to create activity log
      await this.getOrder();

      const oldValue = store.getters.getOrderStatus.find(
        (status) => status.value === this.order.status,
      );
      const newValue = store.getters.getOrderStatus.find(
        (status) => status.value === newStatus,
      );

      await updateDoc(doc(firestore, "salesOrders", this.orderID), {
        status: newStatus,
      });

      // Create activity for order status transition
      await createActivity({
        type: "status",
        action: "Order status transitioned",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} status changed from ${
          oldValue?.text || this.order.status
        } to ${newValue?.text || newStatus}`,
        relatedTo: "salesOrders",
        relatedID: [this.orderID],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "medium",
        priority: "medium",
        changeData: {
          field: "orderStatus",
          oldValue: {
            text: oldValue?.text || this.order.status,
            value: this.order.status,
            color: oldValue?.color || "#000000",
          },
          newValue: {
            text: newValue?.text || newStatus,
            value: newStatus,
            color: newValue?.color || "#000000",
          },
          orderNumber: this.order.orderNumber.internal,
          transitionType: "manual",
          externalOrderUpdated:
            this.externalUpdates.leaflink.includes("statusUpdated"),
        },
      });
    } catch (error) {
      throw error;
    }
  }

  async removePayment(payment, reason) {
    addBreadcrumb("removePayment: start", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        paymentId: payment?.id,
        paymentAmount: payment?.amount,
        paymentType: payment?.paymentType,
        currentDue: this.order?.balance?.due,
        orderStatus: this.order?.status,
      },
    });
    let shouldReturn = false;
    try {
      const batch = writeBatch(firestore);
      const salesOrderDoc = doc(firestore, "salesOrders", this.orderID);
      const brandStatsDoc = doc(
        firestore,
        "brandStats",
        `${this.order.account.id}_${this.order.brand.id}`,
      );

      const brandStats = await getDoc(brandStatsDoc);
      await this.getOrder();
      let due = Number(this.order.balance.due);
      let status = this.order.status;
      let staff = [];

      let leaflinkEmail = null;
      let userLeaflinkInfo = null;
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
              let data = await this.leaflinkClient.getUsers();
              staff = data.results;
              const integrationsRef = collection(firestore, "integrations");
              const platform = where("platform", "==", "leaflink");
              const integrationsQ = query(integrationsRef, platform);
              const integrationsResponse = await getDocs(
                integrationsQ,
                limit(1),
              );

              if (!integrationsResponse.empty) {
                leaflinkEmail = integrationsResponse.docs[0].data().email;
                console.log(leaflinkEmail);
              }

              userLeaflinkInfo = staff.find(
                (staffMember) => staffMember.email === leaflinkEmail,
              );
              console.log(userLeaflinkInfo);

              if (!leaflinkEmail) {
                console.error("removePayment: aborting — LeafLink integration email not configured", { orderId: this.orderID, paymentId: payment?.id });
                logWorkflowWarning("removePayment: aborting — LeafLink integration email not configured", {
                  workflow: "sales-order",
                  action: "remove-payment-leaflink-preflight",
                  context: { orderId: this.orderID, paymentId: payment?.id },
                });
                return;
              }
            }
            break;
          default:
            break;
        }
      }

      let paymentType =
        store.getters.getPaymentTypes.find(
          (type) => type.value === payment.paymentType,
        ) ?? "[Error]";

      console.log(paymentType);

      let paymentAmount = Number(parseFloat(payment.amount).toFixed(2));
      let paymentCreditsApplied = Number(
        parseFloat(payment.creditsApplied).toFixed(2),
      );

      let amountToAdd = paymentAmount - paymentCreditsApplied;
      due = Number(parseFloat(due).toFixed(2)) + amountToAdd;
      if (this.orderIsExternal) {
        addBreadcrumb("leaflink: removePayment reversal start", {
          workflow: "sales-order",
          data: {
            orderId: this.orderID,
            paymentId: payment?.id,
            paymentAmount: payment?.amount,
            externalOrderType: this.externalOrderType,
          },
        });
        try {
          switch (this.externalOrderType) {
            case "leaflink":
              if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
                let paymentData = {
                  order: this.order.externalIDs.leaflink,
                  recorded_by: userLeaflinkInfo.id,
                  total: {
                    amount: Number(
                      parseFloat(
                        (payment.amount - payment.creditsApplied) * -1,
                      ).toFixed(2),
                    ),
                    currency: "usd",
                  },
                  payment_date: moment().toISOString(),
                  reason: reason || "",
                  payment_type:
                    paymentType.value === "chargeOff" ||
                    paymentType.value === "sample-clearBalance" ||
                    paymentType.value === "promo-clearBalance" ||
                    paymentType.value === "promotionCredit" ||
                    paymentType.value === "vendorCredit"
                      ? "other"
                      : paymentType.value,
                  check_number: payment.checkNumber || "",
                };
                await this.leaflinkClient.createPayment(paymentData);
              }
              break;
            default:
              break;
          }
        } catch (e) {
          console.log(e);
          // LeafLink reversal failed — MyTrace batch will NOT commit (shouldReturn = true).
          // This is a silent abort: no error is thrown so the caller sees success.
          logWorkflowWarning(`LeafLink: payment reversal failed — MyTrace reversal also aborted — ${e.message}`, {
            workflow: "sales-order",
            action: "leaflink-payment-removal",
            context: {
              orderId: this.orderID,
              paymentId: payment?.id,
              paymentAmount: payment?.amount,
              balance: this.order?.balance,
            },
          });
          shouldReturn = true;
          return;
        }
      }

      let paymentDoc = await getDoc(
        doc(firestore, "salesOrders_payments", payment.id),
      );

      let capitalizedName =
        `${store.getters.getUser.name.first} ${store.getters.getUser.name.last}`.replace(
          /\b\w/g,
          (c) => c.toUpperCase(),
        );

      let reversalNotes = `Payment reversed by ${capitalizedName} on ${moment().format(
        "MM/DD/YYYY",
      )} with reason: ${reason}`;

      let undoNotes =
        !paymentDoc.data().notes || paymentDoc.data().notes.length === 0
          ? reversalNotes
          : `${reversalNotes} | ${paymentDoc.data().notes}`;

      // Reverse payment
      batch.set(
        doc(firestore, "salesOrders_payments", payment.id),
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          notes: undoNotes,
          flags: {
            reversed: true,
          },
        },
        { merge: true },
      );

      if (payment.overpaymentCreditID) {
        // Set overpayment credit to inactive & remove amount from brandStats doc
        addBreadcrumb("removePayment: deleting overpayment credit", {
          workflow: "sales-order",
          data: {
            orderId: this.orderID,
            overpaymentCreditID: payment.overpaymentCreditID,
            creditsApplied: payment.creditsApplied,
          },
        });
        batch.delete(
          doc(firestore, "promotionCredits", payment.overpaymentCreditID),
        );
        batch.set(
          brandStatsDoc,
          {
            metrics: {
              promotionCredits: {
                total: increment(-payment.creditsApplied),
                available: increment(-payment.creditsApplied),
              },
            },
          },
          { merge: true },
        );
      }

      // Create activity for payment removal
      await createActivity({
        type: "financial",
        action: "Payment reversed",
        item: `${paymentType.text}: -${new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(payment.amount)}`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          paymentAmount: payment.amount,
          paymentType: paymentType.value,
          checkNumber: payment.checkNumber,
          paymentDate: moment(payment.paymentDate.seconds * 1000).format(
            "YYYY-MM-DD",
          ),
          reversalReason: reason,
          orderNumber: this.order.orderNumber,
          monetaryValue: payment.amount,
        },
      });

      if (payment.paymentType === "promotionCredit") {
        const promotionCreditDoc = doc(
          firestore,
          "promotionCredits",
          payment.promotionCredit,
        );
        addBreadcrumb("removePayment: restoring promotion credit balance", {
          workflow: "sales-order",
          data: {
            orderId: this.orderID,
            promotionCreditId: payment.promotionCredit,
            paymentAmount: payment.amount,
          },
        });
        // const selectedPromotionCredit = this.response.promotionCredits.find(
        //   (option) => option.value === payment.promotionCredit.id
        // );
        // const available = Number(
        //   parseFloat(
        //     selectedPromotionCredit.available + payment.amount
        //   ).toFixed(2)
        let promotionCreditData = await getDoc(promotionCreditDoc);
        let selectedPromotionCredit = promotionCreditData.data();

        let available = Number(
          parseFloat(
            selectedPromotionCredit.available + payment.amount,
          ).toFixed(2),
        );

        batch.set(
          promotionCreditDoc,
          {
            updated: serverTimestamp(),
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            active: true,
            available: increment(payment.amount),
          },
          { merge: true },
        );

        // Brand Stats - Metrics
        batch.set(
          brandStatsDoc,
          {
            updated: serverTimestamp(),
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            metrics: {
              promotionCredits: {
                available: increment(payment.amount),
              },
            },
          },
          { merge: true },
        );

        // Create activity for promotion credit update during payment reversal
        await createActivity({
          type: "financial",
          action: "Promotion credit updated",
          item: `Promotion credit balance restored: +$${new Intl.NumberFormat(
            "en-US",
            {
              style: "currency",
              currency: "USD",
            },
          ).format(payment.amount)} for Order #${
            this.order.orderNumber.internal
          } - Payment reversal`,
          relatedTo: "promotionCredits",
          relatedID: [promotionCreditDoc.id],
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          account: {
            id: this.order.account.id,
            name: this.order.account.name,
          },
          brand: {
            id: this.order.brand.id,
            name: this.order.brand.name,
          },
          businessImpact: "high",
          priority: "medium",
          changeData: {
            field: "promotionCreditBalance",
            creditAmount: amountToAdd,
            availableAmount: available,
            orderNumber: this.order.orderNumber.internal,
            orderNumberExternal: this.order.flags.external
              ? this.order.orderNumber.external
              : null,
            statusChange:
              selectedPromotionCredit.available === 0 && available > 0
                ? {
                    oldValue: "inactive",
                    newValue: "active",
                  }
                : null,
            updateReason: "payment_reversal",
            monetaryValue: amountToAdd,
          },
        });
      }

      due = Number(parseFloat(due).toFixed(2));

      if (due >= 0) {
        // Status
        if (this.order.status === "complete") {
          //  If the order is already complete but payments are reversed, we
          //  need to set the status back to 'shipped', as the balance will not
          //  be complete anymore

          status = "shipped";
          const oldStatus = store.getters.getOrderStatus.find(
              (option) => option.value === this.order.status,
            ),
            newStatus = store.getters.getOrderStatus.find(
              (option) => option.value === "shipped",
            );

          //  External Order Status
          if (this.orderIsExternal) {
            switch (this.externalOrderType) {
              case "leaflink":
                if (this.externalUpdates.leaflink.includes("statusUpdated")) {
                  addBreadcrumb("leaflink: transitionOrder ship (removePayment status revert)", {
                    workflow: "sales-order",
                    data: { orderId: this.orderID, fromStatus: this.order?.status },
                  });
                  try {
                    await this.leaflinkClient.transitionOrder("ship");
                  } catch (error) {
                    logWorkflowWarning(`LeafLink: transitionOrder("ship") (removePayment) failed — ${error.message}`, {
                      workflow: "sales-order",
                      action: "leaflink-remove-payment-transition-ship",
                      context: { orderId: this.orderID, balance: this.order?.balance },
                    });
                    throw error;
                  }
                }
                break;
              default:
                break;
            }
          }

          // Create activity for status change
          await createActivity({
            type: "status",
            action: "Order status updated",
            item: `Order #${this.order.orderNumber.internal.toUpperCase()} status reverted from ${
              oldStatus.text
            } to ${newStatus.text} due to payment reversal`,
            relatedTo: "salesOrders",
            relatedID: [salesOrderDoc.id],
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            account: {
              id: this.order.account.id,
              name: this.order.account.name,
            },
            brand: {
              id: this.order.brand.id,
              name: this.order.brand.name,
            },
            businessImpact: "high",
            priority: "high",
            changeData: {
              field: "status",
              oldValue: {
                text: oldStatus.text,
                value: oldStatus.value,
                color: oldStatus.color,
              },
              newValue: {
                text: newStatus.text,
                value: newStatus.value,
                color: newStatus.color,
              },
              reason:
                "Payment reversal caused order completion to revert to shipped status",
              orderNumber: this.order.orderNumber,
            },
          });
        }
      }

      // Order & Account Balance
      batch.set(
        salesOrderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          balance: {
            due: parseFloat(due).toFixed(2),
          },
          flags: {
            paid: due <= 0,
          },
          status,
        },
        { merge: true },
      );
      // Account Balances

      if (payment.paymentType === "chargeOff") {
        //  Charge off balance
        let currentChargeOffBalance = brandStats.data().metrics.chargeOffBalance
          ? brandStats.data().metrics.chargeOffBalance
          : 0;

        const newChargeOffBalance = Number(
          parseFloat(currentChargeOffBalance - payment.amount).toFixed(2),
        );
        batch.set(
          brandStatsDoc,
          {
            metrics: {
              chargeOffBalance: newChargeOffBalance,
            },
          },
          { merge: true },
        );
      }

      //  General Balance

      //  The balance for this order is added to the account's balance on 'shipped' status.
      //  This means that we shouldn't be decreasing the account's balance unless the
      //  status is shipped.

      if (this.order.status === "complete" || this.order.status === "shipped") {
        let currentAccountBalance = brandStats.data().metrics.balance;
        const newBalance = Number(
          parseFloat(currentAccountBalance + amountToAdd).toFixed(2),
        );
        batch.set(
          brandStatsDoc,
          {
            metrics: {
              balance: newBalance,
              // credits: increment(this.accountCredits),
            },
          },
          { merge: true },
        );
      }

      if (!shouldReturn) {
        // Don't commit if there were errors submitting leaflink payment(s)
        await batch.commit();
        addBreadcrumb("removePayment: committed", {
          workflow: "sales-order",
          data: { orderId: this.orderID, newDue: due },
        });
        await this._checkBalanceAnomaly("remove-payment", { balanceDue: due });
      }
    } catch (error) {
      throw error;
    }
  }

  async applyPayment(data) {
    addBreadcrumb("applyPayment: start", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        paymentCount: Array.isArray(data) ? data.length : 1,
        currentDue: this.order?.balance?.due,
        currentTotal: this.order?.balance?.total,
        orderStatus: this.order?.status,
      },
    });
    try {
      await this.getOrder();
      await this.getLineItems();
      await this.getPayments();

      // Handle both single payment and multiple payments
      const payments = Array.isArray(data) ? data : [data];

      const batch = writeBatch(firestore);
      const salesOrderDoc = doc(firestore, "salesOrders", this.order.id);
      const brandStatsDoc = doc(
        firestore,
        "brandStats",
        `${this.order.account.id}_${this.order.brand.id}`,
      );
      const brandStats = await getDoc(brandStatsDoc);
      let updates = [],
        staff = [];
      let status = this.order.status,
        paid = false,
        userExists = true,
        due = Number(parseFloat(this.order.balance.due).toFixed(2));

      const payableToRef = collection(firestore, "payableTo");
      const state = where(
          "location.state.stateID",
          "==",
          this.order.location.shipping.state.stateID,
        ),
        brand = where("brandID", "==", this.order.brand.id);
      const q = query(payableToRef, state, brand);
      const response = await getDocs(q, limit(1));

      let payableTo = {
        id: response.docs[0].id,
        ...response.docs[0].data(),
      };

      let overpaymentCreditID = null;

      let leaflinkEmail = null;
      // TODO: Add external order logic
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
              let data = await this.leaflinkClient.getUsers();
              staff = data.results;
              const integrationsRef = collection(firestore, "integrations");
              const platform = where("platform", "==", "leaflink");
              const integrationsQ = query(integrationsRef, platform);
              const integrationsResponse = await getDocs(
                integrationsQ,
                limit(1),
              );

              if (!integrationsResponse.empty) {
                leaflinkEmail = integrationsResponse.docs[0].data().email;
              }

              if (!leaflinkEmail) {
                // Silent abort — payment will NOT be applied to MyTrace either.
                // Caller receives no error and may show a success toast.
                logWorkflowWarning("applyPayment: aborted — LeafLink integration email not found, payment not applied", {
                  workflow: "sales-order",
                  action: "apply-payment-leaflink-preflight",
                  context: { orderId: this.orderID, balance: this.order?.balance, orderStatus: this.order?.status },
                });
                this.loading.submit = false;
                return;
              }
            }
            break;
          default:
            break;
        }
      }

      let userLeaflinkInfo = staff.find(
        (staffMember) => staffMember.email === leaflinkEmail,
      );

      let lastPayment = brandStats.data().metrics.last.payment;
      let totalCreditsApplied = 0;
      let totalPaymentAmount = 0;
      let totalChargeOffAmount = 0;
      let creditsApplied = 0;
      let shouldReturn = false;
      let needsLeafLinkCompleteTransition = false;
      let leaflinkPaymentsToCreate = []; // Store LeafLink payments to create after MyTrace batch commit

      let nonCreditablePaymentTypes = [
        "chargeOff",
        "sample-clearBalance",
        "promo-clearBalance",
        "promotionCredit",
      ];

      let currency = (value) => {
        return Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "usd",
        }).format(value);
      };

      // Track running balance for multiple payments
      let runningBalance = due;

      // Process each payment
      for (
        let paymentIndex = 0;
        paymentIndex < payments.length;
        paymentIndex++
      ) {
        const paymentData = payments[paymentIndex];

        let paymentAmount = Number(parseFloat(paymentData.amount).toFixed(2));
        let paymentType = paymentData.type;
        let paymentDate = paymentData.date;
        let paymentCheckNumber = paymentData.checkNumber;
        let paymentPromotionCredit = paymentData.promotionCredit;
        let paymentNotes = paymentData.notes;

        // For promotion credits, validate amount doesn't exceed the specific credit's available amount OR running balance
        if (paymentType.value === "promotionCredit" && paymentPromotionCredit) {
          const promotionCreditDoc = doc(
            firestore,
            "promotionCredits",
            paymentPromotionCredit,
          );
          const promotionCreditResponse = await getDoc(promotionCreditDoc);
          const selectedPromotionCredit = promotionCreditResponse.data();

          // For credits, limit to the smaller of: credit available amount OR running balance
          let maxAllowedAmount = Math.min(
            selectedPromotionCredit.available,
            runningBalance,
          );

          maxAllowedAmount = Number(parseFloat(maxAllowedAmount).toFixed(2));

          if (paymentAmount > maxAllowedAmount) {
            throw new Error(
              `Payment amount (${paymentAmount}) exceeds maximum allowed amount (${maxAllowedAmount}) - credit available: ${selectedPromotionCredit.available}, running balance: ${runningBalance}`,
            );
          }
        }

        // Check for overpayment using running balance
        if (
          !nonCreditablePaymentTypes.includes(paymentType.value) &&
          paymentAmount > runningBalance
        ) {
          // Apply credits to account if payment is a creditable type and payment amount is greater than running balance
          addBreadcrumb("applyPayment: overpayment credit will be created", {
            workflow: "sales-order",
            data: {
              orderId: this.orderID,
              paymentAmount,
              runningBalance,
              paymentType: paymentType.value,
            },
          });
          creditsApplied =
            Number(parseFloat(paymentAmount).toFixed(2)) -
            Number(parseFloat(runningBalance).toFixed(2));

          creditsApplied = Number(parseFloat(creditsApplied).toFixed(2));

          totalCreditsApplied += creditsApplied;

          // Create Promotion Credit with "overpayment" credit type
          let creditDoc = doc(collection(firestore, "promotionCredits"));
          overpaymentCreditID = creditDoc.id;
          const brandAbbreviation = this.order.brand.name
            .split(" ")
            .map((word) => word.charAt(0))
            .join("");

          let stateAbbrevation =
            this.order.location.shipping.state.abbreviation;
          let existingCredits = payableTo.records?.creditMemos ?? 0;

          let accountDoc = doc(firestore, "accounts", this.order.account.id);
          let accountResponse = await getDoc(accountDoc);

          let countryID = accountResponse.data().location.shipping.countryID;

          batch.set(
            creditDoc,
            {
              created: serverTimestamp(),
              updated: serverTimestamp(),
              createdBy: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              account: {
                name: this.order.account.name,
                id: this.order.account.id,
              },
              brand: {
                id: this.order.brand.id,
                name: this.order.brand.name,
              },
              location: {
                countryID: countryID,
                state: this.order.location.shipping.state.stateID,
              },
              active: true,
              reversed: false,
              date: moment(paymentDate)
                .startOf("day")
                .add(9, "hours")
                .tz("America/Los_Angeles")
                .toDate(),
              orderNumber: {
                internal: `${brandAbbreviation}-cm-${stateAbbrevation}-${
                  existingCredits + 1
                }`,
                external: "",
              },
              total: creditsApplied,
              available: creditsApplied,
              type: "overpayment",
              relatedID: this.order.id,
              notes: "",
              details: "",
              promotion: null,
            },
            {
              merge: true,
            },
          );

          batch.set(
            doc(firestore, "payableTo", payableTo.id),
            {
              records: {
                creditMemos: increment(1),
              },
            },
            { merge: true },
          );

          // Brand Stats - Metrics
          batch.set(
            brandStatsDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              metrics: {
                promotionCredits: {
                  total: increment(creditsApplied),
                  available: increment(creditsApplied),
                },
              },
            },
            { merge: true },
          );

          // Ledger
          let updates = [];
          let active = true;
          updates.push(`Available: <span>${currency(creditsApplied)}</span>`);
          updates.push(
            `Account: <span class="text-capitalize">${this.order.account.name}</span>`,
          );
          let promotionCreditType = store.getters.getCreditTypes.find(
            (item) => item.value === "overpayment",
          )?.text;
          updates.push(
            `Type: <span class="text-capitalize">${promotionCreditType}</span>`,
          );
          updates.push(
            `Brand: <span class="text-capitalize">${this.order.brand.name}</span>`,
          );
          updates.push(
            `<span class="font-weight-bold text-uppercase" style="color: green">active</span>`,
          );
        }

        totalPaymentAmount += paymentAmount;
        if (paymentType.value === "chargeOff") {
          totalChargeOffAmount += paymentAmount;
        }

        // Update running balance for next payment
        runningBalance = Number(
          parseFloat(runningBalance - paymentAmount + creditsApplied).toFixed(
            2,
          ),
        );

        // Store LeafLink payment data to create AFTER MyTrace batch commit succeeds
        // This ensures MyTrace is the source of truth and LeafLink syncs after
        if (this.orderIsExternal && paymentAmount > 0) {
          switch (this.externalOrderType) {
            case "leaflink":
              if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
                let paymentDateMoment = moment(paymentDate);
                let actualPaymentDate = moment(
                  paymentDateMoment.clone().format("MM/DD/YYYY"),
                )
                  .add(9, "hours")
                  .tz("America/Los_Angeles");
                let paymentData = {
                  order: this.order.externalIDs.leaflink,
                  recorded_by: userLeaflinkInfo.id,
                  total: {
                    amount: Number((paymentAmount - creditsApplied).toFixed(2)),
                    currency: "usd",
                  },
                  payment_date: moment(actualPaymentDate).toISOString(),
                  reason: paymentNotes || "",
                  payment_type: nonCreditablePaymentTypes.includes(
                    paymentType.value,
                  )
                    ? "other"
                    : paymentType.value,
                  check_number: paymentCheckNumber || "",
                };
                leaflinkPaymentsToCreate.push(paymentData);
              }
              break;
            default:
              break;
          }
        }

        let assignedTo = "";

        nonCreditablePaymentTypes.push("credit");

        if (!nonCreditablePaymentTypes.includes(paymentType.value)) {
          if (brandStats.data().staff?.executive.id?.length > 0) {
            assignedTo = {
              name: brandStats.data().staff.executive.name,
              id: brandStats.data().staff.executive.id,
            };
          } else if (brandStats.data().staff?.manager.id?.length > 0) {
            assignedTo = {
              name: brandStats.data().staff.manager.name,
              id: brandStats.data().staff.manager.id,
            };
          }
        }

        let shipDate = this.order.shipDate
          ? moment(this.order.shipDate.seconds * 1000)
              .startOf("day")
              .add(9, "hours")
              .tz("America/Los_Angeles")
              .toDate()
          : null;

        // Submit payment
        batch.set(
          doc(collection(firestore, "salesOrders_payments")),
          {
            created: serverTimestamp(),
            updated: serverTimestamp(),
            createdBy: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            assignedTo,
            account: {
              ...this.order.account,
              state: this.order.location.shipping.state,
            },
            brand: this.order.brand,
            order: {
              external: this.order.flags?.external,
              number: this.order.orderNumber,
              created: moment(this.order.created?.seconds * 1000).toDate(),
              createdBy: this.order.createdBy,
              shipDate,
              paymentTerm: this.order.paymentTerm,
            },
            notes: paymentNotes,
            amount: paymentAmount,
            checkNumber: paymentCheckNumber,
            promotionCredit: paymentPromotionCredit
              ? paymentPromotionCredit
              : null,
            overpaymentCreditID,
            creditsApplied,
            flags: {
              reversed: false,
              hasSupport: this.order.account?.staff?.support ? true : false,
            },
            support: this.order.account?.staff?.support || null,
            paymentDate: moment(paymentDate)
              .startOf("day")
              .add(9, "hours")
              .tz("America/Los_Angeles")
              .toDate(),
            paymentType: paymentType.value,
            relatedID: this.order.id,
            users: [
              brandStats.data().staff.manager.id,
              brandStats.data().staff.executive.id,
            ],
          },
          { merge: true },
        );

        // Set Last Payment on brand metrics if payment date > brand metric's last payment date
        // - lastPayment will have a 'seconds' value if it comes from the database, it won't when
        // user is submitting multiple payments
        lastPayment =
          lastPayment && lastPayment?.seconds
            ? moment(lastPayment?.seconds * 1000).toDate()
            : lastPayment
            ? lastPayment
            : null;

        if (
          !lastPayment ||
          lastPayment < moment(paymentDate).add(12, "hours").toDate()
        ) {
          lastPayment = moment(paymentDate).add(12, "hours").toDate();

          batch.set(
            brandStatsDoc,
            {
              metrics: {
                last: {
                  payment: lastPayment,
                },
              },
            },
            { merge: true },
          );
        }

        // Ledger
        updates.push(
          `<span class="text-capitalize">amount</span>: <span class="font-weight-bold">+${new Intl.NumberFormat(
            "en-US",
            {
              style: "currency",
              currency: "USD",
            },
          ).format(paymentAmount)}</span>`,
        );
        updates.push(
          `<span class="text-capitalize">payment date</span>: ${moment(
            paymentDate,
          ).format("MM/DD/YYYY")}`,
        );
        updates.push(
          `<span class="text-capitalize">type</span>: ${paymentType.text}`,
        );
        if (paymentType.value === "check") {
          updates.push(
            `<span class="text-capitalize">check #</span>: ${paymentCheckNumber}`,
          );
        }

        // Create activity for payment application
        let paymentDetails = [];
        paymentDetails.push(
          `Amount: ${new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(paymentAmount)}`,
        );
        paymentDetails.push(`Type: ${paymentType.text}`);
        paymentDetails.push(
          `Date: ${moment(paymentDate).format("MM/DD/YYYY")}`,
        );
        if (paymentType.value === "check" && paymentCheckNumber) {
          paymentDetails.push(`Check #: ${paymentCheckNumber}`);
        }
        if (creditsApplied > 0) {
          paymentDetails.push(
            `Credits Applied: ${new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(creditsApplied)}`,
          );
        }
        if (paymentNotes) {
          paymentDetails.push(`Notes: "${paymentNotes}"`);
        }

        await createActivity({
          type: "financial",
          action: "Payment applied",
          item: `Order #${this.order.orderNumber.internal.toUpperCase()} - Payment applied - ${paymentDetails.join(
            ", ",
          )}`,
          relatedTo: "salesOrders",
          relatedID: [this.order.id],
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          account: {
            id: this.order.account.id,
            name: this.order.account.name,
          },
          brand: {
            id: this.order.brand.id,
            name: this.order.brand.name,
          },
          businessImpact: "high",
          priority: "high",
          changeData: {
            field: "paymentApplication",
            paymentAmount: paymentAmount,
            paymentType: paymentType.value,
            paymentTypeText: paymentType.text,
            paymentDate: moment(paymentDate).toDate(),
            checkNumber: paymentCheckNumber,
            creditsApplied: creditsApplied,
            notes: paymentNotes,
            orderNumber: this.order.orderNumber.internal,
            previousBalance: due,
            newBalance: runningBalance,
            paymentMethod: paymentType.value,
            externalOrderUpdated:
              this.externalUpdates.leaflink.includes("paymentsApplied"),
            monetaryValue: paymentAmount,
          },
        });

        if (paymentType.value === "promotionCredit") {
          const promotionCreditDoc = doc(
            firestore,
            "promotionCredits",
            paymentPromotionCredit,
          );

          let promotionCreditResponse = await getDoc(promotionCreditDoc);
          let selectedPromotionCredit = promotionCreditResponse.data();

          const available = Number(
            parseFloat(
              selectedPromotionCredit.available - paymentAmount,
            ).toFixed(2),
          );
          let updates = [];

          batch.set(
            promotionCreditDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              active: available > 0 ? true : false,
              available,
            },
            { merge: true },
          );

          // Brand Stats - Metrics
          batch.set(
            brandStatsDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              metrics: {
                promotionCredits: {
                  available: increment(-paymentAmount),
                },
              },
            },
            { merge: true },
          );

          // Ledger
          updates.push(
            `Available, <span class="text-uppercase">old</span>: <span>${currency(
              selectedPromotionCredit.available,
            )}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${currency(
              available,
            )}</span>`,
          );
          updates.push(
            `Account, <span class="text-capitalize">${this.order.account.name}</span>`,
          );
          updates.push(
            `Brand, <span class="text-capitalize">${this.order.brand.name}</span>`,
          );
          updates.push(
            `Order #, Internal: ${
              this.order.orderNumber.internal
            } / External: ${
              this.order.flags.external ? this.order.orderNumber.external : "-"
            }`,
          );
          if (available === 0) {
            updates.push(
              `Active, <span class="text-uppercase">old</span>: <span class="text-uppercase font-weight-bold" style="color: green;">active</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold text-uppercase" style="color: red;">inactive</span>`,
            );
            addBreadcrumb("applyPayment: promotion credit fully exhausted", {
              workflow: "sales-order",
              data: {
                orderId: this.orderID,
                promotionCreditId: paymentPromotionCredit,
                paymentAmount,
              },
            });
          }
        }

        if (runningBalance <= 0) {
          // Status
          paid = true;

          if (this.order.status === "shipped") {
            //  Users are able to add payments at any point in the order process
            //  Because of this, we can't set the status to 'complete' unless
            //    the order is already in the 'shipped' status.

            status = "complete";
            const oldStatus = store.getters.getOrderStatus.find(
                (option) => option.value === this.order.status,
              ),
              newStatus = store.getters.getOrderStatus.find(
                (option) => option.value === "complete",
              );

            //  External Order Status - Track that we need to transition LeafLink after batch commit
            if (this.orderIsExternal && !shouldReturn) {
              switch (this.externalOrderType) {
                case "leaflink":
                  if (this.externalUpdates.leaflink.includes("statusUpdated")) {
                    needsLeafLinkCompleteTransition = true;
                  }
                  break;
                default:
                  break;
              }
            }

            // Create activity for status change
            await createActivity({
              type: "status",
              action: "Order completed - fully paid",
              item: `Order #${this.order.orderNumber.internal.toUpperCase()} status changed from ${
                oldStatus.text
              } to ${newStatus.text} due to full payment`,
              relatedTo: "salesOrders",
              relatedID: [this.order.id],
              user: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              account: {
                id: this.order.account.id,
                name: this.order.account.name,
              },
              brand: {
                id: this.order.brand.id,
                name: this.order.brand.name,
              },
              businessImpact: "high",
              priority: "high",
              changeData: {
                field: "status",
                oldValue: {
                  text: oldStatus.text,
                  value: oldStatus.value,
                  color: oldStatus.color,
                },
                newValue: {
                  text: newStatus.text,
                  value: newStatus.value,
                  color: newStatus.color,
                },
                reason: "Order fully paid - automatic completion",
                orderNumber: this.order.orderNumber.internal,
                completionTrigger: "payment_completion",
              },
            });
          }
          batch.set(
            salesOrderDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              records: {
                completed: moment().startOf("day").add(12, "hours").toDate(),
              },
            },
            { merge: true },
          );
        }

        // Order & Account Balance
        due = parseFloat(runningBalance).toFixed(2);
        batch.set(
          salesOrderDoc,
          {
            updated: serverTimestamp(),
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            balance: {
              due,
            },
            flags: {
              paid,
            },
            status,
          },
          { merge: true },
        );

        // Account Balances
        //  Charge off balance
        let currentChargeOffBalance = brandStats.data().metrics.chargeOffBalance
          ? brandStats.data().metrics.chargeOffBalance
          : 0;
        const newChargeOffBalance = Number(
          parseFloat(currentChargeOffBalance + totalChargeOffAmount).toFixed(2),
        );
        batch.set(
          brandStatsDoc,
          {
            metrics: {
              chargeOffBalance: newChargeOffBalance,
            },
          },
          { merge: true },
        );

        //  General Balance

        //  The balance for this order is added to the account's balance on 'shipped' status.
        //  This means that we shouldn't be decreasing the account's balance unless the
        //  status is shipped.

        if (this.order.status === "shipped") {
          let currentAccountBalance = brandStats.data().metrics.balance;
          const newBalance = Number(
            parseFloat(
              currentAccountBalance - totalPaymentAmount + totalCreditsApplied,
            ).toFixed(2),
          );
          batch.set(
            brandStatsDoc,
            {
              metrics: {
                balance: newBalance,
                // credits: increment(this.accountCredits),
              },
            },
            { merge: true },
          );
        }
      }

      if (!shouldReturn) {
        // Update payment alerts

        const alertsRef = collection(firestore, "watchers");
        const relatedTo = where("relatedTo.id", "==", this.order.id);
        const q = query(alertsRef, relatedTo);
        const response = await getDocs(q);

        let alerts = response.docs.map((doc) => {
          return {
            id: doc.id,
            ...doc.data(),
          };
        });

        alerts
          .filter(
            (alert) =>
              !alert.flags.complete && alert.condition.field === "newPayment",
          )
          .forEach((alert) => {
            const alertDoc = doc(firestore, "watchers", alert.id);
            batch.set(
              alertDoc,
              {
                flags: {
                  complete: true,
                },
                completed: serverTimestamp(),
              },
              { merge: true },
            );
          });

        addBreadcrumb("applyPayment: committing batch", {
          workflow: "sales-order",
          data: { orderId: this.orderID, runningBalance, creditsApplied },
        });
        // Don't commit if there were errors submitting leaflink payment(s)
        await batch.commit();
        addBreadcrumb("applyPayment: committed", {
          workflow: "sales-order",
          data: { orderId: this.orderID, newDue: runningBalance },
        });
        await this._checkBalanceAnomaly("apply-payment", {
          balanceDue: runningBalance,
        });

        // ============================================
        // LEAFLINK OPERATIONS - Only after MyTrace batch commit succeeds
        // This ensures MyTrace is the source of truth
        // ============================================

        // Create LeafLink payments - Only after MyTrace batch commit succeeds
        if (leaflinkPaymentsToCreate.length > 0 && !shouldReturn) {
          for (const paymentData of leaflinkPaymentsToCreate) {
            try {
              if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
                await this.leaflinkClient.createPayment(paymentData);
              }
            } catch (paymentError) {
              // Log error but don't throw - MyTrace is already updated
              // This prevents a LeafLink API issue from rolling back MyTrace changes
              console.error("Failed to create LeafLink payment:", paymentError);
              logWorkflowWarning(`LeafLink: createPayment failed — ${paymentError.message}`, {
                workflow: "sales-order",
                action: "leaflink-payment-sync",
                context: { orderId: this.orderID, paymentAmount: paymentData?.total?.amount, balance: this.order?.balance },
              });
              // Create an activity to track this failure
              try {
                await createActivity({
                  type: "error",
                  action: "LeafLink payment sync failed",
                  item: `Order #${this.order.orderNumber.internal.toUpperCase()} - Failed to create payment in LeafLink. MyTrace payment recorded successfully. Amount: ${
                    paymentData.total.amount
                  }`,
                  relatedTo: "salesOrders",
                  relatedID: [this.order.id],
                  user: {
                    name: store.getters.getUser.name,
                    uid: store.getters.getUser.uid,
                  },
                  account: {
                    id: this.order.account.id,
                    name: this.order.account.name,
                  },
                  brand: {
                    id: this.order.brand.id,
                    name: this.order.brand.name,
                  },
                  businessImpact: "high",
                  priority: "high",
                  changeData: {
                    field: "externalPaymentSync",
                    error: paymentError.message,
                    orderNumber: this.order.orderNumber.internal,
                    paymentAmount: paymentData.total.amount,
                  },
                });
              } catch (activityError) {
                console.error(
                  "Failed to create error activity:",
                  activityError,
                );
                logWorkflowWarning(`applyPayment: activity creation for LeafLink payment failure failed — ${activityError.message}`, {
                  workflow: "sales-order",
                  action: "apply-payment-activity-log",
                  context: { orderId: this.orderID },
                });
              }
            }
          }
        }

        // External Order Status Transition - Only after MyTrace batch commit succeeds
        // This ensures MyTrace is updated before we try to sync LeafLink status
        if (needsLeafLinkCompleteTransition && !shouldReturn) {
          try {
            switch (this.externalOrderType) {
              case "leaflink":
                if (this.externalUpdates.leaflink.includes("statusUpdated")) {
                  await this.leaflinkClient.transitionOrder("complete");
                }
                break;
              default:
                break;
            }
          } catch (transitionError) {
            // Log error but don't throw - MyTrace is already updated
            // This prevents a LeafLink API issue from rolling back MyTrace changes
            console.error(
              "Failed to transition LeafLink order to complete:",
              transitionError,
            );
            logWorkflowWarning(`LeafLink: transitionOrder(complete) failed — ${transitionError.message}`, {
              workflow: "sales-order",
              action: "leaflink-transition-sync",
              context: { orderId: this.orderID, toStatus: "complete", balance: this.order?.balance },
            });
            // Optionally create an activity to track this failure
            try {
              await createActivity({
                type: "error",
                action: "LeafLink status sync failed",
                item: `Order #${this.order.orderNumber.internal.toUpperCase()} - Failed to transition LeafLink order to 'complete' status. MyTrace status updated successfully.`,
                relatedTo: "salesOrders",
                relatedID: [this.order.id],
                user: {
                  name: store.getters.getUser.name,
                  uid: store.getters.getUser.uid,
                },
                account: {
                  id: this.order.account.id,
                  name: this.order.account.name,
                },
                brand: {
                  id: this.order.brand.id,
                  name: this.order.brand.name,
                },
                businessImpact: "medium",
                priority: "medium",
                changeData: {
                  field: "externalStatusSync",
                  error: transitionError.message,
                  orderNumber: this.order.orderNumber.internal,
                },
              });
            } catch (activityError) {
              console.error("Failed to create error activity:", activityError);
              logWorkflowWarning(`applyPayment: activity creation for LeafLink status sync failure failed — ${activityError.message}`, {
                workflow: "sales-order",
                action: "apply-payment-status-activity-log",
                context: { orderId: this.orderID },
              });
            }
          }
        }
      }
    } catch (error) {
      throw error;
    }
  }

  async updateLineItems(cart, lineItems) {
    addBreadcrumb("updateLineItems: start", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        cartItemCount: cart?.length,
        existingLineItemCount: lineItems?.length,
        currentTotal: this.order?.balance?.total,
        currentDue: this.order?.balance?.due,
        orderStatus: this.order?.status,
      },
    });
    try {
      await this.getOrder();
      await this.getLineItems();
      await this.getProductCategories();
      await this.getPriceOverrides();

      // // Use transaction for inventory-sensitive operations
      const result = await runTransaction(firestore, async (transaction) => {
        // Get fresh data within transaction
        const salesOrderDoc = doc(firestore, "salesOrders", this.orderID);

        // Read current order within transaction
        const orderSnapshot = await transaction.get(salesOrderDoc);
        const currentOrder = orderSnapshot.data();

        if (!currentOrder) {
          throw new Error(`Order ${this.orderID} not found`);
        }

        // Add the document ID to the order data
        currentOrder.id = orderSnapshot.id;

        const stateDoc = doc(
          firestore,
          "states",
          currentOrder.location.shipping.state.stateID,
        );

        // Read current state within transaction
        const stateSnapshot = await transaction.get(stateDoc);
        const stateData = stateSnapshot.data();

        if (!stateData) {
          throw new Error(
            `State ${currentOrder.location.shipping.state.stateID} not found`,
          );
        }

        let fulfillmentLocation = stateData.fulfillmentLocation;

        if (currentOrder.fulfillmentLocation) {
          fulfillmentLocation = currentOrder.fulfillmentLocation.id;
        }

        const locationRef = doc(
          firestore,
          "locationManufacturing",
          `${fulfillmentLocation}_${currentOrder.brand.id}`,
        );

        // PRE-VALIDATE INVENTORY AVAILABILITY BEFORE PROCESSING ANY CHANGES
        // This prevents race conditions by checking inventory upfront
        const inventorySnapshot = await transaction.get(locationRef);
        const inventoryData = inventorySnapshot.data()?.manufacturing || {};
        // Create a deep copy of the original inventory data for snapshot purposes
        // This ensures "before" values are always from the original state, not mutated state
        const originalInventoryData = JSON.parse(JSON.stringify(inventoryData));

        // Aggregate total quantities needed per product (combining promo and regular units)
        const productTotals = {};
        for (const cartItem of cart) {
          const categoryID = cartItem.categoryID;
          const productID = cartItem.productID;
          const key = `${categoryID}_${productID}`;

          if (!productTotals[key]) {
            productTotals[key] = {
              categoryID,
              productID,
              name: cartItem.name,
              sku: cartItem.sku,
              existingQty: 0,
              newQty: 0,
              additionalQtyRequested: 0,
            };
          }

          // Calculate quantities for this cart item
          // Handle both case-based and non-case products
          const isCaseProduct =
            cartItem.caseQTY > 0 || cartItem.unitsPerCase > 0;

          let existingQty, newQty;

          if (isCaseProduct) {
            // Case-based product: calculate using cases and singles
            existingQty =
              Number(cartItem.cases.existing || 0) *
                (cartItem.caseQTY || cartItem.unitsPerCase || 0) +
              Number(cartItem.singles.existing || 0);

            newQty =
              Number(cartItem.cases.new || 0) *
                (cartItem.caseQTY || cartItem.unitsPerCase || 0) +
              Number(cartItem.singles.new || 0);
          } else {
            // Non-case product: use singles directly, ignore cases
            existingQty = Number(cartItem.singles.existing || 0);
            newQty = Number(cartItem.singles.new || 0);
          }

          // Aggregate totals for this product
          productTotals[key].existingQty += existingQty;
          productTotals[key].newQty += newQty;
        }

        // Calculate additional quantity requested for each product
        for (const key in productTotals) {
          const product = productTotals[key];
          product.additionalQtyRequested = product.newQty - product.existingQty;
        }

        // Validate that we have enough inventory for all requested changes
        // Now we check inventory per product (not per cart item) to account for promo + regular units
        for (const key in productTotals) {
          const product = productTotals[key];

          // If reducing quantity, no need to check inventory
          if (product.additionalQtyRequested <= 0) {
            continue;
          }

          const totalAvailable =
            inventoryData[product.categoryID]?.available?.[product.productID] ||
            0;
          const totalReserved =
            inventoryData[product.categoryID]?.reserved?.[product.productID] ||
            0;

          // Calculate how much is available for new reservations
          const availableForNewReservations = totalAvailable - totalReserved;

          if (availableForNewReservations < product.additionalQtyRequested) {
            throw new Error(
              `Insufficient inventory for ${product.name} (${product.sku}). Available for new reservations: ${availableForNewReservations}, Additional quantity requested: ${product.additionalQtyRequested}. Please reduce quantity or check inventory levels.`,
            );
          }
        }

        let removedItems = lineItems.filter(
          (item) =>
            !cart.some(
              (cartItem) =>
                cartItem.id === item.id &&
                Boolean(cartItem.promo) === Boolean(item.promo),
            ),
        );

        let addedItems = cart.filter(
          (item) =>
            !lineItems.find(
              (lineItem) =>
                lineItem.id === item.id &&
                Boolean(lineItem.promo) === Boolean(item.promo),
            ),
        );

        let updates = [];
        // Create a set of added items using proper identification (productID + promo + amount)
        let addedItemsSet = new Set(
          addedItems.map((item) => `${item.id}_${item.promo}`),
        );

        let updatedLineItems = cart.filter(
          (item) =>
            (item.cases.new !== item.cases.existing ||
              item.singles.new !== item.singles.existing ||
              item.amount.new !== item.amount.existing) &&
            !addedItemsSet.has(`${item.id}_${item.promo}`),
        );

        let notUpdatedLineItems = cart.filter(
          (item) =>
            item.cases.new === item.cases.existing &&
            item.singles.new === item.singles.existing &&
            item.amount.new === item.amount.existing &&
            !addedItemsSet.has(`${item.id}_${item.promo}`),
        );

        let transitionToAccepted =
          updatedLineItems.length > 0 ||
          removedItems.length > 0 ||
          addedItems.length > 0;

        addBreadcrumb("updateLineItems: diff calculated", {
          workflow: "sales-order",
          data: {
            orderId: this.orderID,
            addedCount: addedItems.length,
            removedCount: removedItems.length,
            updatedCount: updatedLineItems.length,
            transitionToAccepted,
          },
        });

        if (
          transitionToAccepted &&
          currentOrder.status !== "submitted" &&
          currentOrder.status !== "accepted" &&
          currentOrder.status !== "draft"
        ) {
          if (currentOrder.flags.external) {
            switch (this.externalOrderType) {
              case "leaflink":
                if (
                  currentOrder.status !== "deliveryConfirmation" &&
                  this.externalUpdates.leaflink.includes("statusUpdated")
                ) {
                  addBreadcrumb("leaflink: transitionOrder accept", {
                    workflow: "sales-order",
                    data: { orderId: this.orderID, currentStatus: currentOrder.status },
                  });
                  try {
                    await this.leaflinkClient.transitionOrder("accept");
                  } catch (error) {
                    logWorkflowWarning(`LeafLink: transitionOrder("accept") failed — ${error.message}`, {
                      workflow: "sales-order",
                      action: "leaflink-transition-accept",
                      context: { orderId: this.orderID, currentStatus: currentOrder.status, balance: this.order?.balance },
                    });
                    throw error;
                  }
                }
                break;
              default:
                break;
            }
          }
          const orderDoc = doc(firestore, "salesOrders", currentOrder.id);

          const oldValue = store.getters.getOrderStatus.find(
              (status) => status.value === currentOrder.status,
            ),
            newValue = store.getters.getOrderStatus.find(
              (status) => status.value === "accepted",
            );

          // Update order within transaction
          transaction.set(
            orderDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              status: newValue.value,
            },
            { merge: true },
          );

          // Create activity for status transition
          await createActivity({
            type: "status",
            action: "Order status updated",
            item: `Status changed from ${oldValue.text} to ${newValue.text}`,
            relatedTo: "salesOrders",
            relatedID: [orderDoc.id],
            changeData: {
              field: "status",
              oldValue: {
                text: oldValue.text,
                value: oldValue.value,
                color: oldValue.color,
              },
              newValue: {
                text: newValue.text,
                value: newValue.value,
                color: newValue.color,
              },
              reason: "Status transition during order processing",
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });
        }

        // PRE-READ ALL INVENTORY DATA NEEDED FOR UPDATES
        // This ensures all reads happen before any writes
        const inventoryUpdates = [];
        const inventoryFulfilledUpdates = [];

        for (const item of updatedLineItems.concat(notUpdatedLineItems)) {
          if (
            item.cases.new !== item.cases.existing ||
            item.singles.new !== item.singles.existing
          ) {
            item.needsQTYPatch = true;
            //  QTY was modified, need to modify location inventory
            // Handle both case-based and non-case products
            const isCaseProduct = item.caseQTY > 0 || item.unitsPerCase > 0;

            let originalQTY, newQTY, difference;

            if (isCaseProduct) {
              // Case-based product: calculate using cases and singles
              let caseQTY = item.caseQTY ?? item.unitsPerCase;
              originalQTY =
                item.cases.existing * caseQTY + item.singles.existing;
              newQTY = item.cases.new * caseQTY + item.singles.new;
            } else {
              // Non-case product: use singles directly, ignore cases
              originalQTY = item.singles.existing || 0;
              newQTY = item.singles.new || 0;
            }

            difference = newQTY - originalQTY;

            // Store inventory update data for later processing
            inventoryUpdates.push({
              item,
              difference,
              caseQTY: isCaseProduct ? item.caseQTY ?? item.unitsPerCase : 0,
              isCaseProduct,
            });
          }

          if (currentOrder.status === "fulfilled" && transitionToAccepted) {
            // Handle both case-based and non-case products
            const isCaseProduct = item.caseQTY > 0 || item.unitsPerCase > 0;

            // qtyToAdd = EXISTING qty: Firestore must restore the full original amount
            let qtyToAdd;
            if (isCaseProduct) {
              qtyToAdd =
                item.cases.existing * item.unitsPerCase + item.singles.existing;
            } else {
              qtyToAdd = item.singles.existing || 0;
            }

            // activityQty = NEW qty when item had a qty change, so the activity delta
            // matches the line qty change. When no change, use existing qty.
            const hadQtyChange =
              item.cases.new !== item.cases.existing ||
              item.singles.new !== item.singles.existing;
            let activityQty = qtyToAdd;
            if (hadQtyChange) {
              if (isCaseProduct) {
                activityQty =
                  (item.cases.new ?? item.cases.existing) *
                    (item.unitsPerCase ?? item.caseQTY ?? 0) +
                  (item.singles.new ?? item.singles.existing ?? 0);
              } else {
                activityQty = item.singles.new ?? item.singles.existing ?? 0;
              }
            }

            // Store fulfilled inventory update data for later processing
            inventoryFulfilledUpdates.push({
              item,
              qtyToAdd,
              activityQty,
            });
          }
        }

        // Helper function to get listPrice for a product
        const getListPriceForProduct = (product) => {
          let result = null;

          // First check for price override
          const priceOverride = this.getPriceOverrideForProduct(product);
          if (priceOverride?.listPrice) {
            result = priceOverride.listPrice;
          } else {
            // Otherwise check category
            const category = this.categories.find(
              (cat) => cat.id === product.categoryID,
            );
            if (category?.listPrice) {
              result = category.listPrice;
            }
          }

          return result;
        };

        // PRE-READ ALL INVENTORY DATA FOR ADDED ITEMS
        const addedItemInventoryData = [];
        for (let cartItem of addedItems) {
          // Get listPrice for this product
          const listPrice = getListPriceForProduct({
            categoryID: cartItem.categoryID,
            productID: cartItem.productID,
          });

          cartItem = {
            account: {
              id: currentOrder.account.id,
              name: currentOrder.account.name,
            },
            brand: {
              id: currentOrder.brand.id,
              name: currentOrder.brand.name,
            },
            state: currentOrder.location.shipping.state,
            amount: cartItem.amount.new,
            notes: cartItem.notes || "",
            flags: {
              case: cartItem.caseQTY > 0,
              fulfilled: false,
              delivered: false,
              onSale:
                cartItem.amount.new <
                (cartItem.originalPrice || cartItem.amount?.existing),
              discountApplied: false,
            },
            externalIDs: {
              ...cartItem.externalIDs,
              leaflink: cartItem.externalIDs?.leaflink ?? null,
            },
            name: cartItem.name,
            price: {
              normal: cartItem.originalPrice ?? cartItem.amount?.existing,
              sale: cartItem.amount.new,
            },
            listPrice: listPrice,
            productID: cartItem.productID,
            categoryID: cartItem.categoryID,
            categoryName: cartItem.category ?? cartItem.categoryName,
            qty: (() => {
              // Handle both case-based and non-case products
              const isCaseProduct =
                cartItem.caseQTY > 0 || cartItem.unitsPerCase > 0;

              if (isCaseProduct) {
                // Case-based product: calculate using cases and singles
                return (
                  Number(cartItem.cases.new || 0) *
                    (cartItem.caseQTY || cartItem.unitsPerCase || 0) +
                  Number(cartItem.singles.new || 0)
                );
              } else {
                // Non-case product: use singles directly, ignore cases
                return Number(cartItem.singles.new || 0);
              }
            })(),
            unitsPerCase: cartItem.caseQTY || 0,
            cases: cartItem.cases.new || 0,
            singles: cartItem.singles.new || 0,
            relatedID: currentOrder.id || this.orderID,
            sample: cartItem.sample ?? false,
            promo: cartItem.promo || false,
            sku: cartItem.sku.toLowerCase(),
            tax: 0,
            discount: null,
          };

          // Store added item data for later processing
          addedItemInventoryData.push({
            cartItem,
          });
        }

        // PRE-READ ALL INVENTORY DATA FOR REMOVED ITEMS
        const removedItemInventoryData = [];
        for (const item of removedItems) {
          // Handle both case-based and non-case products
          const isCaseProduct = item.caseQTY > 0 || item.unitsPerCase > 0;

          let qty;
          if (isCaseProduct) {
            // Case-based product: calculate using cases and singles
            qty =
              item.cases * (item.unitsPerCase || item.caseQTY || 0) +
              item.singles;
          } else {
            // Non-case product: use singles directly, ignore cases
            qty = item.singles || 0;
          }

          // Store removed item data for later processing
          removedItemInventoryData.push({
            item,
            qty,
          });
        }

        // NOW ALL READS ARE COMPLETE - START PROCESSING WRITES

        // Collectors for aggregating inventory activities by product so that
        // the same product at different prices (e.g. regular + promo) produces
        // one activity with correct before/after snapshots.
        const reservationActivityCollector = {};
        const restorationActivityCollector = {};

        let existingItems = [];
        for (const item of updatedLineItems.concat(notUpdatedLineItems)) {
          let itemUpdates = [];

          if (item.cases.new !== item.cases.existing) {
            //  Cases updated
            itemUpdates.push(
              `Cases - <span class="text-uppercase">old</span>: <span>${item.cases.existing}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${item.cases.new}</span>`,
            );
          }
          if (item.singles.new !== item.singles.existing) {
            //  Single Units updated
            itemUpdates.push(
              `Single Units - <span class="text-uppercase">old</span>: <span>${item.singles.existing}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${item.singles.new}</span>`,
            );
          }
          if (item.amount.new !== item.amount.existing) {
            //  Price updated
            const currencyFormatter = new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            });
            itemUpdates.push(
              `Price - <span class="text-uppercase">old</span>: <span>${currencyFormatter.format(
                item.amount.existing,
              )}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${currencyFormatter.format(
                item.amount.new,
              )}</span>`,
            );
          }
          if (itemUpdates.length > 0) {
            //  Line item was modified

            itemUpdates = itemUpdates.join(" / ");
            let promoUnit = item.promo
              ? `<span class='font-weight-bold text-no-wrap'>
              <span class='font-weight-bold text-caption pa-1 rounded' style='background-color:
                #0277BD; color: white}
              '>Promo</span>
            </span>`
              : "";
            let itemText = `<span class="text-capitalize font-weight-bold">${item.name}</span> <span class="text-uppercase">(${item.sku})${promoUnit}</span>: ${itemUpdates}`;
            updates.push(itemText);
            item.needsPatch = true;
          }

          if (
            item.cases.new !== item.cases.existing ||
            item.singles.new !== item.singles.existing
          ) {
            item.needsQTYPatch = true;
            //  QTY was modified, need to modify location inventory
            // Handle both case-based and non-case products
            const isCaseProduct = item.caseQTY > 0 || item.unitsPerCase > 0;

            let originalQTY, newQTY, difference;

            if (isCaseProduct) {
              // Case-based product: calculate using cases and singles
              let caseQTY = item.caseQTY ?? item.unitsPerCase;
              originalQTY =
                item.cases.existing * caseQTY + item.singles.existing;
              newQTY = item.cases.new * caseQTY + item.singles.new;
            } else {
              // Non-case product: use singles directly, ignore cases
              originalQTY = item.singles.existing || 0;
              newQTY = item.singles.new || 0;
            }

            difference = newQTY - originalQTY;

            // Use pre-read inventory data instead of reading again
            const inventoryUpdate = inventoryUpdates.find(
              (u) => u.item.id === item.id,
            );
            if (inventoryUpdate) {
              // Use originalInventoryData for "before" values to ensure we capture the state
              // before any mutations, even if the same product is processed multiple times
              const currentAvailable =
                originalInventoryData[inventoryUpdate.item.categoryID]
                  ?.available?.[inventoryUpdate.item.productID] || 0;
              const currentReserved =
                originalInventoryData[inventoryUpdate.item.categoryID]
                  ?.reserved?.[inventoryUpdate.item.productID] || 0;
              // Capture all "before" values from original state BEFORE any updates to inventoryData
              const currentPending =
                originalInventoryData[item.categoryID]?.pending?.[
                  item.productID
                ] || 0;
              const currentTransfer =
                originalInventoryData[item.categoryID]?.transfer?.[
                  item.productID
                ] || 0;
              const currentInTesting =
                originalInventoryData[item.categoryID]?.inTesting?.[
                  item.productID
                ] || 0;

              // Calculate available inventory (available - reserved)
              const availableForReservation =
                currentAvailable - currentReserved;

              // Check if we can reserve the additional quantity
              if (
                inventoryUpdate.difference > 0 &&
                availableForReservation < inventoryUpdate.difference
              ) {
                throw new Error(
                  `Insufficient inventory for update: ${item.name} (${item.sku}). Available: ${availableForReservation}, Additional Requested: ${inventoryUpdate.difference}. Please reduce quantity or check inventory levels.`,
                );
              }

              // Update the inventory data to reflect the new reservation
              if (inventoryData[item.categoryID]) {
                if (!inventoryData[item.categoryID].reserved) {
                  inventoryData[item.categoryID].reserved = {};
                }
                if (!inventoryData[item.categoryID].reserved[item.productID]) {
                  inventoryData[item.categoryID].reserved[item.productID] = 0;
                }
                inventoryData[item.categoryID].reserved[item.productID] +=
                  inventoryUpdate.difference;
              }

              transaction.set(
                locationRef,
                {
                  manufacturing: {
                    [item.categoryID]: {
                      reserved: {
                        [item.productID]: increment(inventoryUpdate.difference),
                      },
                    },
                  },
                },
                { merge: true },
              );

              // Collect reservation/release activity data for aggregation by product
              const productKey = `${item.categoryID}_${item.productID}`;
              if (!reservationActivityCollector[productKey]) {
                reservationActivityCollector[productKey] = {
                  skuID: `${
                    currentOrder.fulfillmentLocation?.id ||
                    currentOrder.location.shipping.id
                  }_${currentOrder.brand.id}_${item.categoryID}_${
                    item.productID
                  }`,
                  itemData: item,
                  netDifference: 0,
                  before: {
                    available: currentAvailable,
                    reserved: currentReserved,
                    pending: currentPending,
                    transfer: currentTransfer,
                    inTesting: currentInTesting,
                  },
                };
              }
              reservationActivityCollector[productKey].netDifference +=
                inventoryUpdate.difference;
            }
          }

          if (currentOrder.status === "fulfilled" && transitionToAccepted) {
            // Use pre-read inventory data instead of reading again
            const fulfilledUpdate = inventoryFulfilledUpdates.find(
              (u) => u.item.id === item.id,
            );
            if (fulfilledUpdate) {
              // Use originalInventoryData for validation (state before any mutations)
              const currentAvailable =
                originalInventoryData[fulfilledUpdate.item.categoryID]
                  ?.available?.[fulfilledUpdate.item.productID] || 0;
              const currentReserved =
                originalInventoryData[fulfilledUpdate.item.categoryID]
                  ?.reserved?.[fulfilledUpdate.item.productID] || 0;

              // Validate that we're not exceeding available inventory
              if (currentAvailable + fulfilledUpdate.qtyToAdd < 0) {
                throw new Error(
                  `Invalid inventory operation: ${item.name} (${item.sku}). Would result in negative available inventory.`,
                );
              }

              // For activity display only: restoration's "before" must be state after reservation
              // updates so the activity log chain is correct (e.g. Line qty increased then Order restored).
              const snapshotAvailable =
                inventoryData[fulfilledUpdate.item.categoryID]?.available?.[
                  fulfilledUpdate.item.productID
                ] ??
                originalInventoryData[fulfilledUpdate.item.categoryID]
                  ?.available?.[fulfilledUpdate.item.productID] ??
                0;
              const snapshotReserved =
                inventoryData[fulfilledUpdate.item.categoryID]?.reserved?.[
                  fulfilledUpdate.item.productID
                ] ??
                originalInventoryData[fulfilledUpdate.item.categoryID]
                  ?.reserved?.[fulfilledUpdate.item.productID] ??
                0;

              // Update the inventory data to reflect the changes
              if (inventoryData[fulfilledUpdate.item.categoryID]) {
                if (!inventoryData[fulfilledUpdate.item.categoryID].available) {
                  inventoryData[fulfilledUpdate.item.categoryID].available = {};
                }
                if (!inventoryData[fulfilledUpdate.item.categoryID].reserved) {
                  inventoryData[fulfilledUpdate.item.categoryID].reserved = {};
                }
                if (
                  !inventoryData[fulfilledUpdate.item.categoryID].available[
                    fulfilledUpdate.item.productID
                  ]
                ) {
                  inventoryData[fulfilledUpdate.item.categoryID].available[
                    fulfilledUpdate.item.productID
                  ] = 0;
                }
                if (
                  !inventoryData[fulfilledUpdate.item.categoryID].reserved[
                    fulfilledUpdate.item.productID
                  ]
                ) {
                  inventoryData[fulfilledUpdate.item.categoryID].reserved[
                    fulfilledUpdate.item.productID
                  ] = 0;
                }
                inventoryData[fulfilledUpdate.item.categoryID].available[
                  fulfilledUpdate.item.productID
                ] += fulfilledUpdate.qtyToAdd;
                inventoryData[fulfilledUpdate.item.categoryID].reserved[
                  fulfilledUpdate.item.productID
                ] += fulfilledUpdate.qtyToAdd;
              }

              transaction.set(
                locationRef,
                {
                  manufacturing: {
                    [fulfilledUpdate.item.categoryID]: {
                      available: {
                        [fulfilledUpdate.item.productID]: increment(
                          fulfilledUpdate.qtyToAdd,
                        ),
                      },
                      reserved: {
                        [fulfilledUpdate.item.productID]: increment(
                          fulfilledUpdate.qtyToAdd,
                        ),
                      },
                    },
                  },
                },
                { merge: true },
              );

              // Collect restoration activity data for aggregation by product
              const restorationKey = `${fulfilledUpdate.item.categoryID}_${fulfilledUpdate.item.productID}`;
              const currentPending =
                originalInventoryData[fulfilledUpdate.item.categoryID]
                  ?.pending?.[fulfilledUpdate.item.productID] || 0;
              const currentTransfer =
                originalInventoryData[fulfilledUpdate.item.categoryID]
                  ?.transfer?.[fulfilledUpdate.item.productID] || 0;
              const currentInTesting =
                originalInventoryData[fulfilledUpdate.item.categoryID]
                  ?.inTesting?.[fulfilledUpdate.item.productID] || 0;

              if (!restorationActivityCollector[restorationKey]) {
                restorationActivityCollector[restorationKey] = {
                  skuID: `${
                    currentOrder.fulfillmentLocation?.id ||
                    currentOrder.location.shipping.id
                  }_${currentOrder.brand.id}_${
                    fulfilledUpdate.item.categoryID
                  }_${fulfilledUpdate.item.productID}`,
                  itemData: fulfilledUpdate.item,
                  totalQtyToAdd: 0,
                  totalActivityQty: 0,
                  before: {
                    available: snapshotAvailable,
                    reserved: snapshotReserved,
                    pending: currentPending,
                    transfer: currentTransfer,
                    inTesting: currentInTesting,
                  },
                };
              }
              restorationActivityCollector[restorationKey].totalQtyToAdd +=
                fulfilledUpdate.qtyToAdd;
              restorationActivityCollector[restorationKey].totalActivityQty +=
                fulfilledUpdate.activityQty;
            }
          }
          let discountValue = 0;

          const processedItem = {
            account: item.account,
            brand: item.brand,
            created:
              item.created ||
              moment(currentOrder.created?.seconds * 1000).toDate(),
            externalIDs: item.externalIDs,
            flags: {
              ...item.flags,
              onSale: item.amount.new < item.price.normal,
              discountApplied: discountValue > 0,
            },
            id: item.id,
            name: item.name,
            price: {
              ...item.price,
              sale: item.amount.new,
            },
            listPrice: item.listPrice ?? null,
            productID: item.productID,
            categoryID: item.categoryID || "",
            categoryName: item.categoryName || "",
            relatedID: item.relatedID,
            sku: item.sku,
            state: item.state || currentOrder.location.shipping.state,
            tax: item.tax,
            amount: item.amount.new,
            qty:
              Number(item.cases.new) *
                (item.unitsPerCase || item.caseQTY || 0) +
              Number(item.singles.new),
            cases: item.cases.new,
            singles: item.singles.new,
            notes: item.notes ?? "",
            promo: item.promo || false,
            needsPatch: item.needsPatch || false,
            needsQTYPatch: item.needsQTYPatch || false,
            discount: null,
          };

          existingItems.push(processedItem);
        }

        // Emit aggregated reservation/release activities (one per product)
        for (const productKey of Object.keys(reservationActivityCollector)) {
          const agg = reservationActivityCollector[productKey];
          if (agg.netDifference === 0) continue;
          const isReservation = agg.netDifference > 0;
          const absDifference = Math.abs(agg.netDifference);
          await createInventoryActivity({
            action: isReservation ? "Inventory reserved" : "Inventory released",
            item: `${absDifference} units of ${agg.itemData.name} (${
              agg.itemData.sku
            }) ${isReservation ? "reserved" : "released"} for Order #${
              currentOrder.orderNumber.internal
            }`,
            skuID: agg.skuID,
            itemData: agg.itemData,
            movement: {
              type: isReservation ? "reservation" : "release",
              from: isReservation ? "available" : "reserved",
              to: isReservation ? "reserved" : "available",
              quantity: absDifference,
              reason: isReservation
                ? "salesOrderQtyIncreased"
                : "salesOrderQtyDecreased",
              reasonID: this.orderID,
            },
            snapshot: { before: agg.before },
            location: {
              id:
                currentOrder.fulfillmentLocation?.id ||
                currentOrder.location.shipping.id,
              name:
                currentOrder.fulfillmentLocation?.name ||
                currentOrder.location.shipping.name,
            },
            order: currentOrder,
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            extraSalesOrderData: {
              orderID: this.orderID,
            },
          });
        }

        // Emit aggregated restoration activities (one per product)
        for (const restorationKey of Object.keys(
          restorationActivityCollector,
        )) {
          const agg = restorationActivityCollector[restorationKey];
          if (agg.totalQtyToAdd === 0) continue;
          const afterAvailable =
            (agg.before.available ?? 0) + agg.totalQtyToAdd;
          const afterReserved = (agg.before.reserved ?? 0) + agg.totalQtyToAdd;
          await createInventoryActivity({
            action: "Inventory restored",
            item: `${agg.totalQtyToAdd} units of ${agg.itemData.name} (${agg.itemData.sku}) restored from fulfilled Order #${currentOrder.orderNumber.internal}`,
            skuID: agg.skuID,
            itemData: agg.itemData,
            movement: {
              type: "restoration",
              from: "fulfilled",
              to: "reserved",
              quantity: agg.totalQtyToAdd,
              reason: "salesOrderRestored",
              reasonID: this.orderID,
            },
            snapshot: {
              before: agg.before,
              after: {
                ...agg.before,
                available: afterAvailable,
                reserved: afterReserved,
              },
            },
            location: {
              id:
                currentOrder.fulfillmentLocation?.id ||
                currentOrder.location.shipping.id,
              name:
                currentOrder.fulfillmentLocation?.name ||
                currentOrder.location.shipping.name,
            },
            order: currentOrder,
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            extraSalesOrderData: {
              orderID: this.orderID,
            },
          });
        }

        let addedUpdates = [];

        const addedItemActivityCollector = {};

        if (addedItems.length > 0) {
          // Process added items using pre-read data
          for (const addedItemData of addedItemInventoryData) {
            const { cartItem } = addedItemData;

            let promoUnit = cartItem.promo
              ? `<span class='font-weight-bold text-no-wrap'>
                <span class='font-weight-bold text-caption pa-1 rounded' style='background-color:
                  #0277BD; color: #FFFFFF
                '>Promo</span>
              </span>`
              : "";
            let itemText = `<span class="text-capitalize font-weight-bold">${cartItem.name}</span> <span class="text-uppercase">(${cartItem.sku})${promoUnit}</span>`;
            addedUpdates.push(itemText);

            // Use originalInventoryData for validation (state before any mutations in this transaction)
            const currentAvailable =
              originalInventoryData[cartItem.categoryID]?.available?.[
                cartItem.productID
              ] || 0;
            const currentReserved =
              originalInventoryData[cartItem.categoryID]?.reserved?.[
                cartItem.productID
              ] || 0;

            // Calculate available inventory (available - reserved)
            const availableForReservation = currentAvailable - currentReserved;

            // Check if we can reserve the requested quantity
            // if (availableForReservation < cartItem.qty) {
            //   throw new Error(
            //     `Insufficient inventory: ${cartItem.name} (${cartItem.sku}). Available: ${availableForReservation}, Requested: ${cartItem.qty}. Please reduce quantity or check inventory levels.`
            //   );
            // }

            // For activity display only: added-item "before" must be state after restoration/reservation
            // updates so the activity log chain is correct (e.g. Order restored then Order submitted).
            const snapshotAvailable =
              inventoryData[cartItem.categoryID]?.available?.[
                cartItem.productID
              ] ??
              originalInventoryData[cartItem.categoryID]?.available?.[
                cartItem.productID
              ] ??
              0;
            const snapshotReserved =
              inventoryData[cartItem.categoryID]?.reserved?.[
                cartItem.productID
              ] ??
              originalInventoryData[cartItem.categoryID]?.reserved?.[
                cartItem.productID
              ] ??
              0;

            // Update the inventory data to reflect the new reservation
            if (inventoryData[cartItem.categoryID]) {
              if (!inventoryData[cartItem.categoryID].reserved) {
                inventoryData[cartItem.categoryID].reserved = {};
              }
              if (
                !inventoryData[cartItem.categoryID].reserved[cartItem.productID]
              ) {
                inventoryData[cartItem.categoryID].reserved[
                  cartItem.productID
                ] = 0;
              }
              inventoryData[cartItem.categoryID].reserved[cartItem.productID] +=
                cartItem.qty;
            }

            // Only then reserve the inventory
            transaction.set(
              locationRef,
              {
                manufacturing: {
                  [cartItem.categoryID]: {
                    reserved: {
                      [cartItem.productID]: increment(cartItem.qty),
                    },
                  },
                },
              },
              { merge: true },
            );

            // Collect added-item reservation activity for aggregation by product.
            // snapshot.before must reflect post-restoration state (snapshotAvailable/snapshotReserved
            // from inventoryData) so the activity log chain is correct (submit then restore in time).
            const addedProductKey = `${cartItem.categoryID}_${cartItem.productID}`;
            if (!addedItemActivityCollector[addedProductKey]) {
              const currentPending =
                originalInventoryData[cartItem.categoryID]?.pending?.[
                  cartItem.productID
                ] || 0;
              const currentTransfer =
                originalInventoryData[cartItem.categoryID]?.transfer?.[
                  cartItem.productID
                ] || 0;
              const currentInTesting =
                originalInventoryData[cartItem.categoryID]?.inTesting?.[
                  cartItem.productID
                ] || 0;
              addedItemActivityCollector[addedProductKey] = {
                skuID: `${
                  currentOrder.fulfillmentLocation?.id ||
                  currentOrder.location.shipping.id
                }_${currentOrder.brand.id}_${cartItem.categoryID}_${
                  cartItem.productID
                }`,
                itemData: cartItem,
                totalQty: 0,
                before: {
                  available: snapshotAvailable,
                  reserved: snapshotReserved,
                  pending: currentPending,
                  transfer: currentTransfer,
                  inTesting: currentInTesting,
                },
              };
            }
            addedItemActivityCollector[addedProductKey].totalQty +=
              cartItem.qty;

            // Calculate tax
            cartItem.tax = Number(
              parseFloat(
                cartItem.amount -
                  cartItem.amount /
                    (1 + (currentOrder.taxPercentage ?? 0) / 100),
              ).toFixed(2),
            );

            // Ensure relatedID is never undefined
            if (!cartItem.relatedID) {
              console.error("Line item missing relatedID:", cartItem);
              throw new Error("Line item missing relatedID");
            }

            const cartItemDocRef = doc(
              collection(firestore, "salesOrders_lineItems"),
            );

            // Use the processed cartItem instead of the raw one
            cartItem.id = cartItemDocRef.id;
            // cartItem.id = cartItemDocRef.id;

            transaction.set(
              cartItemDocRef,
              {
                ...cartItem,
                id: cartItemDocRef.id,
                created: serverTimestamp(),
                updated: serverTimestamp(),
              },
              { merge: true },
            );
          }

          if (addedUpdates.length > 0) {
            // Create clean description for activity
            const cleanDescription = addedItems
              .map((item) => {
                const totalQty =
                  (item.cases || 0) * (item.unitsPerCase || 1) +
                  (item.singles || 0);
                return `${item.name} (${item.sku}) - Qty: ${totalQty}${
                  item.promo ? " - Promo" : ""
                }`;
              })
              .join(", ");
            // Create activity for added line items
            await createActivity({
              type: "inventory",
              action: "Line items added",
              item: `Added ${addedItems.length} item${
                addedItems.length !== 1 ? "s" : ""
              }: ${cleanDescription}`,
              relatedTo: "salesOrders",
              relatedID: [this.order.id],
              changeData: {
                field: "lineItems",
                action: "added",
                items: addedItems.map((item) => ({
                  id: item.id || "",
                  name: item.name || "",
                  sku: item.sku || "",
                  cases: item.cases || 0,
                  singles: item.singles || 0,
                  amount: item.amount || 0,
                  promo: item.promo || false,
                  qty:
                    (item.cases || 0) * (item.unitsPerCase || 1) +
                    (item.singles || 0),
                  totalAmount:
                    ((item.cases || 0) * (item.unitsPerCase || 1) +
                      (item.singles || 0)) *
                    (item.amount || 0),
                })),
                count: addedItems.length,
                totalValue: addedItems.reduce((sum, item) => {
                  const totalQty =
                    (item.cases || 0) * (item.unitsPerCase || 1) +
                    (item.singles || 0);
                  return sum + totalQty * (item.amount || 0);
                }, 0),
              },
              user: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              account: {
                id: this.order.account.id,
                name: this.order.account.name,
              },
            });

            addedUpdates = addedUpdates.join(" / ");
          }
        }

        // Emit aggregated added-item activities (one per product)
        for (const addedProductKey of Object.keys(addedItemActivityCollector)) {
          const agg = addedItemActivityCollector[addedProductKey];
          if (agg.totalQty === 0) continue;
          const afterReserved = (agg.before.reserved ?? 0) + agg.totalQty;
          await createInventoryActivity({
            action: "Inventory reserved",
            item: `${agg.totalQty} units of ${agg.itemData.name} (${agg.itemData.sku}) reserved for Order #${currentOrder.orderNumber.internal}`,
            skuID: agg.skuID,
            itemData: agg.itemData,
            movement: {
              type: "reservation",
              from: "available",
              to: "reserved",
              quantity: agg.totalQty,
              reason: "salesOrderSubmitted",
              reasonID: this.orderID,
            },
            snapshot: {
              before: agg.before,
              after: {
                ...agg.before,
                reserved: afterReserved,
              },
            },
            location: {
              id:
                currentOrder.fulfillmentLocation?.id ||
                currentOrder.location.shipping.id,
              name:
                currentOrder.fulfillmentLocation?.name ||
                currentOrder.location.shipping.name,
            },
            order: currentOrder,
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            extraSalesOrderData: {
              orderID: this.orderID,
            },
          });
        }

        if (updates.length > 0) {
          // Create clean description for activity
          const updatedCount = updatedLineItems.filter(
            (item) => item.needsPatch,
          ).length;
          const cleanDescription = updatedLineItems
            .filter((item) => item.needsPatch)
            .map((item) => {
              const changes = [];

              // Check for quantity changes
              if (
                item.cases?.new !== item.cases?.existing ||
                item.singles?.new !== item.singles?.existing
              ) {
                const caseQTY = item.caseQTY ?? item.unitsPerCase;
                const oldQty =
                  (item.cases?.existing || 0) * caseQTY +
                  (item.singles?.existing || 0);
                const newQty =
                  (item.cases?.new || 0) * caseQTY + (item.singles?.new || 0);
                changes.push(`Qty: ${oldQty} → ${newQty}`);
              }

              // Check for price changes
              if (item.amount?.new !== item.amount?.existing) {
                const currencyFormatter = new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                });
                changes.push(
                  `Price: ${currencyFormatter.format(
                    item.amount?.existing || 0,
                  )} → ${currencyFormatter.format(item.amount?.new || 0)}`,
                );
              }

              return `${item.name} (${item.sku}) - ${changes.join(", ")}`;
            })
            .join(", ");

          // Create activity for updated line items
          await createActivity({
            type: "inventory",
            action: "Line items updated",
            item: `Updated ${updatedCount} item${
              updatedCount !== 1 ? "s" : ""
            }: ${cleanDescription}`,
            relatedTo: "salesOrders",
            relatedID: [this.order.id],
            changeData: {
              field: "lineItems",
              action: "updated",
              items: updatedLineItems
                .filter((item) => item.needsPatch)
                .map((item) => ({
                  id: item.id || "",
                  name: item.name || "",
                  sku: item.sku || "",
                  changes: {
                    cases:
                      item.cases?.new !== undefined &&
                      item.cases?.existing !== undefined &&
                      item.cases.new !== item.cases.existing
                        ? {
                            old: item.cases.existing,
                            new: item.cases.new,
                          }
                        : null,
                    singles:
                      item.singles?.new !== undefined &&
                      item.singles?.existing !== undefined &&
                      item.singles.new !== item.singles.existing
                        ? {
                            old: item.singles.existing,
                            new: item.singles.new,
                          }
                        : null,
                    amount:
                      item.amount?.new !== undefined &&
                      item.amount?.existing !== undefined &&
                      item.amount.new !== item.amount.existing
                        ? {
                            old: item.amount.existing,
                            new: item.amount.new,
                          }
                        : null,
                    totalQty: (() => {
                      const caseQTY = item.caseQTY ?? item.unitsPerCase;
                      const oldQty =
                        (item.cases?.existing || 0) * caseQTY +
                        (item.singles?.existing || 0);
                      const newQty =
                        (item.cases?.new || 0) * caseQTY +
                        (item.singles?.new || 0);
                      if (oldQty !== newQty) {
                        return { old: oldQty, new: newQty };
                      }
                      return null;
                    })(),
                  },
                })),
              count: updatedLineItems.filter((item) => item.needsPatch).length,
              totalValue: updatedLineItems
                .filter((item) => item.needsPatch)
                .reduce((sum, item) => {
                  const caseQTY = item.caseQTY ?? item.unitsPerCase;
                  const newQty =
                    (item.cases?.new || 0) * caseQTY + (item.singles?.new || 0);
                  return sum + newQty * (item.amount?.new || 0);
                }, 0),
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            account: {
              id: this.order.account.id,
              name: this.order.account.name,
            },
          });

          updates = updates.join(" | ");
        }

        let removedUpdates = [];

        const removedItemActivityCollector = {};

        if (removedItems.length > 0) {
          // delete lineItem documents from salesOrder_lineItems collection
          for (const removedItemData of removedItemInventoryData) {
            const { item, qty } = removedItemData;

            let promoUnit = item.promo
              ? `<span class='font-weight-bold text-no-wrap'>
            <span class='font-weight-bold text-caption pa-1 rounded' style='background-color:
              #0277BD; color: #FFFFFF
            '>Promo</span>
          </span>`
              : "";
            let itemText = `<span class="text-capitalize font-weight-bold">${item.name}</span> <span class="text-uppercase">(${item.sku})${promoUnit}</span>`;
            removedUpdates.push(itemText);

            // Check if order is fulfilled to determine which inventory to update
            if (currentOrder.status === "fulfilled") {
              // For fulfilled orders, update available inventory (not reserved)
              const currentAvailable =
                inventoryData[item.categoryID]?.available?.[item.productID] ||
                0;
              const currentReserved =
                inventoryData[item.categoryID]?.reserved?.[item.productID] || 0;

              // Calculate new available amount, ensuring it never goes below 0
              const newAvailable = Math.max(0, currentAvailable + qty);

              transaction.set(
                locationRef,
                {
                  manufacturing: {
                    [item.categoryID]: {
                      available: {
                        [item.productID]: newAvailable,
                      },
                    },
                  },
                },
                { merge: true },
              );

              // Collect removed-item release activity for aggregation by product
              const removedKey = `${item.categoryID}_${item.productID}`;
              if (!removedItemActivityCollector[removedKey]) {
                const currentPending =
                  inventoryData[item.categoryID]?.pending?.[item.productID] ||
                  0;
                const currentTransfer =
                  inventoryData[item.categoryID]?.transfer?.[item.productID] ||
                  0;
                const currentInTesting =
                  inventoryData[item.categoryID]?.inTesting?.[item.productID] ||
                  0;
                removedItemActivityCollector[removedKey] = {
                  skuID: `${
                    currentOrder.fulfillmentLocation?.id ||
                    currentOrder.location.shipping.id
                  }_${currentOrder.brand.id}_${item.categoryID}_${
                    item.productID
                  }`,
                  itemData: item,
                  totalQty: 0,
                  isFulfilled: true,
                  before: {
                    available: currentAvailable,
                    reserved: currentReserved,
                    pending: currentPending,
                    transfer: currentTransfer,
                    inTesting: currentInTesting,
                  },
                };
              }
              removedItemActivityCollector[removedKey].totalQty += qty;
            } else {
              // For non-fulfilled orders, update reserved inventory
              const currentReserved =
                inventoryData[item.categoryID]?.reserved?.[item.productID] || 0;
              const currentAvailable =
                inventoryData[item.categoryID]?.available?.[item.productID] ||
                0;

              // Calculate new reserved amount, ensuring it never goes below 0
              const newReserved = Math.max(0, currentReserved - qty);

              transaction.set(
                locationRef,
                {
                  manufacturing: {
                    [item.categoryID]: {
                      reserved: {
                        [item.productID]: increment(-qty),
                      },
                    },
                  },
                },
                { merge: true },
              );

              // Collect removed-item release activity for aggregation by product
              const removedKey = `${item.categoryID}_${item.productID}`;
              if (!removedItemActivityCollector[removedKey]) {
                const currentPending =
                  inventoryData[item.categoryID]?.pending?.[item.productID] ||
                  0;
                const currentTransfer =
                  inventoryData[item.categoryID]?.transfer?.[item.productID] ||
                  0;
                const currentInTesting =
                  inventoryData[item.categoryID]?.inTesting?.[item.productID] ||
                  0;
                removedItemActivityCollector[removedKey] = {
                  skuID: `${
                    currentOrder.fulfillmentLocation?.id ||
                    currentOrder.location.shipping.id
                  }_${currentOrder.brand.id}_${item.categoryID}_${
                    item.productID
                  }`,
                  itemData: item,
                  totalQty: 0,
                  isFulfilled: false,
                  before: {
                    available: currentAvailable,
                    reserved: currentReserved,
                    pending: currentPending,
                    transfer: currentTransfer,
                    inTesting: currentInTesting,
                  },
                };
              }
              removedItemActivityCollector[removedKey].totalQty += qty;
            }

            // LeafLink: delete on external platform first so we never remove from Firestore if LeafLink fails (keeps sync)
            if (this.orderIsExternal) {
              switch (this.externalOrderType) {
                case "leaflink": {
                  if (this.externalUpdates.leaflink.includes("orderUpdated")) {
                    let externalItem = null;
                    if (item.externalIDs?.leaflinkLineItemID) {
                      externalItem = {
                        id: item.externalIDs.leaflinkLineItemID,
                      };
                    } else {
                      if (this.leaflinkClient.lineItems?.length === 0) {
                        await this.leaflinkClient.getLineItems();
                      }
                      externalItem = this.leaflinkClient.lineItems.find(
                        (external) =>
                          external.productID === item.externalIDs.leaflink &&
                          item.amount === external.sale_price.amount,
                      );
                    }
                    if (externalItem) {
                      addBreadcrumb("leaflink: deleteLineItem (updateLineItems)", {
                        workflow: "sales-order",
                        data: { orderId: this.orderID, itemId: item.id, externalItemId: externalItem.id },
                      });
                      try {
                        await this.leaflinkClient.deleteLineItem(externalItem);
                      } catch (leaflinkErr) {
                        logWorkflowWarning(`LeafLink: deleteLineItem failed — ${leaflinkErr.message || leaflinkErr}`, {
                          workflow: "sales-order",
                          action: "leaflink-delete-line-item",
                          context: { orderId: this.orderID, itemId: item.id, itemName: item.name, sku: item.sku },
                        });
                        throw new Error(
                          `LeafLink sync failed while removing ${item.name} (${
                            item.sku
                          }). Line item was not removed. ${
                            leaflinkErr.message || leaflinkErr
                          }`,
                        );
                      }
                    }
                  }
                  break;
                }
                default:
                  break;
              }
            }

            await deleteDoc(doc(firestore, "salesOrders_lineItems", item.id));
          }
        }

        // Emit aggregated removed-item activities (one per product)
        for (const removedKey of Object.keys(removedItemActivityCollector)) {
          const agg = removedItemActivityCollector[removedKey];
          if (agg.totalQty === 0) continue;
          const reason = agg.isFulfilled
            ? "salesOrderLineRemovedFulfilled"
            : "salesOrderLineRemovedAccepted";
          await createInventoryActivity({
            action: "Inventory released",
            item: `${agg.totalQty} units of ${agg.itemData.name} (${
              agg.itemData.sku
            }) released from ${agg.isFulfilled ? "fulfilled" : ""} Order #${
              currentOrder.orderNumber.internal
            }`,
            skuID: agg.skuID,
            itemData: agg.itemData,
            movement: {
              type: "release",
              from: agg.isFulfilled ? "fulfilled" : "reserved",
              to: "available",
              quantity: agg.totalQty,
              reason,
              reasonID: this.orderID,
            },
            snapshot: { before: agg.before },
            location: {
              id:
                currentOrder.fulfillmentLocation?.id ||
                currentOrder.location.shipping.id,
              name:
                currentOrder.fulfillmentLocation?.name ||
                currentOrder.location.shipping.name,
            },
            order: currentOrder,
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            extraSalesOrderData: {
              orderID: this.orderID,
            },
          });
        }

        if (removedUpdates.length > 0) {
          // Create clean description for activity
          const cleanDescription = removedItems
            .map((item) => {
              const totalQty =
                (item.cases || 0) * (item.unitsPerCase || 1) +
                (item.singles || 0);
              return `${item.name} (${item.sku}) - Qty: ${totalQty}${
                item.promo ? " - Promo" : ""
              }`;
            })
            .join(", ");
          // Create activity for removed line items
          await createActivity({
            type: "inventory",
            action: "Line items removed",
            item: `Removed ${removedItems.length} item${
              removedItems.length !== 1 ? "s" : ""
            }: ${cleanDescription}`,
            relatedTo: "salesOrders",
            relatedID: [this.order.id],
            changeData: {
              field: "lineItems",
              action: "removed",
              items: removedItems.map((item) => ({
                id: item.id || "",
                name: item.name || "",
                sku: item.sku || "",
                cases: item.cases || 0,
                singles: item.singles || 0,
                amount: item.amount || 0,
                promo: item.promo || false,
                qty:
                  (item.cases || 0) * (item.unitsPerCase || 1) +
                  (item.singles || 0),
                totalAmount:
                  ((item.cases || 0) * (item.unitsPerCase || 1) +
                    (item.singles || 0)) *
                  (item.amount || 0),
              })),
              count: removedItems.length,
              totalValue: removedItems.reduce((sum, item) => {
                const totalQty =
                  (item.cases || 0) * (item.unitsPerCase || 1) +
                  (item.singles || 0);
                return sum + totalQty * (item.amount || 0);
              }, 0),
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            account: {
              id: this.order.account.id,
              name: this.order.account.name,
            },
          });

          removedUpdates = removedUpdates.join(" / ");
        }

        // Update individual line items
        if (existingItems.filter((item) => item.needsPatch).length > 0) {
          for (const item of existingItems.filter((item) => item.needsPatch)) {
            const lineItemDoc = doc(
              firestore,
              "salesOrders_lineItems",
              item.id,
            );

            if (this.orderIsExternal) {
              switch (this.externalOrderType) {
                case "leaflink":
                  if (this.externalUpdates.leaflink.includes("orderUpdated")) {
                    let externalItem = null;
                    if (item.externalIDs?.leaflinkLineItemID) {
                      externalItem = {
                        id: item.externalIDs.leaflinkLineItemID,
                      };
                    } else {
                      if (this.leaflinkClient.lineItems?.length === 0) {
                        await this.leaflinkClient.getLineItems();
                      }
                      externalItem = this.leaflinkClient.lineItems.find(
                        (external) =>
                          external.productID === item.externalIDs.leaflink,
                      );
                    }
                    let payloadData = {
                      ordered_unit_price: {
                        amount: item.amount,
                        currency: "USD",
                      },
                      sale_price: {
                        amount: item.amount,
                        currency: "USD",
                      },
                      notes: item.notes,
                    };

                    if (item.needsQTYPatch) {
                      payloadData = {
                        ...payloadData,
                        quantity: item.qty,
                      };
                    }
                    if (externalItem) {
                      addBreadcrumb("leaflink: updateLineItem", {
                        workflow: "sales-order",
                        data: { orderId: this.orderID, itemId: item.id, externalItemId: externalItem.id },
                      });
                      try {
                        await this.leaflinkClient.updateLineItem(externalItem, payloadData);
                        item.externalIDs.leaflinkLineItemID = externalItem.id;
                      } catch (error) {
                        logWorkflowWarning(`LeafLink: updateLineItem failed — ${error.message}`, {
                          workflow: "sales-order",
                          action: "leaflink-update-line-item",
                          context: { orderId: this.orderID, itemId: item.id, balance: this.order?.balance },
                        });
                        throw error;
                      }
                    }
                  }
                  break;
                default:
                  break;
              }
            }

            delete item.needsPatch;
            if (item.needsQTYPatch) {
              delete item.needsQTYPatch;
            }

            item.tax = Number(
              parseFloat(
                item.amount -
                  item.amount / (1 + (currentOrder.taxPercentage ?? 0) / 100),
              ).toFixed(2),
            );

            transaction.set(
              lineItemDoc,
              {
                ...item,
                updated: serverTimestamp(),
                lastUpdated: {
                  name: store.getters.getUser.name,
                  uid: store.getters.getUser.uid,
                },
              },
              { merge: true },
            );
          }
        }

        let allLineItems = existingItems.concat(
          addedItemInventoryData.map((data) => data.cartItem),
        );

        console.log("allLineItems HERE:", allLineItems);

        //  Build allLineItems from complete cart state (not just modified/added items)
        allLineItems.map((cartItem) => {
          // Check if this item was already processed as an existing updated item
          // Use proper identification (productID + promo + amount)
          let existingItem = existingItems.find(
            (item) =>
              (item.id === cartItem.id ||
                item.productID === (cartItem.productID || cartItem.id)) &&
              Boolean(item.promo) === Boolean(cartItem.promo) &&
              item.amount.new === cartItem.amount.new,
          );
          if (existingItem) {
            return existingItem;
          }

          // Check if this item was already processed as a new added item
          // Use proper identification (productID + promo + amount)
          let addedItemData = addedItemInventoryData.find(
            (data) =>
              data.cartItem.productID === (cartItem.productID || cartItem.id) &&
              Boolean(data.cartItem.promo) === Boolean(cartItem.promo) &&
              data.cartItem.amount.new === cartItem.amount.new,
          );
          if (addedItemData) {
            return addedItemData.cartItem;
          }

          return cartItem;
        });

        // Helper function to safely convert to number with fallback
        const safeNumber = (value, fallback = 0) => {
          const num = Number(value);
          return isNaN(num) ? fallback : num;
        };

        let priceTotal = cart.reduce((acc, item) => {
          const amount = safeNumber(item.amount?.new, 0);
          const cases = safeNumber(item.cases?.new, 0);
          const singles = safeNumber(item.singles?.new, 0);
          const caseQTY = safeNumber(item.caseQTY, 0);

          // Handle both case-based and non-case products
          const isCaseProduct = caseQTY > 0;
          const totalUnits = isCaseProduct
            ? cases * caseQTY + singles
            : singles;

          return acc + amount * totalUnits;
        }, 0);

        let unitTotal = cart.reduce((acc, item) => {
          const cases = safeNumber(item.cases?.new, 0);
          const singles = safeNumber(item.singles?.new, 0);
          const caseQTY = safeNumber(item.caseQTY, 0);

          // Handle both case-based and non-case products
          const isCaseProduct = caseQTY > 0;
          return acc + (isCaseProduct ? cases * caseQTY + singles : singles);
        }, 0);

        // Include shipping fee in total calculation
        const currentShippingFee = safeNumber(
          currentOrder.balance?.shipping,
          0,
        );
        const totalWithShipping = priceTotal + currentShippingFee;

        const currentBalanceTotal = safeNumber(currentOrder.balance?.total, 0);
        const currentBalanceDue = safeNumber(currentOrder.balance?.due, 0);

        let paymentsApplied = safeNumber(
          currentBalanceTotal - currentBalanceDue,
          0,
        );

        let totalDiscountValue = () => {
          let isPercentage = currentOrder.discount?.type === "%";
          let discountValue = currentOrder.discount?.value ?? 0;
          let totalValue = priceTotal;
          if (isPercentage) {
            return (discountValue / 100) * totalValue;
          } else {
            return discountValue;
          }
        };

        let newTotal = parseFloat(
          priceTotal - totalDiscountValue() + currentShippingFee,
        ).toFixed(2);
        let newBalance = parseFloat(newTotal - paymentsApplied).toFixed(2);

        addBreadcrumb("updateLineItems: totals calculated", {
          workflow: "sales-order",
          data: { orderId: this.orderID, newTotal, newBalance, newShipping: currentShippingFee },
        });

        // Safety check - ensure no undefined values
        if (unitTotal === undefined || unitTotal === null) {
          console.error("unitTotal is undefined/null:", unitTotal);
          logWorkflowWarning("updateLineItems: unitTotal is undefined/null, defaulting to 0", {
            workflow: "sales-order",
            action: "update-line-items-safety-check",
            context: { orderId: this.orderID },
          });
          unitTotal = 0;
        }

        if (!allLineItems || allLineItems.length === 0) {
          console.error("allLineItems is undefined/empty:", allLineItems);
          logWorkflowWarning("updateLineItems: allLineItems is undefined/empty, defaulting to []", {
            workflow: "sales-order",
            action: "update-line-items-safety-check",
            context: { orderId: this.orderID },
          });
          allLineItems = [];
        }

        // Deep clean allLineItems to remove any undefined values
        allLineItems = allLineItems.map((item) => {
          const cleanedItem = {};
          Object.keys(item).forEach((key) => {
            if (item[key] !== undefined && item[key] !== null) {
              cleanedItem[key] = item[key];
            } else {
              logWorkflowWarning(`updateLineItems: undefined/null value for key '${key}' in line item`, {
                workflow: "sales-order",
                action: "update-line-items-data-integrity",
                context: { orderId: this.orderID, key, itemId: item.id },
              });
              // Provide fallback values for critical fields
              if (key === "relatedID") {
                cleanedItem[key] = currentOrder.id || this.orderID;
              } else if (key === "tax") {
                cleanedItem[key] = 0;
              } else if (key === "externalIDs") {
                cleanedItem[key] = {};
              } else {
                cleanedItem[key] = null;
              }
            }
          });
          return cleanedItem;
        });

        if (newBalance === undefined || newBalance === null) {
          console.error("newBalance is undefined/null:", newBalance);
          logWorkflowWarning("updateLineItems: newBalance is undefined/null, defaulting to 0.00", {
            workflow: "sales-order",
            action: "update-line-items-safety-check",
            context: { orderId: this.orderID, newTotal, unitTotal },
          });
          newBalance = "0.00";
        }

        if (newTotal === undefined || newTotal === null) {
          console.error("newTotal is undefined/null:", newTotal);
          logWorkflowWarning("updateLineItems: newTotal is undefined/null, defaulting to 0.00", {
            workflow: "sales-order",
            action: "update-line-items-safety-check",
            context: { orderId: this.orderID, unitTotal },
          });
          newTotal = "0.00";
        }

        if (currentShippingFee === undefined || currentShippingFee === null) {
          console.error(
            "currentShippingFee is undefined/null:",
            currentShippingFee,
          );
          logWorkflowWarning("updateLineItems: currentShippingFee is undefined/null, defaulting to 0", {
            workflow: "sales-order",
            action: "update-line-items-safety-check",
            context: { orderId: this.orderID },
          });
          currentShippingFee = 0;
        }

        let discount = {
          value: currentOrder.discount?.value ?? 0,
          type: currentOrder.discount?.type ?? "%",
          total: totalDiscountValue(),
        };

        // Update order within transaction
        transaction.set(
          salesOrderDoc,
          {
            updated: serverTimestamp(),
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            units: unitTotal,
            lineItems: allLineItems,
            balance: {
              due: newBalance,
              total: newTotal,
              shipping: currentShippingFee,
            },
            discount,
          },
          { merge: true },
        );

        // Return data for external processing
        return {
          allLineItems,
          updates,
          addedUpdates,
          removedUpdates,
          addedItems: addedItemInventoryData.map((data) => data.cartItem),
          existingItems,
          newBalance: Number(newBalance),
          newTotal: Number(newTotal),
        };
      });

      addBreadcrumb("updateLineItems: transaction committed", {
        workflow: "sales-order",
        data: { orderId: this.orderID, newBalance: result.newBalance },
      });
      await this._checkBalanceAnomaly("update-line-items", {
        newBalance: result.newBalance,
        newTotal: result.newTotal,
      });

      // Handle external API calls AFTER transaction succeeds
      // This follows Firebase best practices of keeping transactions focused
      if (result.addedItems?.length > 0) {
        await this.processExternalLineItems(
          result.addedItems,
          result.allLineItems,
        );
      }

      // Update existing line items with external IDs if needed
      if (
        this.orderIsExternal &&
        this.externalOrderType === "leaflink" &&
        result.existingItems?.length > 0
      ) {
        if (this.externalUpdates.leaflink.includes("orderUpdated")) {
          await this.processExternalLineItemUpdates(result.existingItems);
        }
      }

      await this.checkOverpaymentCredits(result.newBalance);
    } catch (error) {
      throw error;
    }
  }

  async updateOrderData(fields) {
    await this.getOrder();
    await this.getLineItems();
    await this.getFulfillmentLocations();
    await this.getPayments();
    addBreadcrumb("updateOrderData: start", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        changedFields: Object.keys(fields),
        orderStatus: this.order?.status,
        currentBalance: this.order?.balance,
      },
    });
    const batch = writeBatch(firestore);

    const salesOrderDoc = doc(firestore, "salesOrders", this.order.id);

    let needsExternalUpdate = false;
    let needsPriceUpdate = false;
    let updatePayload = {};
    let updates = [];

    if (fields.hasOwnProperty("paymentTerm")) {
      updatePayload.paymentTerm = fields.paymentTerm;

      const oldValue = store.getters.getPaymentTerms.find(
          (option) => option.value === this.order.paymentTerm,
        ),
        newValue = store.getters.getPaymentTerms.find(
          (option) => option.value === fields.paymentTerm,
        );

      updates.push(
        `Payment Term, <span class="text-uppercase">old</span>: ${oldValue.text} => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${newValue.text}</span>`,
      );

      needsPriceUpdate = true;

      // Update external order payment term
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (this.externalUpdates.leaflink.includes("orderUpdated")) {
              addBreadcrumb("leaflink: updateOrderData payment term", {
                workflow: "sales-order",
                data: { orderId: this.orderID, paymentTerm: fields.paymentTerm },
              });
              try {
                let orderData = await this.leaflinkClient.getOrder();
                let paymentOption = orderData?.available_payment_options.find(
                  (option) =>
                    fields.paymentTerm.replace(/\s/g, "") ===
                    option.payment_term.code,
                );
                if (paymentOption) {
                  let patchData = {
                    selected_payment_option: {
                      payment_method_id: 1,
                      payment_term_id: paymentOption.payment_term.id,
                    },
                  };
                  await this.leaflinkClient.updateOrderData(patchData);
                }
              } catch (error) {
                logWorkflowWarning(`LeafLink: updateOrderData (payment term) failed — ${error.message}`, {
                  workflow: "sales-order",
                  action: "leaflink-update-order-payment-term",
                  context: { orderId: this.orderID, paymentTerm: fields.paymentTerm, balance: this.order?.balance },
                });
                throw error;
              }
            }
            break;
          default:
            break;
        }
      }
    }

    let shippingDifference = 0;

    if (fields.hasOwnProperty("shippingFee")) {
      if (typeof fields.shippingFee !== "number") {
        fields.shippingFee = Number(fields.shippingFee ?? 0);
      }
      shippingDifference =
        Number(fields.shippingFee ?? 0) - Number(this.order.balance.shipping);

      updatePayload.balance = {
        shipping: parseFloat(fields.shippingFee ?? 0).toFixed(2),
        total: parseFloat(
          Number(this.order.balance.total) + shippingDifference,
        ).toFixed(2),
        due: parseFloat(
          Number(this.order.balance.due) + shippingDifference,
        ).toFixed(2),
      };

      needsExternalUpdate = true;

      // Update external order payment term
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (this.externalUpdates.leaflink.includes("orderUpdated")) {
              addBreadcrumb("leaflink: updateOrderData shipping fee", {
                workflow: "sales-order",
                data: { orderId: this.orderID, shippingFee: fields.shippingFee },
              });
              try {
                let patchData = {
                  shipping_charge: {
                    amount: Number(fields.shippingFee ?? 0),
                    currency: "USD",
                  },
                };
                await this.leaflinkClient.updateOrderData(patchData);
              } catch (error) {
                logWorkflowWarning(`LeafLink: updateOrderData (shipping fee) failed — ${error.message}`, {
                  workflow: "sales-order",
                  action: "leaflink-update-order-shipping",
                  context: { orderId: this.orderID, shippingFee: fields.shippingFee, balance: this.order?.balance },
                });
                throw error;
              }
            }
            break;
          default:
            break;
        }
      }

      const oldValue = Number(this.order.balance.shipping),
        newValue = Number(fields.shippingFee ?? 0);

      let currencyFormat = (value) => {
        return Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "usd",
        }).format(value);
      };

      updates.push(
        `Shipping Cost, <span class="text-uppercase">old</span>: <span>${currencyFormat(
          oldValue,
        )}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${currencyFormat(
          newValue,
        )}</span>`,
      );

      if (["shipped", "complete"].includes(this.order.status)) {
        const brandStatsDoc = doc(
          firestore,
          "brandStats",
          `${this.order.account.id}_${this.order.brand.id}`,
        );

        const brandStats = await getDoc(brandStatsDoc);

        let currentAccountBalance = brandStats.data().metrics.balance;
        let currentAccountTotalSales = brandStats.data().metrics.totalSales;
        const newBalance = Number(
          parseFloat(currentAccountBalance + shippingDifference).toFixed(2),
        );
        const newTotalSales = Number(
          parseFloat(currentAccountTotalSales + shippingDifference).toFixed(2),
        );
        batch.set(
          brandStatsDoc,
          {
            metrics: {
              balance: newBalance,
              totalSales: newTotalSales,
            },
          },
          { merge: true },
        );
      }
    }
    if (fields.hasOwnProperty("shipDate")) {
      updatePayload.shipDate = moment(fields.shipDate)
        .startOf("day")
        .add(9, "hours")
        .tz("America/Los_Angeles")
        .toDate();

      needsExternalUpdate = true;

      const oldValue = this.order.shipDate
          ? moment(this.order.shipDate?.seconds * 1000).format("MM/DD/YYYY")
          : "[Not Set]",
        newValue = moment(fields.shipDate).format("MM/DD/YYYY");

      updates.push(
        `Ship Date, <span class="text-uppercase">old</span>: <span>${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${newValue}</span>`,
      );
    }

    if (fields.hasOwnProperty("manifest")) {
      updatePayload.orderNumber = { manifest: fields.manifest };

      const oldValue =
          this.order.orderNumber?.manifest?.length > 0
            ? this.order.orderNumber.manifest.toUpperCase()
            : "[Not Set]",
        newValue =
          fields.manifest.length > 0
            ? fields.manifest.toUpperCase()
            : "[Not Set]";

      updates.push(
        `Manifest #, <span class="text-uppercase">old</span>: <span>${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${newValue}</span>`,
      );
    }

    if (fields.hasOwnProperty("deliveryOption")) {
      updatePayload.deliveryOption = fields.deliveryOption;

      const oldValue =
          store.getters.getDeliveryTypes.find(
            (item) => item.value === this.order.deliveryOption,
          )?.text ?? "[Not Set]",
        newValue =
          store.getters.getDeliveryTypes.find(
            (item) => item.value === fields.deliveryOption,
          )?.text ?? "[Not Set]";

      updates.push(
        `Delivery Type, <span class="text-uppercase">old</span>: <span class="text-capitalize">${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold text-capitalize">${newValue}</span>`,
      );
    }

    if (fields.hasOwnProperty("deliveredBy")) {
      updatePayload.deliveredBy = {
        id: fields.deliveredBy.id,
        name: fields.deliveredBy.name,
      };

      const oldValue = this.order.deliveredBy?.name || "[Not Set]",
        newValue = fields.deliveredBy?.name ?? "[Not Set]";

      updates.push(
        `Delivery By, <span class="text-uppercase">old</span>: <span class="text-capitalize">${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold text-capitalize">${newValue}</span>`,
      );
    }

    let deliveryFeeDifference = 0;

    if (fields.hasOwnProperty("deliveryLogisticsFee")) {
      deliveryFeeDifference =
        Number(fields.deliveryLogisticsFee) -
        Number(this.order.deliveryLogisticsFee);

      if (updatePayload.balance?.due) {
        updatePayload.balance.due = parseFloat(
          Number(updatePayload.balance.due) + deliveryFeeDifference,
        ).toFixed(2);
        updatePayload.balance.total = parseFloat(
          Number(updatePayload.balance.total) + deliveryFeeDifference,
        ).toFixed(2);
      } else {
        updatePayload.balance = {
          total: parseFloat(
            Number(this.order.balance.total) + deliveryFeeDifference,
          ).toFixed(2),
          due: parseFloat(
            Number(this.order.balance.due) + deliveryFeeDifference,
          ).toFixed(2),
        };
      }

      updatePayload.deliveryLogisticsFee = Number(
        fields.deliveryLogisticsFee ?? 0,
      );

      let currencyFormat = (value) => {
        return Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "usd",
        }).format(value);
      };

      const oldValue =
          this.order.deliveryLogisticsFee !== null
            ? currencyFormat(this.order.deliveryLogisticsFee)
            : "[Not Set]",
        newValue =
          fields.deliveryLogisticsFee !== null
            ? currencyFormat(fields.deliveryLogisticsFee)
            : "[Not Set]";

      updates.push(
        `Delivery/Logistics Fee, <span class="text-uppercase">old</span>: <span class="text-capitalize">${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold text-capitalize">${newValue}</span>`,
      );
    }

    if (fields.hasOwnProperty("notes")) {
      updatePayload.notes = fields.notes;

      needsExternalUpdate = true;

      const oldValue =
          this.order.notes.length > 0 ? `"${this.order.notes}"` : "[Not Set]",
        newValue = fields.notes.length > 0 ? `"${fields.notes}"` : "[Not Set]";

      updates.push(
        `Notes, <span class="text-uppercase">old</span>: <span>${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${newValue}</span>`,
      );
    }
    if (fields.hasOwnProperty("internalNotes")) {
      updatePayload.internalNotes = fields.internalNotes;

      const oldValue =
          this.order.internalNotes.length > 0
            ? `"${this.order.internalNotes}"`
            : "[Not Set]",
        newValue =
          fields.internalNotes.length > 0
            ? `"${fields.internalNotes}"`
            : "[Not Set]";

      updates.push(
        `Internal Notes, <span class="text-uppercase">old</span>: <span>${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${newValue}</span>`,
      );
    }
    if (fields.hasOwnProperty("deliveryPreferences")) {
      updatePayload.deliveryPreferences = fields.deliveryPreferences;

      const oldValue =
          this.order.account.deliveryPreferences &&
          this.order.account.deliveryPreferences.length > 0
            ? `"${this.order.account.deliveryPreferences}"`
            : "[Not Set]",
        newValue =
          fields.deliveryPreferences.length > 0
            ? `"${fields.deliveryPreferences}"`
            : "[Not Set]";

      updates.push(
        `Delivery Preferences, <span class="text-uppercase">old</span>: <span>${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${newValue}</span>`,
      );
    }

    if (
      fields.hasOwnProperty("discountType") ||
      fields.hasOwnProperty("discountValue")
    ) {
      let priceTotal = this.lineItems.reduce((a, b) => a + b.amount * b.qty, 0);

      let totalDiscountValue = () => {
        let isPercentage =
          (fields.discountType ?? this.order.discount?.type ?? "%") === "%";
        let discountValue =
          fields.discountValue ?? this.order.discount?.value ?? 0;
        let totalValue = priceTotal;
        if (isPercentage) {
          return (discountValue / 100) * totalValue;
        } else {
          return discountValue;
        }
      };

      let paymentsApplied =
        Number(this.order.balance.total) - Number(this.order.balance.due);

      let newTotal = parseFloat(
        priceTotal - totalDiscountValue() + Number(this.order.balance.shipping),
      ).toFixed(2);

      let newBalance = parseFloat(newTotal - paymentsApplied).toFixed(2);

      updatePayload.discount = {
        type: fields.discountType ?? this.order.discount?.type ?? "%",
        value: fields.discountValue ?? this.order.discount?.value ?? 0,
        total: totalDiscountValue(),
      };

      updatePayload.balance = {
        total: newTotal,
        due: newBalance,
      };

      if (["shipped", "complete"].includes(this.order.status)) {
        if (newBalance <= 0) {
          updatePayload.status = "complete";
          updatePayload.flags = {
            paid: true,
          };
          updatePayload.records = {
            completed: serverTimestamp(),
          };

          if (this.orderIsExternal) {
            switch (this.externalOrderType) {
              case "leaflink":
                if (this.externalUpdates.leaflink.includes("statusUpdated")) {
                  addBreadcrumb("leaflink: transitionOrder complete (discount)", {
                    workflow: "sales-order",
                    data: { orderId: this.orderID, newBalance, orderStatus: this.order?.status },
                  });
                  try {
                    await this.leaflinkClient.transitionOrder("complete");
                  } catch (error) {
                    logWorkflowWarning(`LeafLink: transitionOrder("complete") failed — ${error.message}`, {
                      workflow: "sales-order",
                      action: "leaflink-transition-complete-discount",
                      context: { orderId: this.orderID, newBalance, balance: this.order?.balance },
                    });
                    throw error;
                  }
                }
                break;
              default:
                break;
            }
          }
        } else {
          updatePayload.status = "shipped";
          updatePayload.flags = {
            paid: false,
          };
          updatePayload.records = {
            completed: null,
          };
        }

        let balanceDifference = newBalance - Number(this.order.balance.due);

        const brandStatsDoc = doc(
          firestore,
          "brandStats",
          `${this.order.account.id}_${this.order.brand.id}`,
        );

        const brandStats = await getDoc(brandStatsDoc);

        let currentAccountBalance = brandStats.data().metrics.balance;
        let currentAccountTotalSales = brandStats.data().metrics.totalSales;
        const newAccountBalance = Number(
          parseFloat(currentAccountBalance + balanceDifference).toFixed(2),
        );
        const newTotalSales = Number(
          parseFloat(currentAccountTotalSales + balanceDifference).toFixed(2),
        );
        batch.set(
          brandStatsDoc,
          {
            metrics: {
              balance: newAccountBalance,
              totalSales: newTotalSales,
            },
          },
          { merge: true },
        );
      }

      updates.push(`Discount Data`);

      needsExternalUpdate = true;
    }

    if (Object.keys(updatePayload).length > 0) {
      console.log(updatePayload);
      batch.set(salesOrderDoc, updatePayload, { merge: true });
    }

    let externalUpdates = false;
    if (this.orderIsExternal) {
      switch (this.externalOrderType) {
        case "leaflink":
          if (this.externalUpdates.leaflink.includes("orderUpdated")) {
            externalUpdates = true;
          }
          break;
        default:
          break;
      }
    }

    if (updates.length > 0) {
      // Create clean description for activity
      const cleanDescription = updates.join(", ");

      // Create structured change data for better display
      const changeData = {
        orderNumber: this.order.orderNumber,
        fieldChanges: [], // Array of structured field changes
        summary: {
          totalFieldsChanged: Object.keys(fields).length,
          hasPriceChanges: needsPriceUpdate,
          hasExternalUpdates: needsExternalUpdate && externalUpdates,
        },
      };

      // Process each field change into structured format
      if (fields.hasOwnProperty("paymentTerm")) {
        const oldValue = store.getters.getPaymentTerms.find(
          (option) => option.value === this.order.paymentTerm,
        );
        const newValue = store.getters.getPaymentTerms.find(
          (option) => option.value === fields.paymentTerm,
        );

        changeData.fieldChanges.push({
          field: "paymentTerm",
          label: "Payment Term",
          oldValue: oldValue?.text || this.order.paymentTerm,
          newValue: newValue?.text || fields.paymentTerm,
          type: "selection",
          impact: "price",
        });
      }

      if (fields.hasOwnProperty("shippingFee")) {
        const oldValue = Number(this.order.balance.shipping);
        const newValue = Number(fields.shippingFee ?? 0);

        changeData.fieldChanges.push({
          field: "shippingFee",
          label: "Shipping Fee",
          oldValue: `$${oldValue.toFixed(2)}`,
          newValue: `$${newValue.toFixed(2)}`,
          type: "currency",
          impact: "balance",
          difference: shippingDifference,
        });
      }

      if (fields.hasOwnProperty("shipDate")) {
        const oldValue = this.order.shipDate
          ? moment(this.order.shipDate?.seconds * 1000).format("MM/DD/YYYY")
          : "[Not Set]";
        const newValue = moment(fields.shipDate).format("MM/DD/YYYY");

        changeData.fieldChanges.push({
          field: "shipDate",
          label: "Ship Date",
          oldValue: oldValue,
          newValue: newValue,
          type: "date",
          impact: "logistics",
        });
      }

      if (fields.hasOwnProperty("manifest")) {
        const oldValue =
          this.order.orderNumber?.manifest?.length > 0
            ? this.order.orderNumber.manifest.toUpperCase()
            : "[Not Set]";
        const newValue =
          fields.manifest.length > 0
            ? fields.manifest.toUpperCase()
            : "[Not Set]";

        changeData.fieldChanges.push({
          field: "manifest",
          label: "Manifest #",
          oldValue: oldValue,
          newValue: newValue,
          type: "text",
          impact: "logistics",
        });
      }

      if (fields.hasOwnProperty("deliveryOption")) {
        const oldValue =
          store.getters.getDeliveryTypes.find(
            (item) => item.value === this.order.deliveryOption,
          )?.text ?? "[Not Set]";
        const newValue =
          store.getters.getDeliveryTypes.find(
            (item) => item.value === fields.deliveryOption,
          )?.text ?? "[Not Set]";

        changeData.fieldChanges.push({
          field: "deliveryOption",
          label: "Delivery Type",
          oldValue: oldValue,
          newValue: newValue,
          type: "selection",
          impact: "logistics",
        });
      }

      if (fields.hasOwnProperty("deliveredBy")) {
        const oldValue = this.order.deliveredBy?.name || "[Not Set]";
        const newValue = fields.deliveredBy?.name ?? "[Not Set]";

        changeData.fieldChanges.push({
          field: "deliveredBy",
          label: "Delivered By",
          oldValue: oldValue,
          newValue: newValue,
          type: "user",
          impact: "logistics",
        });
      }

      if (fields.hasOwnProperty("deliveryLogisticsFee")) {
        const oldValue =
          this.order.deliveryLogisticsFee !== null
            ? this.order.deliveryLogisticsFee
            : "[Not Set]";
        const newValue =
          fields.deliveryLogisticsFee !== null
            ? fields.deliveryLogisticsFee
            : "[Not Set]";

        changeData.fieldChanges.push({
          field: "deliveryLogisticsFee",
          label: "Delivery/Logistics Fee",
          oldValue:
            oldValue !== "[Not Set]" ? `$${oldValue.toFixed(2)}` : oldValue,
          newValue:
            newValue !== "[Not Set]" ? `$${newValue.toFixed(2)}` : newValue,
          type: "currency",
          impact: "balance",
          difference: deliveryFeeDifference,
        });
      }

      if (fields.hasOwnProperty("notes")) {
        const oldValue =
          this.order.notes.length > 0 ? `"${this.order.notes}"` : "[Not Set]";
        const newValue =
          fields.notes.length > 0 ? `"${fields.notes}"` : "[Not Set]";

        changeData.fieldChanges.push({
          field: "notes",
          label: "Notes",
          oldValue: oldValue,
          newValue: newValue,
          type: "text",
          impact: "communication",
        });
      }

      if (fields.hasOwnProperty("internalNotes")) {
        const oldValue =
          this.order.internalNotes.length > 0
            ? `"${this.order.internalNotes}"`
            : "[Not Set]";
        const newValue =
          fields.internalNotes.length > 0
            ? `"${fields.internalNotes}"`
            : "[Not Set]";

        changeData.fieldChanges.push({
          field: "internalNotes",
          label: "Internal Notes",
          oldValue: oldValue,
          newValue: newValue,
          type: "text",
          impact: "internal",
        });
      }

      if (fields.hasOwnProperty("deliveryPreferences")) {
        const oldValue =
          this.order.account.deliveryPreferences &&
          this.order.account.deliveryPreferences.length > 0
            ? `"${this.order.account.deliveryPreferences}"`
            : "[Not Set]";
        const newValue =
          fields.deliveryPreferences.length > 0
            ? `"${fields.deliveryPreferences}"`
            : "[Not Set]";

        changeData.fieldChanges.push({
          field: "deliveryPreferences",
          label: "Delivery Preferences",
          oldValue: oldValue,
          newValue: newValue,
          type: "text",
          impact: "logistics",
        });
      }

      if (fields.hasOwnProperty("discountType")) {
        const oldValue =
          (this.order.discount?.type ?? "%") === "%"
            ? `Percentage`
            : `Fixed Amount`;
        const newValue =
          fields.discountType === "%" ? `Percentage` : `Fixed Amount`;

        changeData.fieldChanges.push({
          field: "discountType",
          label: "Discount Type",
          oldValue: oldValue,
          newValue: newValue,
          type: "text",
          impact: "price",
        });
      }

      if (fields.hasOwnProperty("discountValue")) {
        const oldValue =
          (this.order.discount?.type ?? "%") === "%"
            ? `${this.order.discount?.value ?? 0}%`
            : `$${this.order.discount?.value ?? 0}`;
        const newValue =
          (fields.discountType ?? this.order?.discount?.type ?? "%") === "%"
            ? `${fields.discountValue ?? 0}%`
            : `$${fields.discountValue ?? 0}`;

        changeData.fieldChanges.push({
          field: "discountValue",
          label: "Discount Value",
          oldValue: oldValue,
          newValue: newValue,
          type: "currency",
          impact: "price",
        });
      }

      // Create activity for order details update
      await createActivity({
        type: "order",
        action: "Order details updated",
        item: `Order details updated: ${cleanDescription}`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "medium",
        priority: "medium",
        changeData: changeData,
      });

      updates = updates.join(" | ");
    }

    if (updatePayload.balance || fields.hasOwnProperty("discountValue") || fields.hasOwnProperty("discountType")) {
      addBreadcrumb("updateOrderData: balance-affecting change", {
        workflow: "sales-order",
        data: {
          orderId: this.orderID,
          changedFields: Object.keys(fields),
          currentBalance: this.order?.balance,
        },
      });
    }
    await batch.commit();
    if (updatePayload.balance || fields.hasOwnProperty("discountValue") || fields.hasOwnProperty("discountType")) {
      await this._checkBalanceAnomaly("update-order-data");
    }

    if (
      needsPriceUpdate &&
      !["shipped", "complete"].includes(this.order.status)
    ) {
      await this.updateVariablePricingOnOrderTermChange();
    }

    // If order is in "Shipped" or "Complete" status, user should be forced to revert status back to "Fulfilled"
    // so that the account balance stays accurate

    // External - Update Order

    if (this.orderIsExternal && needsExternalUpdate) {
      switch (this.externalOrderType) {
        case "leaflink":
          if (this.externalUpdates.leaflink.includes("orderUpdated")) {
            let shipDate = null;

            let discount = {
              type: fields.discountType ?? this.order.discount?.type ?? "%",
              value: fields.discountValue ?? this.order.discount?.value ?? 0,
            };

            if (fields.hasOwnProperty("shipDate") && updatePayload.shipDate) {
              let shipDateMoment = moment(updatePayload.shipDate);
              shipDate = moment(shipDateMoment.clone().format("MM/DD/YYYY"))
                .add(9, "hours")
                .tz("America/Los_Angeles");
            }
            let patchData = {
              ship_date: updatePayload.shipDate
                ? moment(shipDate).toISOString()
                : null,
              discount:
                discount.type === "%" ? discount.value / 100 : discount.value,
              discount_type: discount.type,
            };
            addBreadcrumb("leaflink: updateOrderData (order fields)", {
              workflow: "sales-order",
              data: { orderId: this.orderID, changedFields: Object.keys(fields) },
            });
            try {
              await this.leaflinkClient.updateOrderData(patchData);
            } catch (error) {
              logWorkflowWarning(`LeafLink: updateOrderData (order fields) failed — ${error.message}`, {
                workflow: "sales-order",
                action: "leaflink-update-order-fields",
                context: { orderId: this.orderID, changedFields: Object.keys(fields), balance: this.order?.balance },
              });
              throw error;
            }
          }
          break;
        default:
          break;
      }
    }

    if (fields.hasOwnProperty("fulfillmentLocation")) {
      try {
        await this.updateFulfillmentLocation(fields);
      } catch (error) {
        // Re-throw error to be handled by the calling component
        throw error;
      }
    }

    await this.checkOverpaymentCredits();
  }

  // Main
  async updateVariablePricingOnOrderTermChange() {
    addBreadcrumb("updateVariablePricingOnOrderTermChange: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, paymentTerm: this.order?.paymentTerm, balance: this.order?.balance },
    });
    const batch = writeBatch(firestore);
    await this.getOrder();
    await this.getLineItems();
    await this.getProductCategories();
    await this.getPriceOverrides();

    // Get the new payment term
    let term = this.order.paymentTerm;

    // If the order has sample line items, skip the update
    if (this.lineItems.some((item) => item.sample)) return;

    let overriddenCategoryIDs = this.priceOverrides.map(
      (override) => override.category.id,
    );

    this.lineItems.forEach((product) => {
      product.amount = {
        original: product.amount,
        new: product.amount,
      };
      // if (product.promo) return
      let category = this.categories.find(
        (category) => category.id === product.categoryID,
      );
      if (category && category.flags.includes("hasVariablePricing")) {
        product.hasVariablePricing = true;
        product.variablePrice = category.variablePrice;
      }
      if (overriddenCategoryIDs.includes(product.categoryID)) {
        //  Price Override
        let override;
        if (
          this.priceOverrides
            .filter(
              (item) =>
                item.category.id === product.categoryID &&
                item.account &&
                item.account.id === this.order.account.id,
            )
            .find(
              (option) =>
                option.accountSpecific &&
                option.account.id === this.order.account.id,
            )
        ) {
          // Account Specific Price Override
          override = this.priceOverrides
            .filter((item) => item.category.id === product.categoryID)
            .find(
              (option) =>
                option.accountSpecific &&
                option.account.id === this.order.account.id,
            );
        } else if (
          this.priceOverrides
            .filter(
              (item) =>
                item.category.id === product.categoryID &&
                item.product &&
                item.product.id === product.productID,
            )
            .find(
              (option) =>
                option.productSpecific &&
                option.product.id === product.productID,
            )
        ) {
          // SKU Specific Price Override
          override = this.priceOverrides
            .filter((item) => item.category.id === product.categoryID)
            .find(
              (option) =>
                option.productSpecific &&
                option.product.id === product.productID,
            );
        } else {
          // Payable To Price Override
          override = this.priceOverrides.find(
            (item) =>
              item.category.id === product.categoryID &&
              item.accountSpecific === false &&
              item.productSpecific === false,
          );
        }
        if (override && override.variablePrice) {
          let newPrice = override.variablePrice.find(
            (variablePrice) => variablePrice.term === term,
          );
          if (newPrice) {
            product.price.normal = newPrice.price;
            product.price.sale = product.promo ? 0.01 : newPrice.price;
            product.amount.new = product.promo ? 0.01 : newPrice.price;
          }
        } else if (override) {
          product.price.normal = override.price;
          product.price.sale = product.promo ? 0.01 : override.price;
          product.amount.new = product.promo ? 0.01 : override.price;
        } else if (product.hasVariablePricing) {
          // Needed for products within categories where 1 or more products in the category
          // has a price override but this specific product doesn't have an override
          let newPrice = product.variablePrice.find(
            (variablePrice) => variablePrice.term === term,
          );
          if (newPrice) {
            product.price.normal = newPrice.price;
            product.price.sale = product.promo ? 0.01 : newPrice.price;
            product.amount.new = product.promo ? 0.01 : newPrice.price;
          }
        }
      } else if (product.hasVariablePricing) {
        let newPrice = product.variablePrice.find(
          (variablePrice) => variablePrice.term === term,
        );
        if (newPrice) {
          product.price.normal = newPrice.price;
          product.price.sale = product.promo ? 0.01 : newPrice.price;
          product.amount.new = product.promo ? 0.01 : newPrice.price;
        }
      }
    });

    let updates = [];

    let transitionToAccepted = false;

    const orderDoc = doc(firestore, "salesOrders", this.order.id);

    let lineItems = this.lineItems.map((item) => {
      let itemUpdates = [];

      // Get updated price with discount (if applicable)

      if (item.flags.discountApplied && item.discount?.value) {
        let newAmount = item.amount.new;
        item.amount.new = Number(
          parseFloat(newAmount - item.discount.value).toFixed(2),
        );
        item.price.sale = Number(
          parseFloat(newAmount - item.discount.value).toFixed(2),
        );
      }

      if (item.amount.new !== item.amount.original) {
        //  Price updated
        const currencyFormatter = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        });
        itemUpdates.push(
          `Price - <span class="text-uppercase">old</span>: <span>${currencyFormatter.format(
            item.amount.original,
          )}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${currencyFormatter.format(
            item.amount.new,
          )}</span>`,
        );
      }

      if (itemUpdates.length > 0) {
        //  Line item was modified

        itemUpdates = itemUpdates.join(" / ");
        let promoUnit = item.promo
          ? `<span class='font-weight-bold text-no-wrap'>
            <span class='font-weight-bold text-caption pa-1 rounded' style='background-color:
              #0277BD; color: #FFFFFF}
            '>Promo</span>
          </span>`
          : "";
        let itemText = `<span class="text-capitalize font-weight-bold">${item.name}</span> <span class="text-uppercase">(${item.sku})${promoUnit}</span>: ${itemUpdates}`;
        updates.push(itemText);
      }

      delete item.hasVariablePricing;
      delete item.variablePrice;

      return {
        ...item,
        externalIDs: item.externalIDs,
        id: item.id,
        price: item.price,
        amount: item.amount.new,
        qty: Number(item.qty || 0),
      };
    });

    console.log(lineItems);

    const promises = lineItems.map(async (item) => {
      const lineItemDoc = doc(firestore, "salesOrders_lineItems", item.id);

      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (this.externalUpdates.leaflink.includes("orderUpdated")) {
              let externalItem = null;
              if (item.externalIDs?.leaflinkLineItemID) {
                externalItem = {
                  id: item.externalIDs.leaflinkLineItemID,
                };
              } else {
                if (this.leaflinkClient.lineItems.length === 0) {
                  await this.leaflinkClient.getLineItems();
                }
                externalItem = this.leaflinkClient.lineItems.find(
                  (external) =>
                    external.productID === item.externalIDs.leaflink,
                );
              }
              let itemData = {
                ordered_unit_price: item.price.normal,
                sale_price: item.amount,
              };
              if (externalItem) {
                addBreadcrumb("leaflink: updateLineItem (variable pricing)", {
                  workflow: "sales-order",
                  data: { orderId: this.orderID, itemId: item.id, externalItemId: externalItem.id },
                });
                try {
                  await this.leaflinkClient.updateLineItem(externalItem, itemData);
                  item.externalIDs.leaflinkLineItemID = externalItem.id;
                } catch (error) {
                  logWorkflowWarning(`LeafLink: updateLineItem (variable pricing) failed — ${error.message}`, {
                    workflow: "sales-order",
                    action: "leaflink-update-line-item-variable-pricing",
                    context: { orderId: this.orderID, itemId: item.id, balance: this.order?.balance },
                  });
                  throw error;
                }
              }
            }
            break;
          default:
            break;
        }
      }

      // Ensure amount.new is a valid number, fallback to amount.original if needed
      const itemAmount =
        Number(item.amount.new) || Number(item.amount.original) || 0;

      // Ensure taxPercentage is a valid number
      const taxPercentage = Number(this.order.taxPercentage) || 0;

      // Calculate tax with safe division
      const taxRate = 1 + taxPercentage / 100;
      const taxAmount = taxRate > 0 ? itemAmount - itemAmount / taxRate : 0;

      item.tax = Number(parseFloat(taxAmount).toFixed(2));

      batch.set(
        lineItemDoc,
        {
          ...item,
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
        },
        { merge: true },
      );
    });
    //  Need to use Promise to ensure that all items are removed before setup() runs
    await Promise.all(promises);

    //  Sum total price of line items
    let priceTotal = lineItems.reduce((a, b) => a + b.qty * b.amount, 0);

    let totalDiscountValue = () => {
      let isPercentage = this.order.discount?.type === "%";
      let discountValue = this.order.discount?.value ?? 0;
      let totalValue = priceTotal;
      if (isPercentage) {
        return (discountValue / 100) * totalValue;
      } else {
        return discountValue;
      }
    };

    let paymentsApplied =
      Number(this.order.balance.total) - Number(this.order.balance.due);

    let newTotal = parseFloat(
      (
        priceTotal -
        totalDiscountValue() +
        Number(this.order.balance.shipping)
      ).toFixed(2),
    );
    let newBalance = parseFloat((newTotal - paymentsApplied).toFixed(2));

    // Update order
    batch.set(
      orderDoc,
      {
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        lineItems,
        balance: {
          due: newBalance,
          total: newTotal,
        },
        discount: {
          value: this.order.discount?.value ?? 0,
          type: this.order.discount?.type ?? "%",
          total: totalDiscountValue(),
        },
      },
      { merge: true },
    );

    if (updates.length > 0) {
      // Create detailed description for activity with price changes and balance impact
      const updatedItems = this.lineItems.filter(
        (item) => item.amount.new !== item.amount.original,
      );
      const updatedCount = updatedItems.length;

      // Calculate total balance change
      const totalBalanceChange = updatedItems.reduce((sum, item) => {
        const caseQTY = item.caseQTY ?? item.unitsPerCase;
        const totalQty = (item.cases || 0) * caseQTY + (item.singles || 0);
        const priceDifference = item.amount.new - item.amount.original;
        return sum + priceDifference * totalQty;
      }, 0);

      // Format currency
      const currencyFormatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      });

      // Create detailed item descriptions with price changes
      const detailedDescription = updatedItems
        .map((item) => {
          const caseQTY = item.caseQTY ?? item.unitsPerCase;
          const totalQty = (item.cases || 0) * caseQTY + (item.singles || 0);

          return `${item.name} (${
            item.sku
          }) - Qty: ${totalQty} - Price: ${currencyFormatter.format(
            item.amount.original,
          )} → ${currencyFormatter.format(item.amount.new)}`;
        })
        .join(", ");

      // Calculate old and new order totals
      const oldOrderTotal = Number(this.order.balance.total);
      const newOrderTotal =
        Number(this.order.balance.total) + totalBalanceChange;

      // Create activity for line items update during pricing change
      await createActivity({
        type: "inventory",
        action: "Line items pricing updated",
        item: `Updated pricing for ${updatedCount} item${
          updatedCount !== 1 ? "s" : ""
        }: ${detailedDescription} - Order balance: ${currencyFormatter.format(
          oldOrderTotal,
        )} → ${currencyFormatter.format(newOrderTotal)} (${
          totalBalanceChange >= 0 ? "+" : ""
        }${currencyFormatter.format(totalBalanceChange)})`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "medium",
        changeData: {
          field: "lineItemsPricing",
          reason: "Variable pricing update on payment term change",
          action: "updated",
          items: this.lineItems
            .filter((item) => item.amount.new !== item.amount.original)
            .map((item) => ({
              name: item.name,
              sku: item.sku,
              changes: {
                amount: {
                  old: item.amount.original,
                  new: item.amount.new,
                },
              },
            })),
          orderNumber: this.order.orderNumber,
          monetaryValue: this.lineItems
            .filter((item) => item.amount.new !== item.amount.original)
            .reduce(
              (sum, item) => sum + (item.amount.new - item.amount.original),
              0,
            ),
        },
      });

      updates = updates.join(" | ");
      console.log("updating line items");
    }

    addBreadcrumb("updateVariablePricingOnOrderTermChange: committing batch", {
      workflow: "sales-order",
      data: { orderId: this.orderID, lineItemCount: this.lineItems.length },
    });
    await batch.commit();
    await this.checkOverpaymentCredits();
  }

  // Fulfillment Location Update
  async updateFulfillmentLocation(fields) {
    await this.getOrder();
    await this.getLineItems();

    const batch = writeBatch(firestore);
    const salesOrderDoc = doc(firestore, "salesOrders", this.order.id);
    try {
      // Check if new location has enough inventory
      let newFulfillmentLocation = this.fulfillmentLocations.find(
        (item) => item.locationID === fields.fulfillmentLocation,
      );
      if (!newFulfillmentLocation) {
        throw new Error("New Fulfillment Location Not Found");
      }

      let originalFulfillmentLocation = null;
      let originalLocationManufacturingRef = null;

      // Only find original location if one is already set
      if (this.order.fulfillmentLocation) {
        originalFulfillmentLocation = this.fulfillmentLocations.find(
          (item) => item.locationID === this.order.fulfillmentLocation.id,
        );

        if (originalFulfillmentLocation) {
          originalLocationManufacturingRef = doc(
            firestore,
            "locationManufacturing",
            `${originalFulfillmentLocation.locationID}_${this.order.brand.id}`,
          );
        }
      }
      const newLocationManufacturingRef = doc(
        firestore,
        "locationManufacturing",
        `${newFulfillmentLocation.locationID}_${this.order.brand.id}`,
      );
      const response = await getDoc(newLocationManufacturingRef);

      if (
        response.exists() &&
        response.data().hasOwnProperty("manufacturing")
      ) {
        let manufacturing = response.data().manufacturing;

        // Read original location so we can merge and write once per location
        let originalManufacturing = {};
        if (originalLocationManufacturingRef) {
          const origSnap = await getDoc(originalLocationManufacturingRef);
          originalManufacturing =
            origSnap.exists() && origSnap.data().manufacturing
              ? JSON.parse(JSON.stringify(origSnap.data().manufacturing))
              : {};
        }

        // Build merged manufacturing: one object per location with all line-item deltas applied
        const newMfg = JSON.parse(JSON.stringify(manufacturing));
        const originalMfg = JSON.parse(JSON.stringify(originalManufacturing));

        for (const lineItem of this.lineItems) {
          let categoryID = lineItem.categoryID;
          let productID = lineItem.productID;

          const isCaseProduct =
            lineItem.unitsPerCase > 0 || lineItem.caseQTY > 0;
          let itemQTY;
          if (isCaseProduct) {
            itemQTY =
              lineItem.singles +
              lineItem.cases * (lineItem.unitsPerCase ?? lineItem.caseQTY ?? 0);
          } else {
            itemQTY = lineItem.singles || 0;
          }

          const availableAtNew =
            newMfg[categoryID]?.available?.[productID] ?? 0;
          const reservedAtNew = newMfg[categoryID]?.reserved?.[productID] ?? 0;
          const transferAtNew = newMfg[categoryID]?.transfer?.[productID] ?? 0;
          const availableAmount =
            availableAtNew - reservedAtNew - transferAtNew;
          if (itemQTY > availableAmount) {
            throw new Error("Inventory Unavailable");
          }

          if (!newMfg[categoryID]) newMfg[categoryID] = {};
          if (!newMfg[categoryID].reserved) newMfg[categoryID].reserved = {};
          newMfg[categoryID].reserved[productID] =
            (newMfg[categoryID].reserved[productID] || 0) + itemQTY;

          if (originalLocationManufacturingRef) {
            if (!originalMfg[categoryID]) originalMfg[categoryID] = {};
            if (!originalMfg[categoryID].reserved) {
              originalMfg[categoryID].reserved = {};
            }
            originalMfg[categoryID].reserved[productID] =
              (originalMfg[categoryID].reserved[productID] || 0) - itemQTY;
          }
        }

        batch.set(
          newLocationManufacturingRef,
          { manufacturing: newMfg },
          { merge: true },
        );
        if (originalLocationManufacturingRef) {
          batch.set(
            originalLocationManufacturingRef,
            {
              manufacturing: originalMfg,
            },
            { merge: true },
          );
        }

        batch.set(
          salesOrderDoc,
          {
            updated: serverTimestamp(),
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            fulfillmentLocation: {
              id: newFulfillmentLocation.locationID,
              name: newFulfillmentLocation.name,
            },
          },
          { merge: true },
        );

        addBreadcrumb("updateFulfillmentLocation: committing inventory transfer to new location", {
          workflow: "sales-order",
          data: {
            orderId: this.orderID,
            newLocationId: newFulfillmentLocation.locationID,
            lineItemCount: this.lineItems.length,
          },
        });
        await batch.commit();

        // Inventory activities: one per line at old location (unreserve) and one per line at new (reserve)
        // Use pre-commit state for "before"; "after" = before + delta (already applied in merged write)
        for (const lineItem of this.lineItems) {
          const isCaseProduct =
            lineItem.unitsPerCase > 0 || lineItem.caseQTY > 0;
          const itemQTY = isCaseProduct
            ? lineItem.singles +
              lineItem.cases * (lineItem.unitsPerCase ?? lineItem.caseQTY ?? 0)
            : lineItem.singles || 0;

          const categoryID = lineItem.categoryID;
          const productID = lineItem.productID;
          const itemData = {
            categoryID,
            productID,
            name: lineItem.name,
            sku: lineItem.sku,
            categoryName: lineItem.categoryName,
          };

          if (originalLocationManufacturingRef && originalFulfillmentLocation) {
            const beforeAvailable =
              originalManufacturing[categoryID]?.available?.[productID] ?? 0;
            const beforeReserved =
              originalManufacturing[categoryID]?.reserved?.[productID] ?? 0;
            const beforeTransfer =
              originalManufacturing[categoryID]?.transfer?.[productID] ?? 0;
            const afterReserved = Math.max(0, beforeReserved - itemQTY);
            const skuIDOld = `${originalFulfillmentLocation.locationID}_${this.order.brand.id}_${categoryID}_${productID}`;
            await createInventoryActivity({
              action: "Location changed – unreserve",
              item: `${itemQTY} units of ${lineItem.name} (${lineItem.sku}) unreserved at ${originalFulfillmentLocation.name} (fulfillment location changed)`,
              skuID: skuIDOld,
              itemData,
              movement: {
                type: "release",
                from: "reserved",
                to: "available",
                quantity: itemQTY,
                reason: "fulfillmentLocationUnreserve",
                reasonID: this.order.id,
              },
              snapshot: {
                before: {
                  available: beforeAvailable,
                  reserved: beforeReserved,
                  transfer: beforeTransfer,
                },
                after: {
                  available: beforeAvailable,
                  reserved: afterReserved,
                  transfer: beforeTransfer,
                },
              },
              location: {
                id: originalFulfillmentLocation.locationID,
                name: originalFulfillmentLocation.name,
              },
              order: this.order,
              user: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              extraSalesOrderData: { orderID: this.order.id },
            });
          }

          const beforeAvailableNew =
            manufacturing[categoryID]?.available?.[productID] ?? 0;
          const beforeReservedNew =
            manufacturing[categoryID]?.reserved?.[productID] ?? 0;
          const beforeTransferNew =
            manufacturing[categoryID]?.transfer?.[productID] ?? 0;
          const afterReservedNew = beforeReservedNew + itemQTY;
          const skuIDNew = `${newFulfillmentLocation.locationID}_${this.order.brand.id}_${categoryID}_${productID}`;
          await createInventoryActivity({
            action: "Location changed – reserve",
            item: `${itemQTY} units of ${lineItem.name} (${lineItem.sku}) reserved at ${newFulfillmentLocation.name} (fulfillment location changed)`,
            skuID: skuIDNew,
            itemData,
            movement: {
              type: "reservation",
              from: "available",
              to: "reserved",
              quantity: itemQTY,
              reason: "fulfillmentLocationReserve",
              reasonID: this.order.id,
            },
            snapshot: {
              before: {
                available: beforeAvailableNew,
                reserved: beforeReservedNew,
                transfer: beforeTransferNew,
              },
              after: {
                available: beforeAvailableNew,
                reserved: afterReservedNew,
                transfer: beforeTransferNew,
              },
            },
            location: {
              id: newFulfillmentLocation.locationID,
              name: newFulfillmentLocation.name,
            },
            order: this.order,
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            extraSalesOrderData: { orderID: this.order.id },
          });
        }

        // Create activity for fulfillment location change
        await createActivity({
          type: "logistics",
          action: "Fulfillment location updated",
          item: `Location changed from ${
            originalFulfillmentLocation?.name ?? "—"
          } to ${newFulfillmentLocation.name}`,
          relatedTo: "salesOrders",
          relatedID: [this.order.id],
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          account: {
            id: this.order.account.id,
            name: this.order.account.name,
          },
          brand: {
            id: this.order.brand.id,
            name: this.order.brand.name,
          },
          businessImpact: "medium",
          priority: "medium",
          changeData: {
            field: "fulfillmentLocation",
            oldValue: {
              id: originalFulfillmentLocation.locationID,
              name: originalFulfillmentLocation.name,
            },
            newValue: {
              id: newFulfillmentLocation.locationID,
              name: newFulfillmentLocation.name,
            },
            inventoryTransfer: {
              itemsTransferred: this.lineItems.length,
              totalQty: this.lineItems.reduce(
                (sum, item) =>
                  sum + (item.singles + item.cases * (item.unitsPerCase ?? 0)),
                0,
              ),
            },
            orderNumber: this.order.orderNumber,
          },
        });

        addBreadcrumb("updateFulfillmentLocation: committing activity and order update batch", {
          workflow: "sales-order",
          data: { orderId: this.orderID },
        });
        await batch.commit();
      } else {
        throw new Error("Inventory Unavailable");
      }
    } catch (error) {
      throw new Error(error.message);
    }
  }

  async generateInvoice(prefetchedData = null) {
    let ordersForPreviousAccountBalance = [];
    let payableTo = {};
    let brandMetrics = {};
    let brand = {};
    let account = {};

    if (prefetchedData) {
      // Public / unauthenticated path — use pre-fetched data from cloud function
      this.order = prefetchedData.order;
      this.lineItems = prefetchedData.lineItems;
      this.payments = prefetchedData.payments;
      payableTo = prefetchedData.payableTo ?? {};
      brand = prefetchedData.brand ?? {};
      brandMetrics = prefetchedData.brandMetrics ?? {};
      account = prefetchedData.account ?? {};

      // Build ordersForPreviousAccountBalance from raw cloud function data
      const rawOrders = prefetchedData.ordersForPreviousAccountBalance ?? [];
      ordersForPreviousAccountBalance = rawOrders
        .map((salesOrder) => {
          const statusDef = store.getters.getOrderStatus.find(
            (s) => s.value === salesOrder.status,
          );
          const term = store.getters.getPaymentTerms.find(
            (t) => t.value === salesOrder.paymentTerm,
          );
          const shipSeconds = salesOrder.shipDate?.seconds ?? salesOrder.shipDate?._seconds;
          const createdSeconds = salesOrder.created?.seconds ?? salesOrder.created?._seconds;
          const updatedSeconds = salesOrder.updated?.seconds ?? salesOrder.updated?._seconds;
          var paymentDueDate = moment(shipSeconds * 1000)
            .add(term?.due ?? 0, "days")
            .toDate();

          return {
            id: salesOrder.id,
            created: moment(createdSeconds * 1000).toDate(),
            updated: moment(updatedSeconds * 1000).toDate(),
            internal: salesOrder.orderNumber?.internal,
            external: salesOrder.orderNumber?.external,
            createdBy: `${salesOrder.createdBy?.name?.first ?? ""} ${salesOrder.createdBy?.name?.last ?? ""}`,
            balance: Number(salesOrder.balance?.due),
            total: Number(salesOrder.balance?.total),
            status: statusDef,
            shipDate: moment(shipSeconds * 1000).toDate(),
            units: salesOrder.units,
            account: salesOrder.account?.name,
            paymentTerm: salesOrder.paymentTerm,
            paymentDueDate: paymentDueDate,
          };
        })
        .sort((a, b) => (a.created > b.created ? 1 : -1))
        .filter((order) => {
          const term = store.getters.getPaymentTerms.find(
            (t) => t.value === order.paymentTerm,
          );
          return moment(order.shipDate).add(term?.due ?? 0, "days").toDate() <= moment().toDate();
        });
    } else {
      // Authenticated path — fetch everything from Firestore
      await this.getOrder();
      await this.getLineItems();
      await this.getPayments();

      const ordersDoc = collection(firestore, "salesOrders");
      const relatedAccount = where("account.id", "==", this.order.account.id),
        relatedBrand = where("brand.id", "==", this.order.brand.id),
        status = where("status", "in", ["shipped", "complete"]);
      const orderQuery = query(ordersDoc, relatedAccount, relatedBrand, status);

      const orderResponse = await getDocs(orderQuery);

      if (!orderResponse.empty) {
        ordersForPreviousAccountBalance = orderResponse.docs
          .filter(
            (order) =>
              Number(order.data().balance.due) > 0 && order.id !== this.order.id,
          )
          .map((salesOrder) => {
            const status = store.getters.getOrderStatus.find(
              (status) => status.value === salesOrder.data().status,
            );

            const term = store.getters.getPaymentTerms.find(
              (paymentTerm) =>
                paymentTerm.value === salesOrder.data().paymentTerm,
            );

            var paymentDueDate = moment(
              salesOrder.data().shipDate?.seconds * 1000,
            )
              .add(term.due, "days")
              .toDate();

            return {
              id: salesOrder.id,
              created: moment(salesOrder.data().created?.seconds * 1000).toDate(),
              updated: moment(salesOrder.data().updated?.seconds * 1000).toDate(),
              internal: salesOrder.data().orderNumber.internal,
              external: salesOrder.data().orderNumber.external,
              createdBy: `${salesOrder.data().createdBy.name.first} ${
                salesOrder.data().createdBy.name.last
              }`,
              balance: Number(salesOrder.data().balance.due),
              total: Number(salesOrder.data().balance.total),
              status,
              shipDate: moment(
                salesOrder.data().shipDate?.seconds * 1000,
              ).toDate(),
              units: salesOrder.data().units,
              account: salesOrder.data().account.name,
              paymentTerm: salesOrder.data().paymentTerm,
              paymentDueDate: paymentDueDate,
            };
          })
          .sort((a, b) => (a.created > b.created ? 1 : -1))
          .filter((order) => {
            const term = store.getters.getPaymentTerms.find(
              (paymentTerm) => paymentTerm.value === order.paymentTerm,
            );
            var shouldCollect =
              moment(order.shipDate).add(term.due, "days").toDate() <=
              moment().toDate();
            return shouldCollect;
          });
      }

      // Payable To
      const payableToRef = collection(firestore, "payableTo");
      const state = where(
          "location.state.stateID",
          "==",
          this.order.location.shipping.state.stateID,
        ),
        brandWhere = where("brandID", "==", this.order.brand.id);
      const q = query(payableToRef, state, brandWhere);
      const response = await getDocs(q, limit(1));

      payableTo = {
        id: response.docs[0].id,
        ...response.docs[0].data(),
      };

      // Brand
      const brandDoc = doc(firestore, "companyBrands", this.order.brand.id);
      const brandResponse = await getDoc(brandDoc);

      // Brand Metrics
      const brandMetricsDoc = doc(
        firestore,
        "brandStats",
        `${this.order.account.id}_${this.order.brand.id}`,
      );
      const brandMetricsResponse = await getDoc(brandMetricsDoc);

      brandMetrics = brandMetricsResponse.data();

      // Account
      const accountDoc = doc(firestore, "accounts", this.order.account.id);
      const accountResponse = await getDoc(accountDoc);

      if (brandResponse.exists()) {
        brand = brandResponse.data();
      }

      if (accountResponse.exists()) {
        account = accountResponse.data();
      }
    }

    let previousAccountBalance = ordersForPreviousAccountBalance.reduce(
      (acc, order) => acc + Number(order.balance),
      0,
    );

    let currencyFormat = (value) => {
      return Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(value);
    };

    const externalOrderNumber = this.order.flags?.external
      ? this.order.orderNumber.external
      : "-";
    const internalOrderNumber = String(
        this.order.orderNumber.internal,
      ).toUpperCase(),
      internalManifestNumber = String(
        this.order.orderNumber.manifest?.length > 0
          ? this.order.orderNumber.manifest
          : "-",
      ).toUpperCase();
    const invoice = new jsPDF({ format: "letter" });
    let fileName = `${this.order.account.name.replace(/(\b[a-z](?!\s))/g, (l) =>
      l.toUpperCase(),
    )} - ${internalOrderNumber}`;

    const pageWidth = invoice.internal.pageSize.getWidth();

    // Header

    let logoLoaded = false;
    if (brand.flags?.hasLogo) {
      try {
        let img;
        if (prefetchedData?.brandLogoDataUri) {
          img = prefetchedData.brandLogoDataUri;
        } else {
          let logoRef = ref(storage, `brand/${this.order.brand.id}/logo`);
          const bytes = await getBytes(logoRef);
          const metadata = await getMetadata(logoRef);
          img =
            "data:" +
            metadata.contentType +
            ";base64," +
            new Buffer.from(bytes).toString("base64");
        }

        let imgProps = invoice.getImageProperties(img);
        let imgHeight = imgProps.height;
        let imgWidth = imgProps.width;

        let width = 35;
        let height = (imgHeight * width) / imgWidth;

        let y = 0;

        if (height < 15) {
          y = 12;
        }

        invoice.addImage(img, 94, y, width, height, "logo", "FAST");
        logoLoaded = true;
      } catch (err) {
        console.error("generateInvoice: logo fetch failed, using text fallback", err);
        logWorkflowWarning(`generateInvoice: logo fetch failed, using text fallback — ${err.message}`, {
          workflow: "sales-order",
          action: "generate-invoice-logo-fetch",
          context: { orderId: this.orderID, brandId: this.order?.brand?.id },
        });
      }
    }
    if (!logoLoaded) {
      invoice.setFontSize(24);
      invoice.setFont("helvetica", "bold");
      let brandName = (brand.name ?? "").replace(/\b\w/g, (l) => l.toUpperCase());
      invoice.text(brandName, 110, 15, "center");
    }

    // --- Setup Dynamic Y Tracker for Left Column ---
    let leftColY = 35; // Starting point for the left column headers

    // 1. Customer / Store Information
    invoice.setFontSize(10);
    invoice.setFont("helvetica", "bold");
    invoice.text("Store Information:", 10, leftColY);

    leftColY += 5; // Move down 5mm for the details

    invoice.setFont("helvetica", "normal");
    const accountName = account.name.replace(/(\b[a-z](?!\s))/g, (l) =>
      l.toUpperCase(),
    );
    const licenseName =
      account.dba?.replace(/(\b[a-z](?!\s))/g, (l) => l.toUpperCase()) ?? "-";

    let phone = "";
    var cleaned = ("" + account.phoneNumber).replace(/\D/g, "");
    var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      phone = "(" + match[1] + ") " + match[2] + "-" + match[3];
    }

    const phoneNumber = "\n" + phone;
    const license = account.license?.number
      ? "\n" + account.license.number.toUpperCase()
      : "";

    var wrappedStoreInformation = invoice.splitTextToSize(
      accountName + `\n${licenseName}` + phoneNumber + license,
      53,
    );
    invoice.text(wrappedStoreInformation, 10, leftColY);

    // Calculate the height of the printed text (approx 4.5mm per line) + add 4mm of padding
    leftColY += wrappedStoreInformation.length * 4.5 + 4;

    // 2. Customer - Shipping Address
    const accountShippingAddress = `${
      this.order.location.shipping.street.number
    } ${this.order.location.shipping.street.name.replace(/\b\w/g, (l) =>
      l.toUpperCase(),
    )}`;
    const accountShippingCityZipCode = `${this.order.location.shipping.city.replace(
      /\b\w/g,
      (l) => l.toUpperCase(),
    )}, ${this.order.location.shipping.state.abbreviation.toUpperCase()} ${
      this.order.location.shipping.zipCode
    }`;

    invoice.setFont("helvetica", "bold");
    invoice.text("Delivery Address:", 10, leftColY);

    leftColY += 5;

    invoice.setFont("helvetica", "normal");
    var wrappedShippingAddress = invoice.splitTextToSize(
      accountShippingAddress + `\n${accountShippingCityZipCode}`,
      53,
    );
    invoice.text(wrappedShippingAddress, 10, leftColY);

    leftColY += wrappedShippingAddress.length * 4.5 + 4;

    // 3. Customer - Billing Address
    const accountBillingAddress = `${
      this.order.location.billing.street.number
    } ${this.order.location.billing.street.name.replace(/\b\w/g, (l) =>
      l.toUpperCase(),
    )}`;
    const accountBillingCityZipCode = `${this.order.location.billing.city.replace(
      /\b\w/g,
      (l) => l.toUpperCase(),
    )}, ${this.order.location.billing.state.abbreviation.toUpperCase()} ${
      this.order.location.billing.zipCode
    }`;

    invoice.setFont("helvetica", "bold");
    invoice.text("Billing Address:", 10, leftColY);

    leftColY += 5;

    invoice.setFont("helvetica", "normal");
    if (account.flags.sameBilling) {
      invoice.text("Same as Delivery Address", 10, leftColY);
      leftColY += 4.5 + 4; // 1 line height + padding
    } else {
      var wrappedBillingAddress = invoice.splitTextToSize(
        accountBillingAddress + `\n${accountBillingCityZipCode}`,
        53,
      );
      invoice.text(wrappedBillingAddress, 10, leftColY);
      leftColY += wrappedBillingAddress.length * 4.5 + 4;
    }

    // 4. Buyer Information
    const buyerInfoY = leftColY;
    if (this.order.buyerInformation) {
      invoice.setFont("helvetica", "bold");
      invoice.text("Buyer Information:", 10, leftColY);

      leftColY += 5;

      invoice.setFont("helvetica", "normal");
      let buyerName = this.order.buyerInformation.name.replace("PARENT - ", "");
      buyerName = buyerName.replace(/\b\w/g, (l) => l.toUpperCase());

      invoice.text(buyerName, 10, leftColY);
      invoice.text(this.order.buyerInformation.email, 10, leftColY + 5);
    }

    // Payable To - Banking Information
    invoice.setFontSize(10);
    invoice.setFont("helvetica", "bold");
    invoice.text("Payable To:", 70, 35);
    let midColY = 40;
    if (payableTo.bankingInformation) {
      invoice.setFontSize(10);
      invoice.setFont("helvetica", "normal");

      // Check if new details format exists
      if (payableTo.bankingInformation.bank?.details) {
        const bankDetails = payableTo.bankingInformation.bank.details;
        const availableHeight = buyerInfoY - midColY - 4;

        // Use font size 10, shrink only if text is too long
        let bankFontSize = 10;
        let wrappedDetails;
        for (let fs = 10; fs >= 6; fs--) {
          invoice.setFontSize(fs);
          wrappedDetails = invoice.splitTextToSize(bankDetails, 68);
          if (wrappedDetails.length * (fs * 0.45) <= availableHeight) {
            bankFontSize = fs;
            break;
          }
          bankFontSize = fs;
        }

        // Expand line height to fill available space down to billing address end
        const naturalHeight = wrappedDetails.length * (bankFontSize * 0.45);
        const lineHeightFactor =
          naturalHeight < availableHeight
            ? availableHeight / wrappedDetails.length / (bankFontSize * 0.3528)
            : 1.15;

        invoice.setFontSize(bankFontSize);
        invoice.setLineHeightFactor(lineHeightFactor);
        invoice.text(wrappedDetails, 70, midColY);
        invoice.setLineHeightFactor(1.15);
        midColY += availableHeight + 4;
      } else {
        invoice.text(
          `Bank:  ${payableTo.bankingInformation.bank.name}`,
          70,
          midColY,
        );
        midColY += 4.5;
        invoice.text(
          `Account Number:  ${payableTo.bankingInformation.account.number}`,
          70,
          midColY,
        );
        midColY += 4.5;
        invoice.text(
          `Routing Number:  ${payableTo.bankingInformation.account.routingNumber}`,
          70,
          midColY,
        );
        midColY += 4.5 + 2;

        let street = `${
          payableTo.bankingInformation.bank.location.street.number
        } ${payableTo.bankingInformation.bank.location.street.name}${
          payableTo.bankingInformation.bank.location.street.unitNumber?.length >
          0
            ? ` #${payableTo.bankingInformation.bank.location.street.unitNumber}`
            : ""
        }`.replace(/\b\w/g, (l) => l.toUpperCase());

        let cityStateZip = `${
          payableTo.bankingInformation.bank.location.city
        }, ${payableTo.bankingInformation.bank.location.state.abbreviation.toUpperCase()} ${
          payableTo.bankingInformation.bank.location.zipCode
        }`.replace(/\b\w/g, (l) => l.toUpperCase());

        let wrappedBankAddress = invoice.splitTextToSize(
          `${street},\n${cityStateZip}`,
          68,
        );
        invoice.text(wrappedBankAddress, 70, midColY);
        midColY += wrappedBankAddress.length * 4.5 + 4;
      }
    }

    // Fulfilled By - always aligned with Buyer Information
    midColY = buyerInfoY;
    invoice.setFontSize(10);
    invoice.setFont("helvetica", "bold");
    invoice.text("Fulfilled By:", 70, midColY);
    midColY += 4;

    invoice.setFontSize(10);
    invoice.setFont("helvetica", "normal");

    const fulfilledByName = payableTo.name.replace(/\b\w/g, (l) =>
      l.toUpperCase(),
    );

    let fulfilledByPhone = "";
    const fulfilledByPhoneCleaned = ("" + payableTo.phoneNumber).replace(
      /\D/g,
      "",
    );
    const fulfilledByPhoneMatch = fulfilledByPhoneCleaned.match(
      /^(\d{3})(\d{3})(\d{4})$/,
    );
    if (fulfilledByPhoneMatch) {
      fulfilledByPhone = `(${fulfilledByPhoneMatch[1]}) ${fulfilledByPhoneMatch[2]}-${fulfilledByPhoneMatch[3]}`;
    }

    const fulfilledByLicense = payableTo.license.number.toUpperCase();

    const fulfilledByStreet =
      !payableTo.location.street?.name || !payableTo.location.street?.number
        ? "[No Address Set]"
        : `${
            payableTo.location.street.number
          } ${payableTo.location.street.name.replace(/\b\w/g, (l) =>
            l.toUpperCase(),
          )}${
            payableTo.location.unitNumber?.length
              ? ` #${payableTo.location.unitNumber}`
              : ""
          }`;

    const fulfilledByAddress = `${fulfilledByStreet}\n${payableTo.location.city.replace(
      /\b\w/g,
      (l) => l.toUpperCase(),
    )}, ${payableTo.location.state.abbreviation.toUpperCase()} ${
      payableTo.location.zipCode
    }`;

    const wrappedFulfilledByInfo = invoice.splitTextToSize(
      fulfilledByName +
        (fulfilledByPhone ? "\n" + fulfilledByPhone : "") +
        "\n" +
        fulfilledByLicense,
      68,
    );
    invoice.text(wrappedFulfilledByInfo, 70, midColY);
    midColY += wrappedFulfilledByInfo.length * 4.5 + 2;

    const wrappedFulfilledByAddress = invoice.splitTextToSize(
      fulfilledByAddress,
      68,
    );
    invoice.text(wrappedFulfilledByAddress, 70, midColY);
    midColY += wrappedFulfilledByAddress.length * 4.5;
    // Right column - Brand / Account
    invoice.setFontSize(10);
    invoice.setFont("helvetica", "bold");
    invoice.text("Brand:", 145, 35);
    invoice.setFont("helvetica", "normal");
    let rightColY = 40;

    let orderPaymentTerm =
      store.getters.getPaymentTerms.find(
        (item) => item.value === this.order.paymentTerm,
      )?.text ?? "[Error]";

    invoice.text("Payment Term", 145, rightColY);
    invoice.text(orderPaymentTerm, 205, rightColY, { align: "right" });
    rightColY += 5;

    invoice.text("External Invoice", 145, rightColY);
    invoice.text(externalOrderNumber, 205, rightColY, { align: "right" });
    rightColY += 5;

    invoice.text("Ship Date", 145, rightColY);
    if (this.order.shipDate) {
      invoice.text(
        moment(this.order.shipDate?.seconds * 1000).format("MMM DD, YYYY"),
        205,
        rightColY,
        { align: "right" },
      );
    } else {
      invoice.text("-", 205, rightColY, { align: "right" });
    }
    rightColY += 5;

    invoice.text("Created", 145, rightColY);
    invoice.text(
      moment(this.order.created?.seconds * 1000).format("MMM DD, YYYY"),
      205,
      rightColY,
      { align: "right" },
    );
    rightColY += 5;

    invoice.text("Created By", 145, rightColY);
    const createdByName =
      this.order.createdBy.name.first +
      " " +
      this.order.createdBy.name.last.charAt(0) +
      ".";
    invoice.text(
      createdByName.replace(/\b\w/g, (l) => l.toUpperCase()),
      205,
      rightColY,
      { align: "right" },
    );
    rightColY += 10; // extra gap before Account section

    invoice.setFont("helvetica", "bold");
    invoice.text("Account:", 145, rightColY);
    rightColY += 5;
    invoice.setFont("helvetica", "normal");

    let hasExecutive =
      brandMetrics.staff.executive.id &&
      brandMetrics.staff.executive.id.length > 0;
    let hasManager =
      brandMetrics.staff.manager.id && brandMetrics.staff.manager.id.length > 0;

    let phoneNumberFormat = (value) => {
      var cleaned = ("" + value).replace(/\D/g, "");
      var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return "(" + match[1] + ") " + match[2] + "-" + match[3];
      }
    };

    let firstNameWithLastInitial = (name) => {
      if (typeof name === "string") {
        return name.split(" ")[0] + " " + name.split(" ")[1]?.charAt(0) + ".";
      }
      return name.first + " " + name.last?.charAt(0) + ".";
    };

    if (hasExecutive) {
      let executivePhoneNumber = "-";
      if (prefetchedData) {
        const raw = prefetchedData.executivePhoneRaw ?? "";
        executivePhoneNumber = raw ? phoneNumberFormat(raw) ?? "-" : "-";
      } else {
        const userDoc = doc(firestore, "users", brandMetrics.staff.executive.id);
        const response = await getDoc(userDoc);
        if (response.exists()) {
          executivePhoneNumber = response.data().phoneNumber
            ? phoneNumberFormat(response.data().phoneNumber)
            : "-";
        }
      }

      invoice.setFont("helvetica", "normal");
      invoice.text("Executive", 145, rightColY);
      invoice.text(
        firstNameWithLastInitial(
          brandMetrics.staff.executive.name.replace(/\b\w/g, (l) =>
            l.toUpperCase(),
          ),
        ),
        205,
        rightColY,
        { align: "right" },
      );
      rightColY += 5;
      invoice.text(executivePhoneNumber, 205, rightColY, { align: "right" });
      rightColY += 5;
      invoice.text(brandMetrics.staff.executive.email, 205, rightColY, {
        align: "right",
      });
      rightColY += 7;
    }

    invoice.setFont("helvetica", "normal");
    if (hasManager) {
      let managerPhoneNumber = "-";
      if (prefetchedData) {
        const raw = prefetchedData.managerPhoneRaw ?? "";
        managerPhoneNumber = raw ? phoneNumberFormat(raw) ?? "-" : "-";
      } else {
        const userDoc = doc(firestore, "users", brandMetrics.staff.manager.id);
        const response = await getDoc(userDoc);
        if (response.exists()) {
          managerPhoneNumber = response.data().phoneNumber
            ? phoneNumberFormat(response.data().phoneNumber)
            : "-";
        }
      }

      invoice.setFont("helvetica", "normal");
      invoice.text("Manager", 145, rightColY);
      invoice.text(
        firstNameWithLastInitial(
          brandMetrics.staff.manager.name.replace(/\b\w/g, (l) =>
            l.toUpperCase(),
          ),
        ),
        205,
        rightColY,
        { align: "right" },
      );
      rightColY += 5;
      invoice.text(managerPhoneNumber, 205, rightColY, { align: "right" });
      rightColY += 5;
      invoice.text(brandMetrics.staff.manager.email, 205, rightColY, {
        align: "right",
      });
      rightColY += 5;
    }

    // Products divider — positioned below whichever column is tallest
    let productsTableDividerY = Math.max(leftColY, midColY, rightColY);

    invoice.setFontSize(12);
    invoice.setFont("helvetica", "bold");
    invoice.text("Products:", 10, productsTableDividerY + 5);

    let getListPriceForProduct = (product) => {
      let termNeeded = this.order.paymentTerm === "cod" ? "cod" : "net";

      if (product.listPrice?.[termNeeded]) {
        return product.listPrice?.[termNeeded];
      }

      return product.price.normal;
    };

    let capitalize = (value) => {
      return value.charAt(0).toUpperCase() + value.slice(1);
    };

    let addComma = (value) => {
      return value.toLocaleString("en-US");
    };

    let products = this.lineItems
      .filter((item) => !item.promo)
      .sort((a, b) => {
        if (a.categoryName > b.categoryName) {
          return 1;
        } else if (a.categoryName === b.categoryName && a.name > b.name) {
          return 1;
        } else if (a.categoryName === b.categoryName && a.name === b.name) {
          return 0;
        } else if (a.categoryName === b.categoryName && a.name < b.name) {
          return -1;
        } else if (a.categoryName < b.categoryName) {
          return -1;
        }
      })
      .map((item) => {
        let productName = capitalize(
          item.name.replace(/\b\w/g, (l) => l.toUpperCase()),
        );
        let categoryName = capitalize(
          item.categoryName.replace(/\b\w/g, (l) => l.toUpperCase()),
        );

        let notes = item.notes?.length > 0 ? item.notes : `                 `;
        item.price.normal = getListPriceForProduct(item);
        return [
          `${productName}\n${categoryName}`,
          item.sample ? `SAMPLE\n${notes}` : notes,
          // `${item.sample ? "Yes" : "No"}`,
          addComma(item.cases),
          addComma(item.singles),
          addComma(item.qty),
          Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(item.price.normal),
          Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(item.amount - (item.tax ?? 0)),
          Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(item.tax ?? 0),
          Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(item.qty * item.amount),
          item.discount?.value ?? 0, // Case Discount Value
        ];
      });
    let promoUnits = this.lineItems.filter((item) => item.promo);
    if (promoUnits.length > 0) {
      promoUnits = promoUnits.sort((a, b) => {
        if (a.categoryName > b.categoryName) {
          return 1;
        } else if (a.categoryName === b.categoryName && a.name > b.name) {
          return 1;
        } else if (a.categoryName === b.categoryName && a.name === b.name) {
          return 0;
        } else if (a.categoryName === b.categoryName && a.name < b.name) {
          return -1;
        } else if (a.categoryName < b.categoryName) {
          return -1;
        }
      });

      products.push([
        {
          content: "",
          colSpan: 8,
          styles: { halign: "center", fillColor: 255 },
        },
      ]);
      products.push([
        {
          content: "PROMO UNITS",
          colSpan: 8,
          styles: {
            halign: "center",
            fillColor: 255,
            textColor: 0,
            fontStyle: "bold",
          },
        },
      ]);
    }

    let currency = (value) => {
      return Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(value);
    };

    promoUnits.forEach((item) => {
      let productName = capitalize(
        item.name.replace(/\b\w/g, (l) => l.toUpperCase()),
      );
      let categoryName = capitalize(
        item.categoryName.replace(/\b\w/g, (l) => l.toUpperCase()),
      );
      let notes = item.notes?.length > 0 ? item.notes : `                 `;
      item.price.normal = getListPriceForProduct(item);
      products.push([
        `${productName}\n${categoryName}`,
        item.sample ? `SAMPLE\n${notes}` : notes,
        // `${item.sample ? "Yes" : "No"}`,
        addComma(item.cases),
        addComma(item.singles),
        addComma(item.qty),
        Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "usd",
        }).format(item.price.normal),
        `${currencyFormat(item.amount - (item.tax ?? 0))}`,
        `${currencyFormat(item.tax ?? 0)}`,
        `${currencyFormat(item.qty * item.amount)}`,
        0, // Case Discount Value: Promo Items can not receive case discounts
      ]);
    });
    autoTable(invoice, {
      columns: [
        { dataKey: "item", header: "Item" },
        { dataKey: "notes", header: "Notes" },
        { dataKey: "cases", header: "Cases" },
        { dataKey: "singles", header: "Singles" },
        { dataKey: "units", header: "Units" },
        { dataKey: "listPrice", header: "List Price" },
        { dataKey: "salePrice", header: "Sale Price" },
        { dataKey: "tax", header: "Tax" },
        { dataKey: "total", header: "Total" },
      ],
      body: products,
      startY: productsTableDividerY + 10,
      margin: {
        right: 10,
        left: 10,
        bottom:
          promoUnits.length > 0 && products.length - promoUnits.length <= 11
            ? 10
            : 15,
        top: 20,
      },
      theme: "striped",
      headStyles: {
        fillColor: "black",
        textColor: "white",
        fontSize: 9,
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "left" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
      },
      bodyStyles: {
        fontSize: 9,
      },
      didParseCell: (data) => {
        if (data.section === "head") {
          if (data.column.index > 1) {
            data.cell.styles.halign = "right";
          }
        }
      },
      willDrawCell: (data) => {
        if (data.section === "body") {
          if (data.column.index === 0) {
            const cell = data.cell;
            cell.text = [];
          }
        }
      },
      didDrawCell: (data) => {
        if (data.section === "body" && data.row.cells.salePrice) {
          data.row.cells.salePrice.styles.fontStyle = "bold";
        }

        if (
          data.section === "body" &&
          data.column.dataKey === "total" &&
          data.row.cells.listPrice
        ) {
          let currencyFormat = (value) => {
            return Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "usd",
            }).format(value);
          };

          let listPrice = Number(
            parseFloat(data.row.cells.listPrice.raw.replace("$", "")).toFixed(
              2,
            ),
          );
          let salePrice = Number(
            parseFloat(data.row.cells.salePrice.raw.replace("$", "")).toFixed(
              2,
            ),
          );
          let tax = Number(
            parseFloat(data.row.cells.tax.raw.replace("$", "")).toFixed(2),
          );
          salePrice = salePrice + tax;
          // console.log(data.row.cells.item.raw, listPrice, salePrice);
          if (salePrice < listPrice) {
            let discountAmount = Number(
              parseFloat(listPrice - salePrice).toFixed(2),
            );
            let totalSaved = currencyFormat(
              discountAmount *
                Number(
                  parseFloat(data.row.cells.units.raw.replace(",", "")).toFixed(
                    2,
                  ),
                ),
            );
            let caseDiscountAppliedAmount = data.row.raw[9];
            let discountString =
              caseDiscountAppliedAmount > 0
                ? `${currencyFormat(
                    caseDiscountAppliedAmount,
                  )} CASE DISCOUNT / TOTAL DISCOUNT: ${totalSaved}`
                : `TOTAL DISCOUNT: ${totalSaved}`;
            invoice.setFont("helvetica", "bolditalic");
            invoice.setFontSize(6);
            invoice.text(
              data.cell.x + data.cell.width - 2,
              data.cell.y + 9,
              discountString,
              { align: "right" },
            );
          }
        } else if (data.section === "body" && data.column.index === 0) {
          const cell = data.cell;
          const text = cell.raw ?? "";
          // cell.text = [];
          let splitInfo = cell.colSpan > 1 ? [] : text.split("\n");
          let isHeaderRow = cell.colSpan > 1;

          let { x, y, width } = cell;
          if (isHeaderRow) {
            invoice.setFont("helvetica", "bold");
            invoice.text(text.content, pageWidth / 2, y + 5, {
              align: "center",
            });
          } else {
            let cursorX = x;
            splitInfo.forEach((part, i) => {
              if (i === 0) {
                invoice.setFont("helvetica", "normal");
                invoice.text(part, cursorX + 2, y + 4.5);
                y += invoice.getTextDimensions(part).h;
              } else {
                invoice.setFont("helvetica", "italic");

                invoice.setFontSize(8);
                invoice.text(part, cursorX + 2, y + 5);
                invoice.setFont("helvetica", "normal");

                invoice.setFontSize(9);
              }
            });
          }
        }
      },
    });

    //  We need to check the `pageNumber` property of the products table to see if it
    //    overflowed past page 1.
    //
    //    - If it did, jspdf's `lastAutoTable` has a `finalY`
    //    property that we can add to the y values of all of the content after the
    //    product's table to place the content below the products table correctly.
    //
    //    - If the table did not overflow , we set this value to 0 as we
    //    do not have to modify the position of anything on the second page
    if (
      invoice.lastAutoTable.pageNumber === 1 ||
      invoice.lastAutoTable.finalY > 160
    ) {
      invoice.addPage();
      invoice.setPage(invoice.lastAutoTable.pageNumber + 1);
    }

    const productsTableFinalY =
      invoice.lastAutoTable.pageNumber === 1 ||
      invoice.lastAutoTable.finalY > 160
        ? 10
        : invoice.lastAutoTable.finalY;

    // Notes
    invoice.setFont("helvetica", "bold");
    invoice.setFontSize(10);
    invoice.text(10, 15 + productsTableFinalY, `Notes:`);
    invoice.setFont("helvetica", "italic");
    invoice.setFontSize(9);

    var wrappedNotesForDimensions = invoice.splitTextToSize(
      this.order.notes,
      100,
    );
    let notesDimensions = invoice.getTextDimensions(wrappedNotesForDimensions);
    let notesSubstring =
      this.order.notes.length < 600 && notesDimensions.h <= 52.5
        ? this.order.notes
        : this.order.notes.substr(0, 600) + "...";

    var wrappedNotes = invoice.splitTextToSize(notesSubstring, 100);
    invoice.text(wrappedNotes, 10, 25 + productsTableFinalY);

    // Delivery Preferences
    invoice.setFont("helvetica", "bold");
    invoice.setFontSize(10);
    invoice.text(10, 100 + productsTableFinalY, `Delivery Preferences:`);
    invoice.setFont("helvetica", "italic");
    invoice.setFontSize(9);

    var wrappedDeliveryPreferences = invoice.splitTextToSize(
      this.order.account.deliveryPreferences,
      pageWidth - 20,
    );
    let deliveryPreferencesDimensions = invoice.getTextDimensions(
      wrappedDeliveryPreferences,
    );
    invoice.text(wrappedDeliveryPreferences, 10, 110 + productsTableFinalY);

    // Balance
    invoice.setFont("helvetica", "normal");
    invoice.setFontSize(10);

    let totalQTY = this.lineItems.reduce((acc, item) => {
      // Handle both case-based and non-case products
      const isCaseProduct = item.unitsPerCase > 0 || item.caseQTY > 0;

      if (isCaseProduct) {
        // Case-based product: calculate using cases and singles
        return (
          acc +
          item.cases * (item.unitsPerCase || item.caseQTY || 0) +
          item.singles
        );
      } else {
        // Non-case product: use singles directly, ignore cases
        return acc + (item.singles || 0);
      }
    }, 0);

    let subTotal = this.lineItems.reduce(
      (acc, item) => acc + item.amount * item.qty,
      0,
    );

    let totalSaved = this.lineItems
      .filter((item) => item.amount < item.price.normal)
      .reduce((sum, item) => {
        let discountAmount = item.price.normal - item.amount;
        return sum + discountAmount * item.qty;
      }, 0);

    // Calculate order discount amount
    let orderDiscountAmount = 0;
    if (this.order.discount?.value) {
      let isPercentage = this.order.discount.type === "%";
      let discountValue = this.order.discount.value ?? 0;
      if (isPercentage) {
        orderDiscountAmount = (discountValue / 100) * subTotal;
      } else {
        orderDiscountAmount = discountValue;
      }
    }

    invoice.text(
      `Subtotal (Qty: ${addComma(totalQTY)})`,
      125,
      17.5 + productsTableFinalY,
    );

    invoice.text(
      Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(subTotal + totalSaved),
      205,
      17.5 + productsTableFinalY,
      {
        align: "right",
      },
    );
    invoice.line(
      120,
      20 + productsTableFinalY,
      205,
      20 + productsTableFinalY,
      "S",
    );
    invoice.text(`List Price Discounts`, 125, 25 + productsTableFinalY);

    invoice.text(
      `${totalSaved > 0 ? "-" : ""}${Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(totalSaved)}`,
      205,
      25 + productsTableFinalY,
      {
        align: "right",
      },
    );
    invoice.setDrawColor("#E5E7EB");
    invoice.line(
      120,
      27.5 + productsTableFinalY,
      205,
      27.5 + productsTableFinalY,
      "S",
    );
    invoice.stroke();
    if (orderDiscountAmount > 0) {
      invoice.text(`Order Discount`, 125, 32.5 + productsTableFinalY);

      invoice.text(
        `-${Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "usd",
        }).format(orderDiscountAmount)}`,
        205,
        32.5 + productsTableFinalY,
        {
          align: "right",
        },
      );
      invoice.setDrawColor("#E5E7EB");
      invoice.line(
        120,
        35 + productsTableFinalY,
        205,
        35 + productsTableFinalY,
        "S",
      );
      invoice.stroke();
    }
    invoice.text(
      "Adjusted Subtotal",
      125,
      (orderDiscountAmount > 0 ? 40 : 32.5) + productsTableFinalY,
    );
    invoice.text(
      `${Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(subTotal - orderDiscountAmount)}`,
      205,
      (orderDiscountAmount > 0 ? 40 : 32.5) + productsTableFinalY,
      {
        align: "right",
      },
    );
    invoice.setDrawColor("#E5E7EB");
    invoice.line(
      120,
      (orderDiscountAmount > 0 ? 42.5 : 35) + productsTableFinalY,
      205,
      (orderDiscountAmount > 0 ? 42.5 : 35) + productsTableFinalY,
      "S",
    );
    invoice.stroke();
    invoice.text(
      "Shipping",
      125,
      (orderDiscountAmount > 0 ? 47.5 : 40) + productsTableFinalY,
    );
    invoice.text(
      Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(this.order.balance.shipping),
      205,
      (orderDiscountAmount > 0 ? 47.5 : 40) + productsTableFinalY,
      {
        align: "right",
      },
    );
    invoice.setDrawColor("#E5E7EB");
    invoice.line(
      120,
      (orderDiscountAmount > 0 ? 50 : 42.5) + productsTableFinalY,
      205,
      (orderDiscountAmount > 0 ? 50 : 42.5) + productsTableFinalY,
      "S",
    );
    invoice.stroke();
    invoice.text(
      "Tax",
      125,
      (orderDiscountAmount > 0 ? 55 : 47.5) + productsTableFinalY,
    );
    invoice.text(
      `${Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(this.order.balance.excise)}`,
      205,
      (orderDiscountAmount > 0 ? 55 : 47.5) + productsTableFinalY,
      {
        align: "right",
      },
    );
    invoice.setDrawColor("#E5E7EB");
    invoice.line(
      120,
      (orderDiscountAmount > 0 ? 57.5 : 50) + productsTableFinalY,
      205,
      (orderDiscountAmount > 0 ? 57.5 : 50) + productsTableFinalY,
      "S",
    );
    invoice.stroke();
    invoice.text(
      "Payments Received",
      125,
      (orderDiscountAmount > 0 ? 62.5 : 55) + productsTableFinalY,
    );
    invoice.text(
      `${
        Number(this.order.balance.total) - Number(this.order.balance.due) > 0
          ? "-"
          : ""
      }${Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(
        Number(this.order.balance.total) - Number(this.order.balance.due),
      )}`,
      205,
      (orderDiscountAmount > 0 ? 62.5 : 55) + productsTableFinalY,
      {
        align: "right",
      },
    );
    invoice.setDrawColor("#E5E7EB");
    invoice.line(
      120,
      (orderDiscountAmount > 0 ? 65 : 57.5) + productsTableFinalY,
      205,
      (orderDiscountAmount > 0 ? 65 : 57.5) + productsTableFinalY,
      "S",
    );
    invoice.stroke();

    let totalCreditsApplied = 0;
    if (totalCreditsApplied > 0) {
      invoice.text(
        "Credit Memo",
        125,
        (orderDiscountAmount > 0 ? 70 : 62.5) + productsTableFinalY,
      );
      invoice.text(
        `${totalCreditsApplied > 0 ? "-" : ""}${Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "usd",
        }).format(totalCreditsApplied)}`,
        205,
        (orderDiscountAmount > 0 ? 70 : 62.5) + productsTableFinalY,
        {
          align: "right",
        },
      );
      invoice.setDrawColor("#E5E7EB");
      invoice.line(
        120,
        (orderDiscountAmount > 0 ? 72.5 : 65) + productsTableFinalY,
        205,
        (orderDiscountAmount > 0 ? 72.5 : 65) + productsTableFinalY,
        "S",
      );
      invoice.stroke();
    }
    invoice.text(
      "Total Due",
      125,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 77.5
          : 70
        : totalCreditsApplied > 0
        ? 70
        : 62.5) + productsTableFinalY,
    );
    invoice.text(
      Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(Number(this.order.balance.due)),
      205,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 77.5
          : 70
        : totalCreditsApplied > 0
        ? 70
        : 62.5) + productsTableFinalY,
      {
        align: "right",
      },
    );
    invoice.setDrawColor("#E5E7EB");
    // invoice.line(
    //   120,
    //   57.5 + productsTableFinalY,
    //   205,
    //   57.5 + productsTableFinalY,
    //   "S"
    // );
    invoice.stroke();

    // Account Balance Rect
    invoice.setDrawColor(0);
    invoice.setFillColor("#F2F5F6");
    invoice.roundedRect(
      122.5,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 87.5
          : 80
        : totalCreditsApplied > 0
        ? 80
        : 72.5) + productsTableFinalY,
      85,
      15,
      2,
      2,
      "FD",
    );

    invoice.setFont("helvetica", "normal");
    invoice.setFontSize(10);
    invoice.text(
      125,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 92.5
          : 85
        : totalCreditsApplied > 0
        ? 85
        : 77.5) + productsTableFinalY,
      `Previous Account Balance`,
    );
    invoice.text(
      currencyFormat(previousAccountBalance),
      205,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 92.5
          : 85
        : totalCreditsApplied > 0
        ? 85
        : 77.5) + productsTableFinalY,
      {
        align: "right",
      },
    );
    invoice.setFont("helvetica", "bold");
    invoice.setFontSize(10);
    invoice.text(
      125,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 100
          : 92.5
        : totalCreditsApplied > 0
        ? 92.5
        : 85) + productsTableFinalY,
      `New Account Balance`,
    );
    invoice.text(
      Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(previousAccountBalance + Number(this.order.balance.due)),
      205,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 100
          : 92.5
        : totalCreditsApplied > 0
        ? 92.5
        : 85) + productsTableFinalY,
      {
        align: "right",
      },
    );

    invoice.setFont("helvetica", "italic");
    invoice.setFontSize(9);
    let wrappedDisclaimer = invoice.splitTextToSize(
      "If a payment has been submitted in the last 10 days, the account balance may not reflect until it's been processed.",
      85,
    );

    invoice.text(
      wrappedDisclaimer,
      165,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 107.5
          : 100
        : totalCreditsApplied > 0
        ? 100
        : 92.5) + productsTableFinalY,
      {
        align: "center",
      },
    );

    let startYPosition =
      125 + productsTableFinalY + deliveryPreferencesDimensions.h;

    if (startYPosition > 225) {
      invoice.addPage();
      invoice.setPage(invoice.internal.getNumberOfPages() + 1);
      startYPosition = 20;
    }

    // Divider
    invoice.setDrawColor("#F2F5F6");
    invoice.line(10, startYPosition, 205, startYPosition, "S");
    invoice.stroke();

    //   Open Invoices
    if (ordersForPreviousAccountBalance.length > 0) {
      // Divider
      invoice.setDrawColor("#F2F5F6");
      invoice.line(10, startYPosition, 205, startYPosition, "S");
      invoice.stroke();

      //   Open Invoices
      invoice.setFontSize(10);
      invoice.setFont("helvetica", "bold");
      invoice.text("Open Invoices:", 10, startYPosition + 10);

      const orders = ordersForPreviousAccountBalance.map((item) => {
        var shipDate = moment(item.shipDate).format("MM/DD/YY");
        var paymentDueDate = moment(item.paymentDueDate).format("MM/DD/YY");
        var paymentTerm = store.getters.getPaymentTerms.find(
          (paymentTerm) => paymentTerm.value === item.paymentTerm,
        );

        let externalOrderNumber =
          item.external && item.external.length > 0
            ? item.external.toUpperCase()
            : "-";

        const dueDate = moment(item.shipDate)
          .add(paymentTerm.due, "days")
          .toDate();

        let daysPast = moment().diff(moment(dueDate), "days");
        daysPast = daysPast < 0 ? daysPast - 1 : daysPast;

        daysPast = daysPast > 0 ? daysPast : daysPast * -1;

        let daysSinceDelivery = moment()
          .startOf("day")
          .diff(moment(item.shipDate).startOf("day"), "days");

        return [
          shipDate,
          paymentDueDate,
          daysPast,
          paymentTerm.text,
          daysSinceDelivery,
          currencyFormat(item.balance),
          item.internal.toUpperCase(),
          externalOrderNumber,
        ];
      });

      autoTable(invoice, {
        head: [
          [
            "Ship Date",
            "Payment Due Date",
            "Days Past Due",
            "Payment Term",
            "Days Since Delivery",
            "Balance",
            "Internal Order #",
            "External Order #",
          ],
        ],
        body: orders,
        startY: startYPosition + 15,
        margin: {
          right: 10,
          left: 10,
          bottom: 15,
          top: 20,
        },
        theme: "striped",
        headStyles: {
          fillColor: "#4261c7",
          textColor: "white",
          fontSize: 9,
        },
        bodyStyles: {
          fontSize: 9,
        },
      });

      startYPosition = invoice.lastAutoTable.finalY + 10;
    }

    // External Links Section (this remains as it was)
    if (payableTo.invoiceURLData?.url?.length) {
      if (startYPosition > 225) {
        invoice.addPage();
        invoice.setPage(invoice.internal.getNumberOfPages());
      }

      startYPosition = startYPosition > 225 ? 20 : startYPosition;

      // Divider
      invoice.setDrawColor("#F2F5F6");
      invoice.line(10, startYPosition, 205, startYPosition, "S");
      invoice.stroke();

      // External Links
      invoice.setFontSize(10);
      invoice.setFont("helvetica", "bold");
      invoice.text("External Links:", 10, startYPosition + 10);

      let QRCode = require("qrcode");
      let opts = {
        errorCorrectionLevel: "H",
        type: "image/jpeg",
        quality: 0.3,
        margin: 1,
        color: {
          dark: "#000000FF",
          light: "#FFFFFFFF",
        },
      };

      QRCode.toDataURL(payableTo.invoiceURLData.url, opts, function (err, url) {
        if (err) throw err;

        invoice.addImage(
          url,
          10,
          startYPosition + 15,
          20,
          20,
          "licenseeURL",
          "FAST",
        );
      });

      invoice.setFont("helvetica", "normal");
      invoice.setFontSize(9);

      invoice.text(payableTo.invoiceURLData.url, 10, startYPosition + 40);

      let wrappedDescription = invoice.splitTextToSize(
        payableTo.invoiceURLData.description,
        pageWidth - 20,
      );
      invoice.text(wrappedDescription, 10, startYPosition + 45);
    }

    if (payableTo.invoiceURLData?.url?.length) {
      if (startYPosition > 225) {
        invoice.addPage();
        invoice.setPage(invoice.internal.getNumberOfPages());
      }

      startYPosition = startYPosition > 225 ? 20 : startYPosition;

      // Divider
      invoice.setDrawColor("#F2F5F6");
      invoice.line(10, startYPosition, 205, startYPosition, "S");
      invoice.stroke();

      //   External Links
      invoice.setFontSize(10);
      invoice.setFont("helvetica", "bold");
      invoice.text("External Links:", 10, startYPosition + 10);

      let QRCode = require("qrcode");
      let opts = {
        errorCorrectionLevel: "H",
        type: "image/jpeg",
        quality: 0.3,
        margin: 1,
        color: {
          dark: "#000000FF",
          light: "#FFFFFFFF",
        },
      };

      QRCode.toDataURL(payableTo.invoiceURLData.url, opts, function (err, url) {
        if (err) throw err;

        invoice.addImage(
          url,
          10,
          startYPosition + 15,
          20,
          20,
          "licenseeURL",
          "FAST",
        );
      });

      invoice.setFont("helvetica", "normal");
      invoice.setFontSize(9);

      invoice.text(payableTo.invoiceURLData.url, 10, startYPosition + 40);

      let wrappedDescription = invoice.splitTextToSize(
        payableTo.invoiceURLData.description,
        pageWidth - 20,
      );
      invoice.text(wrappedDescription, 10, startYPosition + 45);
    }

    let totalPromotionDiscounts = this.lineItems
      .filter((item) => item.promo)
      .reduce((acc, item) => acc + item.price.normal * item.qty, 0);

    // Promo Unit Totals
    if (totalPromotionDiscounts > 0) {
      invoice.setFontSize(10);
      invoice.setTextColor("#0277BD");
      invoice.text("Total promo value: ", 80, 265, { align: "left" });
      invoice.text(currencyFormat(totalPromotionDiscounts), 130, 265, {
        align: "right",
      });
    }
    invoice.setTextColor("black");

    const QRCode = require("qrcode");
    const customerOrderQrOpts = {
      errorCorrectionLevel: "L",
      type: "image/jpeg",
      quality: 0.3,
      margin: 1,
      color: {
        dark: "#000000FF",
        light: "#FFFFFFFF",
      },
    };
    let customerOrderQrDataUrl = null;
    try {
      // Same path as route `salesOrder-customer-view` (avoid importing router from utils).
      const customerPublicOrderUrl = `${window.location.origin}/salesOrderPublic/${this.order.id}`;
      customerOrderQrDataUrl = await QRCode.toDataURL(
        customerPublicOrderUrl,
        customerOrderQrOpts,
      );
    } catch (err) {
      console.error("SalesOrderClient generateInvoice: customer order QR failed", err);
      logWorkflowWarning(`generateInvoice: customer order QR generation failed — ${err.message}`, {
        workflow: "sales-order",
        action: "generate-invoice-qr-code",
        context: { orderId: this.orderID },
      });
    }

    // END
    var pageCount = invoice.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      invoice.setPage(i);
      invoice.setFontSize(14);
      invoice.setFont("helvetica", "bold");
      invoice.text("invoice".toUpperCase(), 10, 10);
      invoice.setFont("helvetica", "normal");
      invoice.setFontSize(10);
      var orderNumber = invoice.splitTextToSize(
        `Order #: ${internalOrderNumber}`,
        70,
      );
      invoice.text(10, 15, orderNumber);
      invoice.setFont("helvetica", "normal");
      var manifestNumber = invoice.splitTextToSize(
        `Manifest #: ${internalManifestNumber}`,
        80,
      );
      invoice.text(10, 20, manifestNumber);
      invoice.setFont("helvetica", "normal");
      invoice.setFontSize(8);
      invoice.text(
        110,
        270,
        `Page ${
          invoice.internal.getCurrentPageInfo().pageNumber
        } / ${pageCount}`,
        {
          align: "center",
        },
      );

      if (i === 1 && customerOrderQrDataUrl) {
        const qrSize = 12;
        const qrX = pageWidth - 23;
        const qrY = 13;
        invoice.addImage(
          customerOrderQrDataUrl,
          "JPEG",
          qrX,
          qrY,
          qrSize,
          qrSize,
          "customerOrderQr",
          "FAST",
        );

        invoice.setFont("helvetica", "normal");
        invoice.setFontSize(8);
        // Right-align to QR's right edge; slight upward nudge (~1px @ 96dpi in mm).
        invoice.text(qrX + qrSize, qrY + qrSize + 5 - 0.26, "View Invoice", {
          align: "right",
        });
      }
    }

    let externalNumber = this.order.flags.external
      ? ` (${this.order.orderNumber.external})`
      : "";

    fileName = `${this.order.account.name.replace(/(\b[a-z](?!\s))/g, (l) =>
      l.toUpperCase(),
    )} - #${internalOrderNumber}${externalNumber}`;

    // invoice.output("dataurlnewwindow");      //  Opens in a new tab, doesn't work on iOS/Safari
    invoice.save(`${fileName}.pdf`);
  }

  async transitionAction(status) {
    addBreadcrumb("transitionAction: dispatching", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        fromStatus: this.order?.status,
        toStatus: status,
        balance: this.order?.balance,
      },
    });
    switch (status) {
      case "cancelled":
      case "rejected":
        await this.cancelledAction();
        this.transitionOrderStatus("cancelled");
        break;
      case "draft":
        this.transitionOrderStatus("draft");
        break;
      case "submitted":
        this.transitionOrderStatus("submitted");
        break;
      case "accepted":
        this.transitionOrderStatus("accepted");
        break;
      case "deliveryConfirmation":
        this.transitionOrderStatus("deliveryConfirmation");
        break;
      case "fulfilled":
        this.transitionOrderStatus("fulfilled");
        break;
      case "shipped":
        this.transitionOrderStatus("shipped");
        break;
      case "complete":
        this.transitionOrderStatus("complete");
        break;
      default:
        break;
    }
  }

  // MARK: STATUS TRANSITIONS

  // [CANCELLED]
  async cancelledAction(cancelReason) {
    addBreadcrumb("cancelledAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });
    await this.getOrder();
    await this.getLineItems();

    if (this.order?.status === "cancelled") {
      return;
    }

    // Atomic claim: only one concurrent request can transition to cancelled.
    const salesOrderDocRef = doc(firestore, "salesOrders", this.order.id);
    const applied = await runTransaction(firestore, async (transaction) => {
      const orderSnap = await transaction.get(salesOrderDocRef);
      if (!orderSnap.exists() || orderSnap.data().status === "cancelled") {
        return false;
      }
      transaction.update(salesOrderDocRef, {
        status: "cancelled",
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
      });
      return true;
    });

    if (!applied) {
      return;
    }

    const batch = writeBatch(firestore);
    const brandStatsDoc = doc(
      firestore,
      "brandStats",
      `${this.order.account.id}_${this.order.brand.id}`,
    );

    const salesOrderDoc = salesOrderDocRef;

    const stateDoc = doc(
      firestore,
      "states",
      this.order.location.shipping.state.stateID,
    );
    const stateResponse = await getDoc(stateDoc);

    let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

    if (this.order.fulfillmentLocation) {
      fulfillmentLocation = this.order.fulfillmentLocation.id;
    }

    const locationRef = doc(
      firestore,
      "locationManufacturing",
      `${fulfillmentLocation}_${this.order.brand.id}`,
    );

    // Get current inventory data for activity tracking
    const inventoryCheck = await getDoc(locationRef);
    const inventoryData = inventoryCheck.exists()
      ? inventoryCheck.data().manufacturing || {}
      : {};

    const field = this.order.status === "fulfilled" ? "available" : "reserved",
      direction = this.order.status === "fulfilled" ? 1 : -1;
    const cancelActivityMap = {};
    for (const li of this.lineItems) {
      const key = `${li.categoryID}_${li.productID}`;
      if (!cancelActivityMap[key]) {
        cancelActivityMap[key] = { totalQty: 0, lineItem: li };
      }
      cancelActivityMap[key].totalQty += li.qty ?? 0;
    }

    // Reset inventory
    for (let index = 0; index < this.lineItems.length; index++) {
      let lineItem = this.lineItems[index];

      if (lineItem.productLots?.length) {
        for (let i = 0; i < lineItem.productLots.length; i++) {
          const productLot = lineItem.productLots[i];
          const productLotDoc = doc(firestore, "productLots", productLot.id);
          const productLotResponse = await getDoc(productLotDoc);

          if (productLotResponse.exists()) {
            const productLotData = productLotResponse.data();
            let availableUnits = productLotResponse.data().units;

            if (productLotData.hasOwnProperty("available")) {
              availableUnits =
                Number(productLotResponse.data().available) + productLot.units;
            }

            const newRelatedLineItems =
              productLotData.relatedLineItems?.filter(
                (item) => item.id !== lineItem.id,
              ) ?? [];

            let foundProductLot = this.response.productLots.find(
              (item) => item.id === productLot.id,
            );
            let foundLocationData = foundProductLot?.locationData?.find(
              (item) => item.id === this.order.fulfillmentLocation.id,
            );
            if (
              this.order.fulfillmentLocation?.id?.length &&
              foundLocationData
            ) {
              let locationDataDoc = doc(
                firestore,
                "productLots",
                productLot.id,
                "locationData",
                foundLocationData.id,
              );
              batch.set(
                locationDataDoc,
                {
                  available: increment(productLot.units),
                  updated: serverTimestamp(),
                  lastUpdated: {
                    name: store.getters.getUser.name,
                    uid: store.getters.getUser.uid,
                  },
                },
                { merge: true },
              );
            }

            await updateDoc(
              productLotDoc,
              {
                active: availableUnits > 0,
                available: availableUnits,
                relatedLineItems: newRelatedLineItems,
                updated: serverTimestamp(),
                lastUpdated: {
                  name: store.getters.getUser.name,
                  uid: store.getters.getUser.uid,
                },
              },
              { merge: true },
            );
          }

          let lineItemDoc = doc(
            firestore,
            "salesOrders_lineItems",
            lineItem.id,
          );

          batch.set(
            lineItemDoc,
            {
              productLots: [],
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
            },
            { merge: true },
          );
        }
      }
    }

    // Location manufacturing: one write per product (same product, multiple lines = one aggregated update)
    for (const key of Object.keys(cancelActivityMap)) {
      const agg = cancelActivityMap[key];
      const { categoryID, productID } = agg.lineItem;
      batch.set(
        locationRef,
        {
          manufacturing: {
            [categoryID]: {
              [field]: {
                [productID]: increment(direction * agg.totalQty),
              },
            },
          },
        },
        { merge: true },
      );
    }

    // Order
    const oldStatus = store.getters.getOrderStatus.find(
        (option) => option.value === this.order.status,
      ),
      newStatus = store.getters.getOrderStatus.find(
        (option) => option.value === "cancelled",
      );

    batch.set(
      salesOrderDoc,
      {
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        productLots: [],
        status: "cancelled",
        records: {
          cancelled: moment().startOf("day").add(12, "hours").toDate(),
        },
      },
      { merge: true },
    );
    // External - Update Status
    if (this.orderIsExternal) {
      switch (this.externalOrderType) {
        case "leaflink":
          if (this.externalUpdates.leaflink.includes("statusUpdated")) {
            let transition =
              this.order.status === "draft" ? "cancel" : "reject";
            addBreadcrumb(`leaflink: transitionOrder ${transition} (cancelled)`, {
              workflow: "sales-order",
              data: { orderId: this.orderID, transition, fromStatus: this.order?.status },
            });
            try {
              await this.leaflinkClient.transitionOrder(transition);
            } catch (error) {
              logWorkflowWarning(`LeafLink: transitionOrder("${transition}") (cancelled) failed — ${error.message}`, {
                workflow: "sales-order",
                action: "leaflink-cancelled-transition",
                context: { orderId: this.orderID, transition, balance: this.order?.balance },
              });
              throw error;
            }
          }
          break;
        default:
          break;
      }
    }

    // Create activity for order cancellation
    await createActivity({
      type: "status",
      action: "Order cancelled",
      item: `Order #${this.order.orderNumber.internal.toUpperCase()} cancelled - Status changed from ${
        oldStatus.text
      } to ${newStatus.text}${
        cancelReason ? ` - Reason: ${cancelReason}` : ""
      }`,
      relatedTo: "salesOrders",
      relatedID: [this.order.id],
      user: {
        name: store.getters.getUser.name,
        uid: store.getters.getUser.uid,
      },
      account: {
        id: this.order.account.id,
        name: this.order.account.name,
      },
      brand: {
        id: this.order.brand.id,
        name: this.order.brand.name,
      },
      businessImpact: "high",
      priority: "high",
      changeData: {
        field: "status",
        oldValue: {
          text: oldStatus.text,
          value: oldStatus.value,
          color: oldStatus.color,
        },
        newValue: {
          text: newStatus.text,
          value: newStatus.value,
          color: newStatus.color,
        },
        reason: cancelReason,
        orderNumber: this.order.orderNumber,
      },
    });

    const brandStats = await getDoc(brandStatsDoc);
    if (brandStats.data().metrics.closeDate?.order === this.orderID) {
      batch.set(
        brandStatsDoc,
        {
          metrics: {
            closeDate: null,
          },
        },
        { merge: true },
      );
    }

    addBreadcrumb("cancelledAction: committing batch", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        lineItemCount: this.lineItems.length,
        balance: this.order?.balance,
        fromStatus: this.order?.status,
      },
    });
    await batch.commit();

    // Inventory activities: one per product (same product, multiple lines = one aggregated activity)
    for (const key of Object.keys(cancelActivityMap)) {
      const agg = cancelActivityMap[key];
      const { categoryID, productID } = agg.lineItem;
      const skuID = `${fulfillmentLocation}_${this.order.brand.id}_${categoryID}_${productID}`;
      const currentAvailable =
        inventoryData[categoryID]?.available?.[productID] ?? 0;
      const currentReserved =
        inventoryData[categoryID]?.reserved?.[productID] ?? 0;
      const currentPending =
        inventoryData[categoryID]?.pending?.[productID] ?? 0;
      const currentTransfer =
        inventoryData[categoryID]?.transfer?.[productID] ?? 0;
      const currentInTesting =
        inventoryData[categoryID]?.inTesting?.[productID] ?? 0;
      const fromState =
        this.order.status === "fulfilled" ? "fulfilled" : "reserved";
      const toState = "available";
      await createInventoryActivity({
        action: "Inventory released",
        item: `${agg.totalQty} units of ${agg.lineItem.name} (${agg.lineItem.sku}) released from cancelled Order #${this.order.orderNumber.internal}`,
        skuID,
        itemData: agg.lineItem,
        movement: {
          type: "release",
          from: fromState,
          to: toState,
          quantity: agg.totalQty,
          reason: "salesOrderCancelled",
          reasonID: this.orderID,
        },
        snapshot: {
          before: {
            available: currentAvailable,
            reserved: currentReserved,
            pending: currentPending,
            transfer: currentTransfer,
            inTesting: currentInTesting,
          },
        },
        location: {
          id: fulfillmentLocation,
          name:
            this.order.fulfillmentLocation?.name ||
            this.order.location.shipping.name,
        },
        order: this.order,
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        extraSalesOrderData: {
          cancelReason: cancelReason,
        },
      });
    }
  }

  // [PREVIOUS]
  async previousAction(order, reason) {
    addBreadcrumb("previousAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });
    await this.getOrder();
    await this.getLineItems();

    const PREVIOUS_STATUS_MAP = {
      complete: "shipped",
      shipped: "fulfilled",
      fulfilled: "deliveryConfirmation",
      deliveryConfirmation: "accepted",
      accepted: "submitted",
      submitted: "draft",
      draft: "cancelled",
    };
    const expectedCurrentStatus = this.order?.status;
    const targetStatus = PREVIOUS_STATUS_MAP[expectedCurrentStatus];

    if (!targetStatus) {
      return;
    }

    // Atomic claim: only one concurrent request can perform this revert.
    const salesOrderDocRef = doc(firestore, "salesOrders", this.order.id);
    const applied = await runTransaction(firestore, async (transaction) => {
      const orderSnap = await transaction.get(salesOrderDocRef);
      const current = orderSnap.exists() ? orderSnap.data().status : null;
      if (current !== expectedCurrentStatus) {
        return false;
      }
      transaction.update(salesOrderDocRef, {
        status: targetStatus,
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
      });
      return true;
    });

    if (!applied) {
      return;
    }

    // Ensure we have the latest line item qtys (e.g. after user decreased line qty before rollback)
    await this.getLineItems();

    try {
      const batch = writeBatch(firestore);
      const brandStatsDoc = doc(
        firestore,
        "brandStats",
        `${this.order.account.id}_${this.order.brand.id}`,
      );

      const salesOrderDoc = salesOrderDocRef;

      let updates = [];
      const stateDoc = doc(
        firestore,
        "states",
        this.order.location.shipping.state.stateID,
      );

      const stateResponse = await getDoc(stateDoc);
      let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

      if (this.order.fulfillmentLocation) {
        fulfillmentLocation = this.order.fulfillmentLocation.id;
      }

      const locationRef = doc(
        firestore,
        "locationManufacturing",
        `${fulfillmentLocation}_${this.order.brand.id}`,
      );

      // Get current inventory data for activity tracking (needed for fulfilled case)
      const inventoryDoc = await getDoc(locationRef);
      const inventoryData = inventoryDoc.exists() ? inventoryDoc.data() : {};

      let status = "shipped";
      let leaflinkStatus = null;
      let transitionLeaflink = true;

      switch (order.fullStatus.value) {
        case "complete":
          status = "shipped";
          leaflinkStatus = "shipped";
          break;
        case "shipped":
          status = "fulfilled";
          leaflinkStatus = "fulfilled";
          order.lineItems.forEach((item) => {
            item.flags.delivered = false;
          });

          // Account Balance
          const brandStats = await getDoc(brandStatsDoc);
          const currentAccountBalance = brandStats.data().metrics.balance;
          const newBalance = Number(
            parseFloat(
              currentAccountBalance - Number(this.order.balance.due),
            ).toFixed(2),
          );

          batch.set(
            brandStatsDoc,
            {
              metrics: {
                balance: newBalance,
              },
            },
            { merge: true },
          );

          break;
        case "fulfilled":
          status = "deliveryConfirmation";
          leaflinkStatus = "accepted";

          // Use current line item qtys (getLineItems() was called after transaction).
          // If user edited line qty (e.g. 4→2) before reverting, rollback shows that diff (e.g. -2 available, +2 reserved).
          // If no edit, rollback shows the original fulfilled amount (e.g. -4 available, +4 reserved).
          for (const item of this.lineItems) {
            batch.set(
              locationRef,
              {
                manufacturing: {
                  [item.categoryID]: {
                    available: {
                      [item.productID]: increment(item.qty),
                    },
                    reserved: {
                      [item.productID]: increment(item.qty),
                    },
                  },
                },
              },
              { merge: true },
            );

            // Track inventory movement for this SKU
            const skuID = `${fulfillmentLocation}_${this.order.brand.id}_${item.categoryID}_${item.productID}`;
            const currentAvailable =
              inventoryData.manufacturing?.[item.categoryID]?.available?.[
                item.productID
              ] || 0;
            const currentReserved =
              inventoryData.manufacturing?.[item.categoryID]?.reserved?.[
                item.productID
              ] || 0;
            const currentPending =
              inventoryData.manufacturing?.[item.categoryID]?.pending?.[
                item.productID
              ] || 0;
            const currentTransfer =
              inventoryData.manufacturing?.[item.categoryID]?.transfer?.[
                item.productID
              ] || 0;
            const currentInTesting =
              inventoryData.manufacturing?.[item.categoryID]?.inTesting?.[
                item.productID
              ] || 0;

            await createInventoryActivity({
              action: "Inventory restored to reserved",
              item: `${item.qty} units of ${item.name} (${item.sku}) restored to reserved from fulfilled Order #${this.order.orderNumber.internal}`,
              skuID,
              itemData: item,
              movement: {
                type: "restoration",
                from: "fulfilled",
                to: "reserved",
                quantity: item.qty,
                reason: "salesOrderRestored",
                reasonID: this.orderID,
              },
              snapshot: {
                before: {
                  available: currentAvailable,
                  reserved: currentReserved,
                  pending: currentPending,
                  transfer: currentTransfer,
                  inTesting: currentInTesting,
                },
                // "after" is calculated dynamically from "before" + movement.quantity in the display
              },
              location: {
                id: fulfillmentLocation,
                name:
                  this.order.fulfillmentLocation?.name ||
                  this.order.location.shipping.name,
              },
              order: this.order,
              user: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              extraSalesOrderData: {
                orderID: this.orderID,
              },
            });
          }
          break;
        case "deliveryConfirmation":
          status = "accepted";
          order.lineItems.forEach((item) => {
            item.flags.fulfilled = false;
            delete item.fulfilled;
          });
          transitionLeaflink = false;
          break;
        case "accepted":
          status = "submitted";
          transitionLeaflink = false;
          break;
        case "submitted":
          status = "draft";
          break;
        case "draft":
          status = "cancelled";
          break;
        default:
          break;
      }

      batch.set(
        salesOrderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          status,
          lineItems: order.lineItems,
        },
        { merge: true },
      );

      let transition = null;
      switch (leaflinkStatus) {
        case "shipped":
          transition = "ship";
          break;
        case "fulfilled":
          transition = "fulfill";
          break;
        case "accepted":
          transition = "accept";
          break;
        case null:
          break;
      }

      // External - Update Status
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (
              transitionLeaflink &&
              transition?.length &&
              this.externalUpdates.leaflink.includes("statusUpdated")
            ) {
              addBreadcrumb(`leaflink: transitionOrder ${transition} (previousAction)`, {
                workflow: "sales-order",
                data: { orderId: this.orderID, transition, fromStatus: this.order?.status },
              });
              try {
                await this.leaflinkClient.transitionOrder(transition);
              } catch (error) {
                logWorkflowWarning(`LeafLink: transitionOrder("${transition}") (previousAction) failed — ${error.message}`, {
                  workflow: "sales-order",
                  action: "leaflink-previous-transition",
                  context: { orderId: this.orderID, transition, balance: this.order?.balance },
                });
                throw error;
              }
            }
            break;
          default:
            break;
        }
      }

      const oldStatus = store.getters.getOrderStatus.find(
          (option) => option.value === this.order.status,
        ),
        newStatus = store.getters.getOrderStatus.find(
          (option) => option.value === status,
        );

      // Create activity for status rollback
      await createActivity({
        type: "status",
        action: "Order status rolled back",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} status rolled back from ${
          oldStatus.text
        } to ${newStatus.text}${reason ? ` - Reason: ${reason}` : ""}`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "status",
          oldValue: {
            text: oldStatus.text,
            value: oldStatus.value,
            color: oldStatus.color,
          },
          newValue: {
            text: newStatus.text,
            value: newStatus.value,
            color: newStatus.color,
          },
          reason: reason,
          orderNumber: this.order.orderNumber,
        },
      });

      addBreadcrumb("previousAction: committing batch", {
        workflow: "sales-order",
        data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
      });
      await batch.commit();
    } catch (error) {
      throw error;
    }
  }

  // [COMPLETE]
  async completeAction() {
    addBreadcrumb("completeAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });
    console.log("completeAction");
    await this.getOrder();
    await this.getLineItems();

    try {
      const batch = writeBatch(firestore);
      const orderDoc = doc(firestore, "salesOrders", this.order.id);
      console.log(this.order);
      const oldValue = store.getters.getOrderStatus.find(
          (status) => status.value === this.order.status,
        ),
        newValue = store.getters.getOrderStatus.find(
          (status) => status.value === "complete",
        );

      console.log(oldValue, newValue);

      // Update order
      batch.set(
        orderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          status: newValue.value,
        },
        { merge: true },
      );

      // Create activity for order acceptance
      await createActivity({
        type: "status",
        action: "Order completed",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} status updated to complete`,
        relatedTo: "salesOrders",
        relatedID: [orderDoc.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "completionStatus",
          oldValue: {
            text: oldValue.text,
            value: oldValue.value,
            color: oldValue.color,
          },
          newValue: {
            text: newValue.text,
            value: newValue.value,
            color: newValue.color,
          },
          orderNumber: this.order.orderNumber.internal,
          externalOrderUpdated:
            this.externalUpdates.leaflink.includes("statusUpdated"),
        },
      });

      // Try MyTrace operations first
      addBreadcrumb("completeAction: committing batch", {
        workflow: "sales-order",
        data: { orderId: this.orderID, balance: this.order?.balance },
      });
      await batch.commit();

      console.log("this.orderIsExternal", this.orderIsExternal);

      // External - Update Status (only after MyTrace commit succeeds)
      if (this.orderIsExternal) {
        try {
          switch (this.externalOrderType) {
            case "leaflink":
              if (this.externalUpdates.leaflink.includes("statusUpdated")) {
                let orderData = await this.leaflinkClient.getOrder();
                if (orderData?.status.toLowerCase() === "shipped") {
                  await this.leaflinkClient.transitionOrder("complete");
                }
              }
              break;
            default:
              break;
          }
        } catch (e) {
          console.log("Leaflink transition failed:", e);
          // Note: MyTrace has already been committed successfully at this point
          // Consider adding rollback logic or notification here if needed
          logWorkflowWarning(`LeafLink: transitionOrder (completeAction) failed — ${e.message}`, {
            workflow: "sales-order",
            action: "leaflink-complete-transition",
            context: { orderId: this.orderID, balance: this.order?.balance },
          });
          throw e;
        }
      }
    } catch (error) {
      throw error;
    }
  }

  // [SHIPPED]
  async rescheduleShippedAction(reason, shipDate = null) {
    addBreadcrumb("rescheduleShippedAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });
    console.log("rescheduleShippedAction");
    await this.getOrder();
    await this.getLineItems();

    if (this.order?.status !== "shipped") {
      return;
    }

    let updates = [];

    const batch = writeBatch(firestore);
    const salesOrderDoc = doc(firestore, "salesOrders", this.orderID);

    // Order
    batch.set(
      salesOrderDoc,
      {
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        rescheduled: increment(1),
        status: "deliveryConfirmation",
        shipDate: null,
      },
      { merge: true },
    );

    const stateDoc = doc(
      firestore,
      "states",
      this.order.location.shipping.state.stateID,
    );
    const stateResponse = await getDoc(stateDoc);
    let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

    if (this.order.fulfillmentLocation) {
      fulfillmentLocation = this.order.fulfillmentLocation.id;
    }

    const locationRef = doc(
      firestore,
      "locationManufacturing",
      `${fulfillmentLocation}_${this.order.brand.id}`,
    );

    this.lineItems.forEach((item) => {
      batch.set(
        locationRef,
        {
          manufacturing: {
            [item.categoryID]: {
              available: {
                [item.productID]: increment(item.qty),
              },
              reserved: {
                [item.productID]: increment(item.qty),
              },
            },
          },
        },
        { merge: true },
      );
    });

    // External - Ship date

    if (this.orderIsExternal) {
      switch (this.externalOrderType) {
        case "leaflink":
          // let shipDateMoment = moment(shipDate);
          // let shipDate = moment(shipDateMoment.clone().format("MM/DD/YYYY"))
          //   .add(9, "hours")
          //   .tz("America/Los_Angeles");
          let patchData = {
            ship_date: null,
          };
          if (this.externalUpdates.leaflink.includes("orderUpdated")) {
            addBreadcrumb("leaflink: updateOrderData ship_date null (reschedule)", {
              workflow: "sales-order",
              data: { orderId: this.orderID, orderStatus: this.order?.status },
            });
            try {
              await this.leaflinkClient.updateOrderData(patchData);
            } catch (error) {
              logWorkflowWarning(`LeafLink: updateOrderData (reschedule) failed — ${error.message}`, {
                workflow: "sales-order",
                action: "leaflink-reschedule-update-order",
                context: { orderId: this.orderID, balance: this.order?.balance },
              });
              throw error;
            }
          }
          if (this.externalUpdates.leaflink.includes("statusUpdated")) {
            addBreadcrumb("leaflink: transitionOrder accept (reschedule)", {
              workflow: "sales-order",
              data: { orderId: this.orderID, orderStatus: this.order?.status },
            });
            try {
              await this.leaflinkClient.transitionOrder("accept");
            } catch (error) {
              logWorkflowWarning(`LeafLink: transitionOrder("accept") (reschedule) failed — ${error.message}`, {
                workflow: "sales-order",
                action: "leaflink-reschedule-transition-accept",
                context: { orderId: this.orderID, balance: this.order?.balance },
              });
              throw error;
            }
          }
          break;
        default:
          break;
      }
    }

    // Ledger
    updates.push(`Reschedule reason: "${reason}"`);

    // Create activity for order rescheduling
    await createActivity({
      type: "logistics",
      action: "Order rescheduled",
      item: `Order rescheduled: ${updates.join(", ")}`,
      relatedTo: "salesOrders",
      relatedID: [this.order.id],
      user: {
        name: store.getters.getUser.name,
        uid: store.getters.getUser.uid,
      },
      account: {
        id: this.order.account.id,
        name: this.order.account.name,
      },
      brand: {
        id: this.order.brand.id,
        name: this.order.brand.name,
      },
      businessImpact: "medium",
      priority: "medium",
      changeData: {
        field: "shipDate",
        oldValue: this.order.shipDate,
        newValue: null,
        reason: reason,
        from: "shipped",
        to: "deliveryConfirmation",
        rescheduleCount: this.order.rescheduled
          ? this.order.rescheduled + 1
          : 1,
        inventoryReturned: this.lineItems.reduce(
          (sum, item) => sum + item.qty,
          0,
        ),
      },
    });

    addBreadcrumb("rescheduleShippedAction: committing batch", {
      workflow: "sales-order",
      data: { orderId: this.orderID, balance: this.order?.balance },
    });
    await batch.commit();
  }

  async failedShippedAction(reason, notifyUser = null, shipDate = null) {
    addBreadcrumb("failedShippedAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });
    console.log("failedShippedAction");
    await this.getOrder();
    await this.getLineItems();

    let updates = [];
    let locationBeforeDataForActivity = null;

    const batch = writeBatch(firestore);
    const salesOrderDoc = doc(firestore, "salesOrders", this.orderID);

    // Order
    batch.set(
      salesOrderDoc,
      {
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        rescheduled: increment(1),
        // shipDate: null, (dont write new ship date)
      },
      { merge: true },
    );

    const stateDoc = doc(
      firestore,
      "states",
      this.order.location.shipping.state.stateID,
    );
    const stateResponse = await getDoc(stateDoc);
    let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

    if (this.order.fulfillmentLocation) {
      fulfillmentLocation = this.order.fulfillmentLocation.id;
    }

    const locationRef = doc(
      firestore,
      "locationManufacturing",
      `${fulfillmentLocation}_${this.order.brand.id}`,
    );

    // Read current state for inventory activity recording (before batch changes)
    const locSnap = await getDoc(locationRef);
    locationBeforeDataForActivity = locSnap.exists() ? locSnap.data() : null;

    this.lineItems.forEach((item) => {
      batch.set(
        locationRef,
        {
          manufacturing: {
            [item.categoryID]: {
              available: {
                [item.productID]: increment(item.qty),
              },
              reserved: {
                [item.productID]: increment(item.qty),
              },
            },
          },
        },
        { merge: true },
      );
    });

    // Create notification if user is selected
    if (notifyUser) {
      const notificationRef = doc(collection(firestore, "notifications"));
      batch.set(notificationRef, {
        created: serverTimestamp(),
        updated: serverTimestamp(),
        assignedTo: {
          name: notifyUser.name,
          uid: notifyUser.id,
        },
        createdBy: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        notes: `This order needs to be reviewed. Reason: ${reason}`,
        relatedTo: {
          id: this.orderID,
          resource: "salesOrder",
          name: `Order #${this.order.orderNumber.internal.toUpperCase()}`,
          type: "sales order",
        },
        viewed: false,
      });
    }

    // External - Ship date
    if (this.orderIsExternal) {
      switch (this.externalOrderType) {
        case "leaflink":
          let patchData = {
            ship_date: null,
          };
          if (this.externalUpdates.leaflink.includes("orderUpdated")) {
            addBreadcrumb("leaflink: updateOrderData ship_date null (failedShipped)", {
              workflow: "sales-order",
              data: { orderId: this.orderID, orderStatus: this.order?.status },
            });
            try {
              await this.leaflinkClient.updateOrderData(patchData);
            } catch (error) {
              logWorkflowWarning(`LeafLink: updateOrderData (failedShipped) failed — ${error.message}`, {
                workflow: "sales-order",
                action: "leaflink-failed-shipped-update-order",
                context: { orderId: this.orderID, balance: this.order?.balance },
              });
              throw error;
            }
          }
          if (this.externalUpdates.leaflink.includes("statusUpdated")) {
            addBreadcrumb("leaflink: transitionOrder accept (failedShipped)", {
              workflow: "sales-order",
              data: { orderId: this.orderID, orderStatus: this.order?.status },
            });
            try {
              await this.leaflinkClient.transitionOrder("accept");
            } catch (error) {
              logWorkflowWarning(`LeafLink: transitionOrder("accept") (failedShipped) failed — ${error.message}`, {
                workflow: "sales-order",
                action: "leaflink-failed-shipped-transition-accept",
                context: { orderId: this.orderID, balance: this.order?.balance },
              });
              throw error;
            }
          }
          break;
        default:
          break;
      }
    }

    // Ledger
    updates.push(`Reschedule reason: "${reason}"`);

    // Create activity for order rescheduling
    await createActivity({
      type: "logistics",
      action: "Order rescheduled",
      item: `Order rescheduled: ${updates.join(", ")}`,
      relatedTo: "salesOrders",
      relatedID: [this.order.id],
      user: {
        name: store.getters.getUser.name,
        uid: store.getters.getUser.uid,
      },
      account: {
        id: this.order.account.id,
        name: this.order.account.name,
      },
      brand: {
        id: this.order.brand.id,
        name: this.order.brand.name,
      },
      businessImpact: "medium",
      priority: "medium",
      changeData: {
        field: "shipDate",
        oldValue: this.order.shipDate,
        newValue: null,
        reason: reason,
        from: "shipped",
        to: "shipped",
        rescheduleCount: this.order.rescheduled
          ? this.order.rescheduled + 1
          : 1,
        inventoryReturned: this.lineItems.reduce(
          (sum, item) => sum + item.qty,
          0,
        ),
        notifiedUser: notifyUser
          ? {
              name: notifyUser.fullName,
              id: notifyUser.id,
            }
          : null,
      },
    });

    addBreadcrumb("failedShippedAction: committing batch", {
      workflow: "sales-order",
      data: { orderId: this.orderID, balance: this.order?.balance },
    });
    await batch.commit();

    // Record inventory activities for report (order rescheduled from shipped – inventory returned and re-reserved)
    const locationId =
      this.order.fulfillmentLocation?.id ?? fulfillmentLocation;
    const locationName =
      this.order.fulfillmentLocation?.name ??
      this.order.location?.shipping?.name ??
      "";
    if (locationId && locationBeforeDataForActivity != null) {
      for (const item of this.lineItems) {
        const mfg =
          locationBeforeDataForActivity.manufacturing?.[item.categoryID];
        const before = {
          available: mfg?.available?.[item.productID] ?? 0,
          reserved: mfg?.reserved?.[item.productID] ?? 0,
          transfer: mfg?.transfer?.[item.productID] ?? 0,
        };
        const after = {
          available: before.available + item.qty,
          reserved: before.reserved + item.qty,
          transfer: before.transfer,
        };
        const skuID = `${locationId}_${this.order.brand.id}_${item.categoryID}_${item.productID}`;
        try {
          await createInventoryActivity({
            action: "Inventory restored (reschedule)",
            item: `${item.qty} units of ${item.name} (${item.sku}) returned from shipped and re-reserved – Order #${this.order.orderNumber.internal} rescheduled`,
            skuID,
            itemData: {
              name: item.name,
              sku: item.sku,
              categoryID: item.categoryID,
              productID: item.productID,
              categoryName: item.categoryName || item.category,
            },
            movement: {
              type: "restoration",
              from: "fulfilled",
              to: "reserved",
              quantity: item.qty,
              reason: "orderRescheduled",
              reasonID: this.order.id,
            },
            snapshot: { before, after },
            location: { id: locationId, name: locationName },
            order: this.order,
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            extraSalesOrderData: { orderID: this.orderID },
          });
        } catch (activityErr) {
          console.error(
            "createInventoryActivity (order rescheduled from shipped):",
            activityErr,
            { skuID, productName: item.name, orderId: this.order.id },
          );
          logWorkflowWarning(`rescheduleShippedAction: inventory activity creation failed — ${activityErr.message}`, {
            workflow: "sales-order",
            action: "reschedule-shipped-inventory-activity",
            context: { orderId: this.orderID, productName: item.name, skuID },
          });
        }
      }
    }
  }

  async shippedAction(lineItems, shipDate = null) {
    addBreadcrumb("shippedAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });
    console.log("shippedAction");

    await this.getOrder();
    await this.getLineItems();
    await this.getPayments();

    if (this.order?.status === "shipped") {
      return;
    }

    if (lineItems.length === 0) {
      throw new Error(
        "No line items were provided. Please refresh the page and try again."
      );
    }

    // Atomic claim: only one concurrent request can transition to shipped.
    // If two users click at the exact same time, the transaction ensures only one proceeds.
    const salesOrderDocRef = doc(firestore, "salesOrders", this.order.id);
    const applied = await runTransaction(firestore, async (transaction) => {
      const orderSnap = await transaction.get(salesOrderDocRef);
      if (!orderSnap.exists() || orderSnap.data().status === "shipped") {
        return false;
      }
      transaction.update(salesOrderDocRef, {
        status: "shipped",
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
      });
      return true;
    });

    if (!applied) {
      return;
    }

    try {
      let updates = [];
      let locationBeforeDataForActivity = null;

      const orderSubmissionEmail = httpsCallable(functions, "sendgridEmail");

      const batch = writeBatch(firestore);
      const salesOrderDoc = salesOrderDocRef;
      const deliveredItems = lineItems.filter((item) => item.delivered);
      const rejectedItems = lineItems.filter((item) => !item.delivered);

      const stateDoc = doc(
        firestore,
        "states",
        this.order.location.shipping.state.stateID,
      );
      const stateResponse = await getDoc(stateDoc);
      let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

      if (this.order.fulfillmentLocation) {
        fulfillmentLocation = this.order.fulfillmentLocation.id;
      }

      const locationRef = doc(
        firestore,
        "locationManufacturing",
        `${fulfillmentLocation}_${this.order.brand.id}`,
      );
      let payableToResponse = await getDocs(
        query(
          collection(firestore, "payableTo"),
          where("active", "==", true),
          where(
            "location.state.stateID",
            "==",
            this.order.location.shipping.state.stateID,
          ),
          where("brandID", "==", this.order.brand.id),
        ),
      );
      let payableTo = null;
      if (!payableToResponse.empty) {
        payableTo = {
          id: payableToResponse.docs[0].id,
          ...payableToResponse.docs[0].data(),
        };
      }

      // Line Items
      if (deliveredItems.length > 0) {
        deliveredItems.forEach((item) => {
          const lineItemDoc = doc(firestore, "salesOrders_lineItems", item.id);

          batch.set(
            lineItemDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              flags: {
                delivered: true,
              },
            },
            { merge: true },
          );
        });
      }
      if (rejectedItems.length > 0) {
        let removed = [];

        // External - Update Status to accepted temporarily
        //
        //  - Leaflink API doesn't allow rejection of line items while the order status is `fulfilled`
        //     so we have to transition the status back to accepted, delete the line items from the order,
        //     and then transition the status back to fulfilled, then finally transition to shipped.
        //
        //  This is necessary to keep the line items/inventory on Leaflink consistent
        // NOTE: Leaflink operations moved to after batch commit for data consistency

        const stateDoc = doc(
          firestore,
          "states",
          this.order.location.shipping.state.stateID,
        );
        const stateResponse = await getDoc(stateDoc);
        // Assign to outer fulfillmentLocation so activity recording after batch.commit() uses same value
        fulfillmentLocation =
          stateResponse.data()?.fulfillmentLocation ?? fulfillmentLocation;
        if (this.order.fulfillmentLocation) {
          fulfillmentLocation = this.order.fulfillmentLocation.id;
        }

        const locationRef = doc(
          firestore,
          "locationManufacturing",
          `${fulfillmentLocation}_${this.order.brand.id}`,
        );

        // Read current state for activity recording only (does not affect core flow)
        const locSnap = await getDoc(locationRef);
        locationBeforeDataForActivity = locSnap.exists()
          ? locSnap.data()
          : null;

        const promises = rejectedItems.map(async (item) => {
          const lineItemDoc = doc(firestore, "salesOrders_lineItems", item.id);
          batch.delete(lineItemDoc);

          // Reset inventory
          batch.set(
            locationRef,
            {
              manufacturing: {
                [item.categoryID]: {
                  available: {
                    [item.productID]: increment(item.qty),
                  },
                },
              },
            },
            { merge: true },
          );

          removed.push(
            `<span class="text-capitalize">${
              item.name
            }</span> (${item.sku.toUpperCase()}) - <span class="text-uppercase">qty</span>: ${
              item.qty
            }`,
          );

          for (let i = 0; i < item.productLots?.length; i++) {
            const productLot = item.productLots[i];
            const productLotDoc = doc(firestore, "productLots", productLot.id);
            const productLotResponse = await getDoc(productLotDoc);

            if (productLotResponse.exists()) {
              const productLotData = productLotResponse.data();
              let availableUnits = productLotResponse.data().units;

              if (productLotData.hasOwnProperty("available")) {
                availableUnits =
                  Number(productLotResponse.data().available) +
                  productLot.units;
              }

              const newRelatedLineItems =
                productLotData.relatedLineItems?.filter(
                  (lineItem) => lineItem.id !== item.id,
                ) ?? [];

              await updateDoc(
                productLotDoc,
                {
                  active: availableUnits > 0,
                  available: availableUnits,
                  relatedLineItems: newRelatedLineItems,
                  updated: serverTimestamp(),
                  lastUpdated: {
                    name: store.getters.getUser.name,
                    uid: store.getters.getUser.uid,
                  },
                },
                { merge: true },
              );
            }
          }

          // NOTE: Leaflink deleteLineItem operations moved to after batch commit for data consistency
        });

        //  Need to use Promise to ensure that all items are removed before setup() runs
        await Promise.all(promises);

        //  External - Update order status back to fulfilled, then update to shipped
        // NOTE: Leaflink fulfill transition moved to after batch commit for data consistency

        updates.push(
          `<span class="text-uppercase font-weight-bold" style="color: red">-rejected</span>: ${removed.join(
            " / ",
          )}`,
        );
      }
      //  External Status Update
      // NOTE: Leaflink ship transition moved to after batch commit for data consistency

      // Recalculate order balance & QTY + Payment due date
      const units = deliveredItems.reduce((a, b) => a + b.qty, 0);
      const total = deliveredItems.reduce((a, b) => a + b.qty * b.amount, 0);

      let submittedPayments = this.payments
        .filter((item) => !item.flags.reversed || Math.sign(item.amount) === -1)
        .reduce((a, b) => a + b.amount - b.creditsApplied, 0);

      let excise = Number(this.order.balance.excise) || 0;
      let shipping = Number(this.order.balance.shipping) || 0;

      let totalDiscountValue = () => {
        let isPercentage = this.order.discount?.type === "%";
        let discountValue = this.order.discount?.value ?? 0;
        let totalValue = total;
        if (isPercentage) {
          return (discountValue / 100) * totalValue;
        } else {
          return discountValue;
        }
      };

      const balance = {
        due: parseFloat(
          (total - totalDiscountValue() - submittedPayments + shipping).toFixed(
            2,
          ),
        ),
        excise: parseFloat(excise).toFixed(2),
        shipping: parseFloat(shipping).toFixed(2),
        total: parseFloat((total - totalDiscountValue() + shipping).toFixed(2)),
      };

      let rejectedItemIDs = rejectedItems.map((item) => item.id);
      let remainingProductLots =
        this.order.productLots?.filter(
          (productLot) => !rejectedItemIDs.includes(productLot.relatedID),
        ) ?? [];

      // Order
      batch.set(
        salesOrderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          status: "shipped",
          balance,
          units,
          productLots: remainingProductLots,
          lineItems: deliveredItems
            .map((item) => {
              return {
                ...item,
                flags: {
                  ...item.flags,
                  delivered: true,
                },
              };
            })
            .sort((a, b) => (a.sku > b.sku ? 1 : -1)),
          records: {
            shipped: moment().startOf("day").add(12, "hours").toDate(),
          },
          discount: {
            value: this.order.discount?.value ?? 0,
            type: this.order.discount?.type ?? "%",
            total: totalDiscountValue(),
          },
        },
        { merge: true },
      );

      // Update Ship Date
      if (!this.order.shipDate) {
        const oldValue = "[Not Set]",
          newValue = moment(shipDate).format("MM/DD/YYYY");

        batch.set(
          salesOrderDoc,
          {
            shipDate: moment(shipDate)
              .startOf("day")
              .add(9, "hours")
              .tz("America/Los_Angeles")
              .toDate(),
          },
          { merge: true },
        );

        // Create activity for ship date set during fulfillment
        await createActivity({
          type: "logistics",
          action: "Ship date set during fulfillment",
          item: `Order #${this.order.orderNumber.internal.toUpperCase()} ship date set to ${moment(
            shipDate,
          ).format("MM/DD/YYYY")} during fulfillment process`,
          relatedTo: "salesOrders",
          relatedID: [salesOrderDoc.id],
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          account: {
            id: this.order.account.id,
            name: this.order.account.name,
          },
          brand: {
            id: this.order.brand.id,
            name: this.order.brand.name,
          },
          businessImpact: "medium",
          priority: "medium",
          changeData: {
            field: "shipDate",
            oldValue: oldValue,
            newValue: newValue,
            orderNumber: this.order.orderNumber.internal,
            updateType: "fulfillment_process",
            shipDate: moment(shipDate).toDate(),
            fulfillmentContext: "order_fulfillment",
            externalOrderUpdated:
              this.externalUpdates.leaflink.includes("orderUpdated"),
          },
        });

        // NOTE: Leaflink ship date update moved to after batch commit for data consistency
      }
      // Account Balance
      const brandStatsDoc = doc(
        firestore,
        "brandStats",
        `${this.order.account.id}_${this.order.brand.id}`,
      );
      const accountDoc = doc(firestore, "accounts", this.order.account.id);
      const brandStats = await getDoc(brandStatsDoc);
      const accountData = await getDoc(accountDoc);
      let currentAccountBalance = brandStats.data().metrics.balance;
      let currentAccountTotalSales = brandStats.data().metrics.totalSales;
      const newBalance = Number(
        parseFloat(currentAccountBalance + Number(balance.due)).toFixed(2),
      );
      const newTotalSales = Number(
        parseFloat(currentAccountTotalSales + Number(balance.total)).toFixed(2),
      );

      let brandStatsPayload = {
        metrics: {
          balance: newBalance,
          totalSales: newTotalSales,
          totalUnitsSold: increment(units),
        },
      };

      if (brandStats.data().metrics.closeDate?.order === salesOrderDoc.id) {
        // Update account's closeDate to order's ship date
        let shipDate = this.order.shipDate
          ? moment(this.order.shipDate?.seconds * 1000)
              .startOf("day")
              .add(9, "hours")
              .tz("America/Los_Angeles")
              .toDate()
          : moment(shipDate)
              .startOf("day")
              .add(9, "hours")
              .tz("America/Los_Angeles")
              .toDate();
        brandStatsPayload.metrics.closeDate = {
          date: shipDate,
        };
      }

      batch.set(brandStatsDoc, brandStatsPayload, { merge: true });

      if (
        ["lead_cold", "lead_hot", "lead_onHold"].includes(
          brandStats.data().status,
        ) ||
        (["samples_sent", "samples_received", "needs_reonboarding"].includes(
          brandStats.data().status,
        ) &&
          !this.order.flags.sample)
      ) {
        const oldValue = store.getters.getAccountStatus.find(
            (option) => option.value === brandStats.data().status,
          ),
          newValue = store.getters.getAccountStatus.find(
            (option) => option.value === "active_current",
          );

        let managedBy = null;
        if (brandStats.data().staff.manager?.id) {
          managedBy = brandStats.data().staff.manager;
        } else if (brandStats.data().staff.executive?.id) {
          managedBy = brandStats.data().staff.executive;
        } else {
          managedBy = {
            name: `${this.order.createdBy.name.first} ${this.order.createdBy.name.last}`,
            id: this.order.createdBy.uid,
            email: this.order.createdBy.email ?? "",
          };
        }

        let closer =
          this.order.createdBy.id === "storeGenerated"
            ? managedBy
            : {
                name: `${this.order.createdBy.name.first} ${this.order.createdBy.name.last}`,
                id: this.order.createdBy.uid,
                email: this.order.createdBy.email ?? "",
              };

        batch.set(
          brandStatsDoc,
          {
            status: "active_current",
            metrics: {
              closeDate: {
                date: moment(shipDate).startOf("day").add(12, "hours").toDate(),
                order: this.order.id,
              },
            },
            staff: {
              close: closer,
            },
          },
          { merge: true },
        );
        // Onboarding
        // Duplicate check
        const accountOnboardings = collection(firestore, "accountOnboardings");
        const account = where("account.id", "==", this.order.account.id),
          brand = where("brand.id", "==", this.order.brand.id);

        const q = query(accountOnboardings, account, brand);
        const response = await getDocs(q);
        if (!response.empty) {
          // Onboarding already exists
        } else {
          // Onboarding doesn't exist
          const onboardingDoc = doc(
            collection(firestore, "accountOnboardings"),
          );
          const account = {
            text: this.order.account.name,
            value: this.order.account.id,
            staff: brandStats.data().staff,
            location: this.order.location.shipping,
            flags: accountData.data().flags,
          };
          const brand = {
            text: this.order.brand.name,
            value: this.order.brand.id,
          };

          const trainingTask = doc(collection(firestore, "tasks"));
          const sampleOrderTask = doc(collection(firestore, "tasks"));
          const storeEventTask = doc(collection(firestore, "tasks"));
          const introEmailTask = doc(collection(firestore, "tasks"));
          const logoTask = doc(collection(firestore, "tasks"));

          batch.set(onboardingDoc, {
            created: serverTimestamp(),
            updated: serverTimestamp(),
            createdBy: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            flags: {
              complete: false,
              budtenderTraining: false,
              sampleOrder: false,
              storeEvent: false,
              introEmail: false,
              logo: account.flags.logo,
            },
            externalIDs: {},
            account: {
              name: account.text,
              id: account.value,
              staff: account.staff,
            },
            brand: {
              name: brand.text,
              id: brand.value,
            },
            records: {
              budtenderTraining: trainingTask.id,
              storeEvent: storeEventTask.id,
              sampleOrder: sampleOrderTask.id,
              introEmail: introEmailTask.id,
              logo: account.flags.logo === false ? logoTask.id : "",
            },
            location: account.location,
            metrics: {
              complete: null,
              budtenderTraining: null,
              sampleOrder: null,
              storeEvent: null,
              introEmail: null,
              logo: account.flags.logo ? serverTimestamp() : null,
            },
            notes: "",
          });

          let staff = [];
          let managedBy = null;
          if (brandStats.data().staff.manager?.id?.length > 0) {
            staff.push(brandStats.data().staff.manager.id);
            managedBy = brandStats.data().staff.manager;
          }
          if (brandStats.data().staff.executive?.id?.length > 0) {
            staff.push(brandStats.data().staff.executive.id);
            managedBy = brandStats.data().staff.executive;
          }

          let users = [store.getters.getUser.uid, ...staff];
          users = [...new Set(users)]; //  Removes duplicates
          batch.set(
            trainingTask,
            {
              created: serverTimestamp(),
              updated: serverTimestamp(),
              dueDate: moment().add(3, "days").toDate(),
              completedDate: null,
              createdBy: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              completedBy: null,
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              flags: {
                complete: null,
                system: true,
              },
              status: "notStarted",
              priority: "normal",
              notes: `Schedule Budtender Training for ${capitalize(
                account.text,
              )} ${capitalize(brand.text)}'s onboarding`,
              type: "other",
              relatedTo: {
                name: `${capitalize(account.text)} - Onboarding`,
                id: onboardingDoc.id,
                resource: "accountOnboardings",
              },
              relatedContact: null,
              assignedTo: {
                name: {
                  first: managedBy.name.split(" ")[0],
                  last: managedBy.name.split(" ")[1],
                },
                uid: managedBy.id,
              },
              externalIDs: {},
              users,
            },
            { merge: true },
          );

          // Create activity for budtender training task creation
          await createActivity({
            type: "onboarding",
            action: "Budtender training task created",
            item: `Budtender training task created for ${account.text} ${
              brand.text
            } onboarding - Assigned to ${managedBy.name}, Due: ${moment()
              .add(3, "days")
              .format("MM/DD/YYYY")}`,
            relatedTo: "accountOnboardings",
            relatedID: [onboardingDoc.id],
            changeData: {
              field: "onboardingTask",
              taskType: "budtenderTraining",
              taskId: trainingTask.id,
              accountName: account.text,
              brandName: brand.text,
              assignedTo: managedBy.name,
              dueDate: moment().add(3, "days").toDate(),
              priority: "normal",
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });

          batch.set(
            sampleOrderTask,
            {
              created: serverTimestamp(),
              updated: serverTimestamp(),
              dueDate: moment().add(3, "days").toDate(),
              completedDate: null,
              createdBy: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              completedBy: null,
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              flags: {
                complete: null,
                system: true,
              },
              status: "notStarted",
              priority: "normal",
              notes: `Submit Sample Order for ${capitalize(
                account.text,
              )} ${capitalize(brand.text)}'s onboarding`,
              type: "other",
              relatedTo: {
                name: `${capitalize(account.text)} - Onboarding`,
                id: onboardingDoc.id,
                resource: "accountOnboardings",
              },
              relatedContact: null,
              assignedTo: {
                name: {
                  first: managedBy.name.split(" ")[0],
                  last: managedBy.name.split(" ")[1],
                },
                uid: managedBy.id,
              },
              externalIDs: {},
              users,
            },
            { merge: true },
          );

          // Create activity for sample order task creation
          await createActivity({
            type: "onboarding",
            action: "Sample order task created",
            item: `Sample order task created for ${account.text} ${
              brand.text
            } onboarding - Assigned to ${managedBy.name}, Due: ${moment()
              .add(3, "days")
              .format("MM/DD/YYYY")}`,
            relatedTo: "accountOnboardings",
            relatedID: [onboardingDoc.id],
            changeData: {
              field: "onboardingTask",
              taskType: "sampleOrder",
              taskId: sampleOrderTask.id,
              accountName: account.text,
              brandName: brand.text,
              assignedTo: managedBy.name,
              dueDate: moment().add(3, "days").toDate(),
              priority: "normal",
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });
          //  Schedule Store Event Task

          batch.set(
            storeEventTask,
            {
              created: serverTimestamp(),
              updated: serverTimestamp(),
              dueDate: moment().add(3, "days").toDate(),
              completedDate: null,
              createdBy: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              completedBy: null,
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              flags: {
                complete: null,
                system: true,
              },
              status: "notStarted",
              priority: "normal",
              notes: `Schedule Store Event for ${capitalize(
                account.text,
              )} ${capitalize(brand.text)}'s onboarding`,
              type: "other",
              relatedTo: {
                name: `${capitalize(account.text)} - Onboarding`,
                id: onboardingDoc.id,
                resource: "accountOnboardings",
              },
              relatedContact: null,
              assignedTo: {
                name: {
                  first: managedBy.name.split(" ")[0],
                  last: managedBy.name.split(" ")[1],
                },
                uid: managedBy.id,
              },
              externalIDs: {},
              users,
            },
            { merge: true },
          );

          // Create activity for store event task creation
          await createActivity({
            type: "onboarding",
            action: "Store event task created",
            item: `Store event task created for ${account.text} ${
              brand.text
            } onboarding - Assigned to ${managedBy.name}, Due: ${moment()
              .add(3, "days")
              .format("MM/DD/YYYY")}`,
            relatedTo: "accountOnboardings",
            relatedID: [onboardingDoc.id],
            changeData: {
              field: "onboardingTask",
              taskType: "storeEvent",
              taskId: storeEventTask.id,
              accountName: account.text,
              brandName: brand.text,
              assignedTo: managedBy.name,
              dueDate: moment().add(3, "days").toDate(),
              priority: "normal",
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });
          //  Send Intro Email Task
          batch.set(
            introEmailTask,
            {
              created: serverTimestamp(),
              updated: serverTimestamp(),
              dueDate: moment().add(3, "days").toDate(),
              completedDate: null,
              createdBy: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              completedBy: null,
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              flags: {
                complete: false,
                requiresReview: false,
              },
              status: "notStarted",
              priority: "normal",
              notes: `Send Intro Email to ${capitalize(
                account.text,
              )} for ${capitalize(brand.text)}'s onboarding`,
              type: "email",
              relatedTo: {
                name: `${capitalize(account.text)} - Onboarding`,
                id: onboardingDoc.id,
                resource: "accountOnboardings",
              },
              relatedContact: null,
              assignedTo: {
                name: {
                  first: managedBy.name.split(" ")[0],
                  last: managedBy.name.split(" ")[1],
                },
                uid: managedBy.id,
              },
              externalIDs: {},
              users,
            },
            { merge: true },
          );

          // Create activity for intro email task creation
          await createActivity({
            type: "onboarding",
            action: "Intro email task created",
            item: `Intro email task created for ${account.text} ${
              brand.text
            } onboarding - Assigned to ${managedBy.name}, Due: ${moment()
              .add(3, "days")
              .format("MM/DD/YYYY")}`,
            relatedTo: "accountOnboardings",
            relatedID: [onboardingDoc.id],
            changeData: {
              field: "onboardingTask",
              taskType: "introEmail",
              taskId: introEmailTask.id,
              accountName: account.text,
              brandName: brand.text,
              assignedTo: managedBy.name,
              dueDate: moment().add(3, "days").toDate(),
              priority: "normal",
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });
          //  Upload Logo Task
          batch.set(
            logoTask,
            {
              created: serverTimestamp(),
              updated: serverTimestamp(),
              dueDate: moment().add(3, "days").toDate(),
              completedDate:
                account.flags.logo === true ? serverTimestamp() : null,
              createdBy: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              completedBy: {
                name: account.flags.logo ? store.getters.getUser.name : null,
                id: account.flags.logo ? store.getters.getUser.uid : null,
              },
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              flags: {
                complete: account.flags.logo ? true : null,
                requiresReview: false,
              },
              status: account.flags.logo ? "complete" : "notStarted",
              priority: "normal",
              notes: `Upload logo for ${capitalize(account.text)}`,
              type: "other",
              relatedTo: {
                name: `${capitalize(account.text)} - Onboarding`,
                id: onboardingDoc.id,
                resource: "accountOnboardings",
              },
              relatedContact: null,
              assignedTo: {
                name: {
                  first: managedBy.name.split(" ")[0],
                  last: managedBy.name.split(" ")[1],
                },
                uid: managedBy.id,
              },
              externalIDs: {},
              users,
            },
            { merge: true },
          );

          // Create activity for logo upload task creation
          await createActivity({
            type: "onboarding",
            action: "Logo upload task created",
            item: `Logo upload task created for ${account.text} ${
              brand.text
            } onboarding - Assigned to ${managedBy.name}, Due: ${moment()
              .add(3, "days")
              .format("MM/DD/YYYY")}${
              account.flags.logo ? " (Already completed)" : ""
            }`,
            relatedTo: "accountOnboardings",
            relatedID: [onboardingDoc.id],
            changeData: {
              field: "onboardingTask",
              taskType: "logoUpload",
              taskId: logoTask.id,
              accountName: account.text,
              brandName: brand.text,
              assignedTo: managedBy.name,
              dueDate: moment().add(3, "days").toDate(),
              priority: "normal",
              alreadyComplete: account.flags.logo,
              taskStatus: account.flags.logo ? "complete" : "notStarted",
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });

          // Ledger
          let appendActions = [];
          // if (this.payload.onboarding.logo) {
          //   appendActions.push(`Logo Uploaded`);
          // }

          // Create activity for account onboarding creation
          await createActivity({
            type: "onboarding",
            action: "Account onboarding created",
            item: `Account onboarding created for ${account.text} ${
              brand.text
            } - Assigned to ${managedBy.name}, Due: ${moment()
              .add(3, "days")
              .format("MM/DD/YYYY")} - 5 tasks created`,
            relatedTo: "accountOnboardings",
            relatedID: [onboardingDoc.id],
            changeData: {
              field: "accountOnboarding",
              accountName: account.text,
              brandName: brand.text,
              tasksCreated: {
                budtenderTraining: trainingTask.id,
                sampleOrder: sampleOrderTask.id,
                storeEvent: storeEventTask.id,
                introEmail: introEmailTask.id,
                logoUpload: account.flags.logo === false ? logoTask.id : null,
              },
              flags: {
                complete: false,
                budtenderTraining: false,
                sampleOrder: false,
                storeEvent: false,
                introEmail: false,
                logo: account.flags.logo,
              },
              dueDate: moment().add(3, "days").toDate(),
              assignedTo: managedBy.name,
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });
        }
      } else if (brandStats.data().status === "samples_sent") {
        const oldValue = store.getters.getAccountStatus.find(
            (option) => option.value === "samples_sent",
          ),
          newValue = store.getters.getAccountStatus.find(
            (option) => option.value === "samples_received",
          );

        batch.set(
          brandStatsDoc,
          {
            status: "samples_received",
            samplesReceivedDate: moment(this.order.shipDate?.seconds * 1000)
              .startOf("day")
              .add(12, "hours")
              .toDate(),
          },
          { merge: true },
        );

        // Create activity for samples received status update
        await createActivity({
          type: "sales",
          action: "Sample status updated",
          item: `${this.order.brand.name} sample status updated from ${oldValue.text} to ${newValue.text} for ${this.order.account.name} - Order #${this.order.orderNumber.internal}`,
          relatedTo: "accounts",
          relatedID: [this.order.account.id],
          changeData: {
            field: "sampleStatus",
            oldValue: {
              text: oldValue.text,
              value: oldValue.value,
              color: oldValue.color,
            },
            newValue: {
              text: newValue.text,
              value: newValue.value,
              color: newValue.color,
            },
            orderNumber: this.order.orderNumber.internal,
            brandName: this.order.brand.name,
            samplesReceivedDate: moment(this.order.shipDate?.seconds * 1000)
              .startOf("day")
              .add(12, "hours")
              .toDate(),
            followUpTaskCreated: true,
          },
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
        });
        const taskDoc = doc(collection(firestore, "tasks"));

        let assignedTo = null;
        if (brandStats.data().staff.manager.id?.length > 0) {
          assignedTo = {
            name: {
              first: brandStats.data().staff.manager.name.split(" ")[0],
              last: brandStats.data().staff.manager.name.split(" ")[1],
            },
            uid: brandStats.data().staff.manager.id,
          };
        }
        if (brandStats.data().staff.executive.id?.length > 0) {
          assignedTo = {
            name: {
              first: brandStats.data().staff.executive.name.split(" ")[0],
              last: brandStats.data().staff.executive.name.split(" ")[1],
            },
            uid: brandStats.data().staff.executive.id,
          };
        }
        batch.set(
          taskDoc,
          {
            created: serverTimestamp(),
            updated: serverTimestamp(),
            dueDate: moment().add(1, "weeks").endOf("day").toDate(),
            completedDate: null,
            createdBy: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            flags: {
              complete: false,
              system: true,
            },
            type: "orders",
            status: "notStarted",
            priority: "normal",
            notes: `Follow up about samples on Order ${this.order.orderNumber.internal}`,
            relatedTo: {
              name: this.order.account.name,
              id: this.order.account.id,
              resource: "accounts",
            },
            contact: null,
            assignedTo,
            externalIDs: {},
            users: [store.getters.getUser.uid],
          },
          { merge: true },
        );

        // Create activity for follow-up task creation
        await createActivity({
          type: "task",
          action: "Follow-up task created",
          item: `Follow-up task created for samples on Order #${
            this.order.orderNumber.internal
          } - ${this.order.account.name} - Assigned to ${
            assignedTo
              ? `${assignedTo.name.first} ${assignedTo.name.last}`
              : "Unassigned"
          }, Due: ${moment()
            .add(1, "weeks")
            .endOf("day")
            .format("MM/DD/YYYY")}`,
          relatedTo: "tasks",
          relatedID: [taskDoc.id],
          changeData: {
            field: "taskCreation",
            taskType: "sampleFollowUp",
            relatedOrder: this.order.orderNumber.internal,
            accountName: this.order.account.name,
            accountId: this.order.account.id,
            assignedTo: assignedTo
              ? `${assignedTo.name.first} ${assignedTo.name.last}`
              : "Unassigned",
            dueDate: moment().add(1, "weeks").endOf("day").toDate(),
            priority: "normal",
            autoGenerated: true,
          },
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
        });
      }
      // External - Status

      // Ledger
      const oldStatus = store.getters.getOrderStatus.find(
          (option) => option.value === this.order.status,
        ),
        newStatus = store.getters.getOrderStatus.find(
          (option) => option.value === "shipped",
        );
      updates.push(
        `<span class="text-capitalize">status</span> - <span class="text-uppercase">old</span>: <span class="font-weight-bold" style="color: ${oldStatus.color};">${oldStatus.text}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold" style="color: ${newStatus.color};">${newStatus.text}</span>`,
      );
      updates.push(
        `<span class="text-capitalize">balance due</span>: <span class="font-weight-bold">${new Intl.NumberFormat(
          "en-US",
          {
            style: "currency",
            currency: "USD",
          },
        ).format(balance.due)}</span>`,
      );

      if (Number(balance.due) <= 0) {
        // Status
        let status = "complete";
        let paid = true;
        const oldStatus = store.getters.getOrderStatus.find(
            (option) => option.value === "shipped",
          ),
          newStatus = store.getters.getOrderStatus.find(
            (option) => option.value === "complete",
          );

        // Order
        batch.set(
          salesOrderDoc,
          {
            updated: serverTimestamp(),
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            status,
            balance,
            flags: {
              paid,
            },
            records: {
              completed: moment().startOf("day").add(12, "hours").toDate(),
            },
          },
          { merge: true },
        );

        //  External Order Status
        // NOTE: Leaflink complete transition moved to after batch commit for data consistency

        // Create activity for order completion
        await createActivity({
          type: "financial",
          action: "Order completed - fully paid",
          item: `Order #${this.order.orderNumber.internal.toUpperCase()} automatically completed - zero balance remaining`,
          relatedTo: "salesOrders",
          relatedID: [salesOrderDoc.id],
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          account: {
            id: this.order.account.id,
            name: this.order.account.name,
          },
          brand: {
            id: this.order.brand.id,
            name: this.order.brand.name,
          },
          businessImpact: "high",
          priority: "high",
          changeData: {
            field: "orderCompletion",
            oldValue: {
              text: oldStatus.text,
              value: oldStatus.value,
              color: oldStatus.color,
            },
            newValue: {
              text: newStatus.text,
              value: newStatus.value,
              color: newStatus.color,
            },
            finalBalance: balance.due,
            paymentStatus: "fully_paid",
            orderNumber: this.order.orderNumber.internal,
            completionTrigger: "shipped_with_zero_balance",
            completionType: "automatic",
            externalOrderUpdated:
              this.externalUpdates.leaflink.includes("statusUpdated"),
            completionDate: new Date(),
          },
        });
      }

      // Try MyTrace operations first
      addBreadcrumb("shippedAction: committing batch", {
        workflow: "sales-order",
        data: {
          orderId: this.orderID,
          deliveredCount: deliveredItems.length,
          rejectedCount: rejectedItems.length,
          balance,
        },
      });
      await batch.commit();

      await this.checkOverpaymentCredits(balance.due);

      // Record inventory activities for rejected (not delivered) line items so they appear on the Inventory Activity Report.
      // Same place records both current and legacy (e.g. external/Leaflink) removals; report shows "Line removed (shipped)" for both.
      if (rejectedItems.length > 0) {
        const locationName =
          this.order.fulfillmentLocation?.name ??
          this.order.location?.shipping?.name ??
          "";
        const locationId =
          this.order.fulfillmentLocation?.id ??
          fulfillmentLocation ??
          this.order.location?.shipping?.id;
        const brandId = this.order.brand?.id;
        const orderNumberInternal =
          this.order.orderNumber?.internal ?? this.order.orderNumber;
        for (const item of rejectedItems) {
          let snapshot = {};
          if (locationBeforeDataForActivity != null) {
            const mfg =
              locationBeforeDataForActivity.manufacturing?.[item.categoryID];
            snapshot = {
              before: {
                available: mfg?.available?.[item.productID] ?? 0,
                reserved: mfg?.reserved?.[item.productID] ?? 0,
                transfer: mfg?.transfer?.[item.productID] ?? 0,
              },
            };
          }
          if (!locationId || !brandId) {
            console.error(
              "createInventoryActivity (line removed at ship): missing location or brand, skipping activity",
              { locationId, brandId, productName: item.name, orderId: this.order.id },
            );
            logWorkflowWarning("shippedAction: inventory activity skipped — missing locationId or brandId", {
              workflow: "sales-order",
              action: "shipped-inventory-activity",
              context: { orderId: this.orderID, locationId, brandId, productName: item.name },
            });
            continue;
          }
          const skuID = `${locationId}_${brandId}_${item.categoryID}_${item.productID}`;
          const itemText = `${item.qty} units of ${item.name} (${item.sku}) returned to available – not delivered at ship for Order #${orderNumberInternal}`;
          const reason = this.orderIsExternal
            ? "lineItemRemoved"
            : "salesOrderLineRemovedShipped";
          try {
            await createInventoryActivity({
              action: "Inventory released",
              item: itemText,
              skuID,
              itemData: {
                name: item.name,
                sku: item.sku,
                categoryID: item.categoryID,
                productID: item.productID,
                categoryName: item.categoryName || item.category,
              },
              movement: {
                type: "release",
                from: "reserved",
                to: "available",
                quantity: item.qty,
                reason,
                reasonID: this.order.id,
              },
              snapshot,
              location: { id: locationId, name: locationName },
              order: this.order,
              user: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              extraSalesOrderData: { orderID: this.orderID },
            });
          } catch (activityErr) {
            console.error(
              "createInventoryActivity (line removed at ship):",
              activityErr,
              { skuID, productName: item.name, orderId: this.order.id },
            );
            logWorkflowWarning(`shippedAction: createInventoryActivity failed for rejected item — ${activityErr.message}`, {
              workflow: "sales-order",
              action: "shipped-inventory-activity",
              context: { orderId: this.orderID, skuID, productName: item.name },
            });
            // Don't throw – order is already shipped; report row may be missing for this SKU
          }
        }
      }

      // Create activity for order shipping
      const deliveredCount = deliveredItems.length;
      const rejectedCount = rejectedItems.length;
      const totalItems = deliveredCount + rejectedCount;

      let shippingDetails = [];
      if (deliveredCount > 0) {
        shippingDetails.push(
          `${deliveredCount} item${deliveredCount > 1 ? "s" : ""} delivered`,
        );
      }
      if (rejectedCount > 0) {
        shippingDetails.push(
          `${rejectedCount} item${
            rejectedCount > 1 ? "s" : ""
          } rejected and inventory returned`,
        );
      }

      await createActivity({
        type: "logistics",
        action: "Order shipped",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} shipped - ${shippingDetails.join(
          ", ",
        )} - Balance due: ${new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(balance.due)}`,
        relatedTo: "salesOrders",
        relatedID: [salesOrderDoc.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "orderShipping",
          oldValue: {
            text: oldStatus.text,
            value: oldStatus.value,
            color: oldStatus.color,
          },
          newValue: {
            text: newStatus.text,
            value: newStatus.value,
            color: newStatus.color,
          },
          shippingSummary: {
            totalItems,
            deliveredItems: deliveredCount,
            rejectedItems: rejectedCount,
            deliveredItemsList: deliveredItems.map((item) => ({
              name: item.name,
              qty: item.qty,
              sku: item.sku,
            })),
            rejectedItemsList: rejectedItems.map((item) => ({
              name: item.name,
              qty: item.qty,
              sku: item.sku,
            })),
          },
          balanceDue: balance.due,
          balanceDueFormatted: new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(balance.due),
          orderNumber: this.order.orderNumber.internal,
          shipDate: shipDate || new Date(),
          inventoryUpdated: rejectedCount > 0,
          externalOrderUpdated:
            this.externalUpdates.leaflink.includes("statusUpdated"),
        },
      });

      // Add status update activity for shipped status
      const statusOld = store.getters.getOrderStatus.find(
        (option) => option.value === this.order.status,
      );
      const statusNew = store.getters.getOrderStatus.find(
        (option) => option.value === "shipped",
      );
      await createActivity({
        type: "status",
        action: "Order status updated",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} status changed from ${
          statusOld?.text || this.order.status
        } to ${statusNew?.text || "shipped"}`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "status",
          oldValue: {
            text: statusOld?.text || this.order.status,
            value: this.order.status,
            color: statusOld?.color || "#000000",
          },
          newValue: {
            text: statusNew?.text || "shipped",
            value: "shipped",
            color: statusNew?.color || "#000000",
          },
          orderNumber: this.order.orderNumber.internal,
          fulfillmentContext: "order_shipping",
          externalOrderUpdated:
            this.externalUpdates.leaflink.includes("statusUpdated"),
        },
      });

      // External - All Leaflink operations (only after MyTrace commit succeeds)
      if (this.orderIsExternal) {
        try {
          switch (this.externalOrderType) {
            case "leaflink":
              // 1. If there are rejected items, transition to accept first
              if (rejectedItems.length > 0) {
                if (
                  this.externalUpdates.leaflink.includes("statusUpdated") &&
                  this.externalUpdates.leaflink.includes("orderUpdated")
                ) {
                  await this.leaflinkClient.transitionOrder("accept");
                }
                console.log("transitioned to accepted");

                // 2. Delete rejected line items from Leaflink
                for (const item of rejectedItems) {
                  if (this.externalUpdates.leaflink.includes("orderUpdated")) {
                    let externalItem = null;
                    if (item.externalIDs?.leaflinkLineItemID) {
                      externalItem = {
                        id: item.externalIDs.leaflinkLineItemID,
                      };
                    } else {
                      if (this.leaflinkClient.lineItems?.length === 0) {
                        await this.leaflinkClient.getLineItems();
                      }
                      externalItem = this.leaflinkClient.lineItems.find(
                        (external) =>
                          external.productID === item.externalIDs.leaflink,
                      );
                    }
                    if (externalItem) {
                      await this.leaflinkClient.deleteLineItem(externalItem);
                    }
                  }
                }

                // 3. Transition back to fulfilled
                if (
                  this.externalUpdates.leaflink.includes("statusUpdated") &&
                  this.externalUpdates.leaflink.includes("orderUpdated")
                ) {
                  await this.leaflinkClient.transitionOrder("fulfill");
                }
                console.log("transitioned to fulfilled");
              }

              // 4. Transition to shipped
              if (this.externalUpdates.leaflink.includes("statusUpdated")) {
                await this.leaflinkClient.transitionOrder("ship");
              }
              console.log("transitioned to shipped");

              // 5. Update ship date if needed
              if (!this.order.shipDate) {
                let shipDateMoment = moment(shipDate);
                let leaflinkShipDate = moment(
                  shipDateMoment.clone().format("MM/DD/YYYY"),
                )
                  .add(9, "hours")
                  .tz("America/Los_Angeles");
                let patchData = {
                  ship_date: moment(leaflinkShipDate).toISOString(),
                };
                if (this.externalUpdates.leaflink.includes("orderUpdated")) {
                  await this.leaflinkClient.updateOrderData(patchData);
                }
              }

              // 6. If order is complete (balance due <= 0), transition to complete
              if (Number(balance.due) <= 0) {
                if (this.externalUpdates.leaflink.includes("statusUpdated")) {
                  await this.leaflinkClient.transitionOrder("complete");
                }
                console.log("transitioned to completed");
              }
              break;
            default:
              break;
          }
        } catch (e) {
          console.log("Leaflink operations failed:", e);
          // Note: MyTrace has already been committed successfully at this point
          // Consider adding rollback logic or notification here if needed
          logWorkflowWarning(`LeafLink: shippedAction operations failed — ${e.message}`, {
            workflow: "sales-order",
            action: "leaflink-shipped-operations",
            context: { orderId: this.orderID, balance: this.order?.balance },
          });
          throw e;
        }
      }

      let totalSaved = this.lineItems
        .filter((item) => item.amount < item.price.normal)
        .reduce((sum, item) => {
          let discountAmount = item.price.normal - item.amount;
          return sum + discountAmount * item.qty;
        }, 0);

      let totalQTY = this.lineItems.reduce((acc, item) => {
        return acc + Number(item.qty);
      }, 0);

      let orderPaymentTerm =
        store.getters.getPaymentTerms.find(
          (item) => item.value === this.order.paymentTerm,
        )?.text ?? "[Error]";

      let savingsStyle = totalSaved > 0 ? "color: #556B2F" : "color: #000000";
      let savings = `<span style="${savingsStyle}">${Intl.NumberFormat(
        "en-US",
        {
          style: "currency",
          currency: "usd",
        },
      ).format(totalSaved)}</span>`;

      if (
        payableTo.flags.orderEmails &&
        this.order.buyerInformation?.email?.length > 0
      ) {
        let cc = new Set();
        if (brandStats.data().staff.manager.email.length > 0) {
          cc.add(brandStats.data().staff.manager.email);
        }
        if (brandStats.data().staff.executive.email.length > 0) {
          cc.add(brandStats.data().staff.executive.email);
        }
        if (payableTo.staff?.inventoryManager?.uid) {
          let inventoryManagerUser = doc(
            firestore,
            "users",
            payableTo.staff?.inventoryManager?.uid,
          );
          const inventoryManagerRes = await getDoc(inventoryManagerUser);
          cc.add(inventoryManagerRes.data().email);
        }
        if (payableTo.staff?.accountManager?.uid) {
          let accountManagerUser = doc(
            firestore,
            "users",
            payableTo.staff?.accountManager?.uid,
          );
          const accountManagerRes = await getDoc(accountManagerUser);
          cc.add(accountManagerRes.data().email);
        }
        cc = Array.from(cc);

        const shippedEmailCurrency = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "usd",
        });

        const shippedEmailTableWrap =
          "width:100%;border-collapse:collapse;margin:0 0 20px 0;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.4;color:#333333;";
        const shippedEmailTh =
          "text-align:left;padding:12px 14px;border-bottom:2px solid #d0d0d0;background-color:#f4f4f5;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.06em;color:#555555;";
        const shippedEmailTd =
          "padding:12px 14px;border-bottom:1px solid #ececec;vertical-align:middle;";
        const shippedEmailTdNum =
          "padding:12px 14px;border-bottom:1px solid #ececec;vertical-align:middle;text-align:right;white-space:nowrap;";
        const shippedEmailSectionTitle =
          "font-family:Arial,Helvetica,sans-serif;font-size:13px;font-weight:700;color:#222222;margin:0 0 10px 0;letter-spacing:0.02em;";

        const categoryPriceBuckets = new Map();
        for (const lineItem of deliveredItems) {
          const rawCategory =
            lineItem.categoryName || lineItem.category || "Uncategorized";
          const unitCents = Math.round(Number(lineItem.amount) * 100);
          const key = `${String(rawCategory)
            .toLowerCase()
            .trim()}\x00${unitCents}`;
          const qty = Number(lineItem.qty) || 0;
          const existing = categoryPriceBuckets.get(key);
          if (existing) {
            existing.qty += qty;
          } else {
            categoryPriceBuckets.set(key, {
              categoryLabel: capitalize(String(rawCategory)),
              qty,
              unitPrice: unitCents / 100,
            });
          }
        }

        const categorySummaryRows = Array.from(categoryPriceBuckets.values())
          .sort((a, b) => {
            const byCat = a.categoryLabel.localeCompare(b.categoryLabel, "en", {
              sensitivity: "base",
            });
            if (byCat !== 0) return byCat;
            return a.unitPrice - b.unitPrice;
          })
          .map(
            (row) => `<tr>
                  <td style="${shippedEmailTd}">${row.categoryLabel}</td>
                  <td style="${shippedEmailTdNum}">${row.qty}</td>
                  <td style="${shippedEmailTdNum}">${shippedEmailCurrency.format(
              row.unitPrice,
            )}</td>
                  <td style="${shippedEmailTdNum}">${shippedEmailCurrency.format(
              row.qty * row.unitPrice,
            )}</td>
                </tr>`,
          )
          .join("");

        const lineItemsForDetailTable = [...deliveredItems].sort((a, b) => {
          const catA = (a.categoryName || a.category || "").trim();
          const catB = (b.categoryName || b.category || "").trim();
          const byCat = catA.localeCompare(catB, "en", { sensitivity: "base" });
          if (byCat !== 0) return byCat;
          const nameA = (a.name || "").trim();
          const nameB = (b.name || "").trim();
          return nameA.localeCompare(nameB, "en", { sensitivity: "base" });
        });

        const lineItemDetailRows = lineItemsForDetailTable
          .map((lineItem) => {
            const categorySubline = (
              lineItem.categoryName ||
              lineItem.category ||
              ""
            ).trim();
            const categoryHtml = categorySubline
              ? `<span style="display:block;font-size:11px;color:#777777;margin-top:4px;">${capitalize(
                  categorySubline,
                )}</span>`
              : "";
            const strainTypeRaw = (lineItem.strainType ?? "").toString().trim();
            const strainTypeCell = strainTypeRaw
              ? capitalize(strainTypeRaw)
              : "&#8212;";
            return `<tr>
                  <td style="${shippedEmailTd}">${capitalize(
              lineItem.name,
            )}${categoryHtml}</td>
                  <td style="${shippedEmailTd}">${strainTypeCell}</td>
                  <td style="${shippedEmailTdNum}">${lineItem.qty}</td>
                  <td style="${shippedEmailTdNum}">${shippedEmailCurrency.format(
              lineItem.amount,
            )}</td>
                  <td style="${shippedEmailTdNum}">${shippedEmailCurrency.format(
              Number(lineItem.amount) * Number(lineItem.qty),
            )}</td>
                </tr>`;
          })
          .join("");

        let shipDate = this.order.shipDate
          ? moment(this.order.shipDate.seconds * 1000)
              .startOf("day")
              .add(9, "hours")
              .tz("America/Los_Angeles")
              .toDate()
          : null;

        let replyTo =
          payableTo?.email?.length > 0
            ? {
                email: payableTo.email,
              }
            : null;

        const shippedEmailData = {
          subject: `Your ${capitalize(
            this.order.brand.name
          )} order has been shipped | ${this.order.orderNumber.internal.toUpperCase()}`,
          disposition: `Your order has been shipped! Below is a summary of your order.`,
          brand: capitalize(this.order.brand.name),
          name: capitalize(`${this.order.buyerInformation.name.split(" ")[0]}`),
          toEmail: this.order.buyerInformation.email,
          paymentTerm: orderPaymentTerm,
          totalQty: totalQTY,
          savings,
          balance: Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(Number(this.order.balance.due)),
          subtotal: Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(
            Number(this.order.balance.due) - Number(this.order.balance.shipping)
          ),
          shipping: Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(Number(this.order.balance.shipping)),
          totalSum: Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(Number(this.order.balance.total)),
          storeName: accountData.data().name,
          storeAddress: capitalize(
            accountData.data().location.shipping.street.number +
              " " +
              accountData.data().location.shipping.street.name +
              ", " +
              accountData.data().location.shipping.city.name +
              ", " +
              accountData
                .data()
                .location.shipping.state.abbreviation.toUpperCase() +
              " " +
              accountData.data().location.shipping.zipCode
          ),
          licenseNumber: accountData.data().license.number.toUpperCase(),
          licenseExpiration: moment(
            accountData.data().license.expiration?.seconds * 1000
          ).format("MM/DD/YYYY"),
          deliveryPreferences:
            this.order.account.deliveryPreferences.length > 0
              ? this.order.account.deliveryPreferences
              : "-",
          notes: this.order.notes.length > 0 ? this.order.notes : "-",
          shipDate: shipDate ? moment(shipDate).format("MM/DD/YYYY") : "-",
          lineItems: `<div>
              <p style="${shippedEmailSectionTitle}">Summary</p>
              <table role="presentation" cellpadding="0" cellspacing="0" style="${shippedEmailTableWrap}">
                <thead>
                  <tr>
                    <th width="40%" style="${shippedEmailTh}">Category</th>
                    <th width="20%" style="${shippedEmailTh};text-align:right;">QTY</th>
                    <th width="20%" style="${shippedEmailTh};text-align:right;">Price</th>
                    <th width="20%" style="${shippedEmailTh};text-align:right;">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${categorySummaryRows}
                </tbody>
              </table>
              <p style="${shippedEmailSectionTitle}">Products Shipped</p>
              <table role="presentation" cellpadding="0" cellspacing="0" style="${shippedEmailTableWrap}">
                <thead>
                  <tr>
                    <th width="32%" style="${shippedEmailTh}">Product</th>
                    <th width="18%" style="${shippedEmailTh}">Strain</th>
                    <th width="14%" style="${shippedEmailTh};text-align:right;">Qty</th>
                    <th width="18%" style="${shippedEmailTh};text-align:right;">Price</th>
                    <th width="18%" style="${shippedEmailTh};text-align:right;">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${lineItemDetailRows}
                </tbody>
              </table>
            </div>`,
        };

        await orderSubmissionEmail({
          subject: shippedEmailData.subject,
          html: buildOrderEmailHtml(shippedEmailData),
          cc,
          reply_to: replyTo,
          dynamic_template_data: { toEmail: shippedEmailData.toEmail },
        });
      }
    } catch (error) {
      // Revert statuses: order was set to fulfilled in transaction; Leaflink may have been transitioned
      try {
        const salesOrderDocRef = doc(firestore, "salesOrders", this.order.id);
        await updateDoc(salesOrderDocRef, {
          status: "fulfilled",
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
        });
      } catch (revertError) {
        console.error(
          "Failed to revert order status to fulfilled:",
          revertError,
        );
        logWorkflowWarning(`shippedAction: status revert to fulfilled failed — ${revertError.message}`, {
          workflow: "sales-order",
          action: "shipped-action-revert",
          context: { orderId: this.orderID, balance: this.order?.balance },
        });
      }
      if (this.orderIsExternal && this.externalOrderType === "leaflink") {
        try {
          if (this.externalUpdates.leaflink.includes("statusUpdated")) {
            await this.leaflinkClient.transitionOrder("fulfill");
          }
        } catch (revertError) {
          console.error(
            "Failed to revert Leaflink order to fulfilled:",
            revertError,
          );
          logWorkflowWarning(`shippedAction: LeafLink revert to fulfilled failed — ${revertError.message}`, {
            workflow: "sales-order",
            action: "leaflink-shipped-action-revert",
            context: { orderId: this.orderID, balance: this.order?.balance },
          });
        }
      }

      // Log error to Firestore "errorLogs" collection for shippedAction
      try {
        const errorLogRef = doc(collection(firestore, "errorLogs"));
        const timestamp = serverTimestamp();
        await setDoc(errorLogRef, {
          error: {
            message: error.message || String(error),
            stack: error.stack || null,
            name: error.name || null,
            context: {
              function: "shippedAction",
              additionalInfo: {
                orderId: this?.order?.id || null,
                brandId: this?.order?.brand?.id || null,
                accountId: this?.order?.account?.id || null,
                lineItemCount: Array.isArray(arguments[0])
                  ? arguments[0].length
                  : null, // argument for lineItems if present
              },
            },
          },
          createdBy: {
            uid: store.getters.getUser?.uid || null,
            email: store.getters.getUser?.email || null,
            name: store.getters.getUser?.name || null,
          },
          created: timestamp,
          updated: timestamp,
          lastUpdated: {
            uid: store.getters.getUser?.uid || null,
            name: store.getters.getUser?.name || null,
          },
          relatedOrder: {
            id: this?.order?.id || null,
            orderNumber: this?.order?.orderNumber || null,
          },
          relatedBrand: {
            id: this?.order?.brand?.id || null,
            name: this?.order?.brand?.name || null,
          },
          relatedAccount: {
            id: this?.order?.account?.id || null,
            name: this?.order?.account?.name || null,
          },
          triggeredFrom: "SalesOrderClient/shippedAction",
          data: {
            lineItems,
          },
        });
      } catch (logError) {
        console.error("Failed to log error to Firestore errorLogs:", logError);
        logWorkflowWarning(`shippedAction: errorLogs Firestore write failed — ${logError.message}`, {
          workflow: "sales-order",
          action: "shipped-action-errorlog",
          context: { orderId: this.orderID },
        });
      }
      throw error;
    }
  }

  // [FULFILLED]
  async fulfilledAction(
    deliveryLogistics,
    deliveryMethod,
    deliveredBy,
    shipDate,
    confirmationMethod,
    manifest,
    notes,
    deliveryFee,
    accountCloseData = null,
  ) {
    addBreadcrumb("fulfilledAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });
    await this.getOrder();
    await this.getLineItems();

    if (this.order?.status === "fulfilled") {
      return;
    }

    // Atomic claim: only one concurrent request can transition to fulfilled.
    const salesOrderDocRef = doc(firestore, "salesOrders", this.order.id);
    const applied = await runTransaction(firestore, async (transaction) => {
      const orderSnap = await transaction.get(salesOrderDocRef);
      if (!orderSnap.exists() || orderSnap.data().status === "fulfilled") {
        return false;
      }
      transaction.update(salesOrderDocRef, {
        status: "fulfilled",
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
      });
      return true;
    });

    if (!applied) {
      return;
    }

    let updates = [];

    try {
      const batch = writeBatch(firestore);

      const salesOrderDoc = salesOrderDocRef;

      let newShipDate = moment(shipDate)
        .startOf("day")
        .add(9, "hours")
        .tz("America/Los_Angeles")
        .toDate();
      let formattedShipDate = moment(newShipDate).format("MM/DD/YYYY");
      let newDeliveredBy = null,
        deliveryOption = deliveryMethod;
      if (deliveryMethod !== "account") {
        const selected = deliveryLogistics.find(
          (option) => option.id === deliveredBy,
        );
        console.log(deliveryLogistics, selected);
        if (selected) {
          newDeliveredBy = {
            name: selected.name,
            id: selected.id,
          };
        }
      }

      batch.set(
        salesOrderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          status: "fulfilled",
          records: {
            fulfilled: moment().startOf("day").add(12, "hours").toDate(),
          },
          deliveryOption: deliveryMethod,
          deliveredBy: newDeliveredBy,
          shipDate: newShipDate,
          deliveryLogisticsFee: deliveryFee,
          orderNumber: {
            manifest,
          },
        },
        { merge: true },
      );

      const stateDoc = doc(
        firestore,
        "states",
        this.order.location.shipping.state.stateID,
      );
      const stateResponse = await getDoc(stateDoc);
      let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

      if (this.order.fulfillmentLocation) {
        fulfillmentLocation = this.order.fulfillmentLocation.id;
      }

      const locationRef = doc(
        firestore,
        "locationManufacturing",
        `${fulfillmentLocation}_${this.order.brand.id}`,
      );

      // CRITICAL: Validate inventory before fulfillment to prevent negative inventory
      const inventoryCheck = await getDoc(locationRef);

      if (
        inventoryCheck.exists() &&
        inventoryCheck.data().hasOwnProperty("manufacturing")
      ) {
        let manufacturing = inventoryCheck.data().manufacturing;

        // Validate each line item BEFORE fulfilling
        this.lineItems.forEach((item) => {
          let available =
            manufacturing?.[item.categoryID]?.available?.[item.productID] ?? 0;
          let reserved =
            manufacturing?.[item.categoryID]?.reserved?.[item.productID] ?? 0;
          let transfer =
            manufacturing?.[item.categoryID]?.transfer?.[item.productID] ?? 0;

          let availableAmount = available - reserved - transfer;

          // Check if we have enough AVAILABLE inventory
          if (available - item.qty < 0) {
            throw new Error(
              `Insufficient available inventory for ${item.name} (${item.sku}). Available: ${available}, Requested: ${item.qty}`,
            );
          }

          // Check if we have enough RESERVED inventory
          if (reserved - item.qty < 0) {
            throw new Error(
              `Insufficient reserved inventory for ${item.name} (${item.sku}). Reserved: ${reserved}, Requested: ${item.qty}`,
            );
          }
        });
      } else {
        throw new Error("Inventory data not found for fulfillment location");
      }

      // Get manufacturing data for inventory snapshots (pre-write state for activities)
      let manufacturing = inventoryCheck.data().manufacturing;

      // Merge fix: one manufacturing update per location with all line-item deltas applied
      const merged = JSON.parse(JSON.stringify(manufacturing));
      this.lineItems.forEach((item) => {
        const cid = item.categoryID;
        const pid = item.productID;
        const qty = item.qty ?? 0;
        if (!merged[cid]) merged[cid] = {};
        if (!merged[cid].available) merged[cid].available = {};
        if (!merged[cid].reserved) merged[cid].reserved = {};
        merged[cid].available[pid] = Math.max(
          0,
          (merged[cid].available[pid] ?? 0) - qty,
        );
        merged[cid].reserved[pid] = Math.max(
          0,
          (merged[cid].reserved[pid] ?? 0) - qty,
        );
      });
      batch.set(locationRef, { manufacturing: merged }, { merge: true });

      // Aggregate fulfillment activity data by product so that the same product
      // at different prices (e.g. regular + promo) produces one activity with
      // correct before/after snapshots instead of overlapping per-line-item entries.
      const fulfillmentActivityMap = {};
      for (const item of this.lineItems) {
        const productKey = `${item.categoryID}_${item.productID}`;
        if (!fulfillmentActivityMap[productKey]) {
          const currentAvailable =
            manufacturing?.[item.categoryID]?.available?.[item.productID] ?? 0;
          const currentReserved =
            manufacturing?.[item.categoryID]?.reserved?.[item.productID] ?? 0;
          const currentPending =
            manufacturing?.[item.categoryID]?.pending?.[item.productID] ?? 0;
          const currentTransfer =
            manufacturing?.[item.categoryID]?.transfer?.[item.productID] ?? 0;
          const currentInTesting =
            manufacturing?.[item.categoryID]?.inTesting?.[item.productID] ?? 0;
          fulfillmentActivityMap[productKey] = {
            skuID: `${fulfillmentLocation}_${this.order.brand.id}_${item.categoryID}_${item.productID}`,
            itemData: item,
            totalQty: 0,
            before: {
              available: currentAvailable,
              reserved: currentReserved,
              pending: currentPending,
              transfer: currentTransfer,
              inTesting: currentInTesting,
            },
          };
        }
        fulfillmentActivityMap[productKey].totalQty += item.qty ?? 0;
      }

      for (const productKey of Object.keys(fulfillmentActivityMap)) {
        const agg = fulfillmentActivityMap[productKey];
        await createInventoryActivity({
          action: "Inventory fulfilled",
          item: `${agg.totalQty} units of ${agg.itemData.name} (${agg.itemData.sku}) fulfilled for Order #${this.order.orderNumber.internal}`,
          skuID: agg.skuID,
          itemData: agg.itemData,
          movement: {
            type: "fulfillment",
            from: "reserved",
            to: "fulfilled",
            quantity: agg.totalQty,
            reason: "salesOrderFulfilled",
            reasonID: this.orderID,
          },
          snapshot: {
            before: agg.before,
            after: {
              available: Math.max(0, agg.before.available - agg.totalQty),
              reserved: Math.max(0, agg.before.reserved - agg.totalQty),
              pending: agg.before.pending,
              transfer: agg.before.transfer,
              inTesting: agg.before.inTesting,
            },
          },
          location: {
            id: fulfillmentLocation,
            name:
              this.order.fulfillmentLocation?.name ||
              this.order.location.shipping.name,
          },
          order: this.order,
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          extraSalesOrderData: {
            deliveryMethod: deliveryMethod,
            shipDate: newShipDate,
          },
        });
      }

      // External - Update Status
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (this.externalUpdates.leaflink.includes("statusUpdated")) {
              addBreadcrumb("leaflink: transitionOrder fulfill (fulfilledAction)", {
                workflow: "sales-order",
                data: { orderId: this.orderID, fromStatus: this.order?.status },
              });
              try {
                await this.leaflinkClient.transitionOrder("fulfill");
              } catch (error) {
                logWorkflowWarning(`LeafLink: transitionOrder("fulfill") (fulfilledAction) failed — ${error.message}`, {
                  workflow: "sales-order",
                  action: "leaflink-fulfilled-transition",
                  context: { orderId: this.orderID, balance: this.order?.balance },
                });
                throw error;
              }
            }
            let shipDateMoment = moment(newShipDate);
            let shipDate = moment(shipDateMoment.clone().format("MM/DD/YYYY"))
              .add(9, "hours")
              .tz("America/Los_Angeles");
            let patchData = {
              ship_date: moment(shipDate).toISOString(),
            };
            if (this.externalUpdates.leaflink.includes("orderUpdated")) {
              addBreadcrumb("leaflink: updateOrderData ship_date (fulfilledAction)", {
                workflow: "sales-order",
                data: { orderId: this.orderID, newShipDate },
              });
              try {
                await this.leaflinkClient.updateOrderData(patchData);
              } catch (error) {
                logWorkflowWarning(`LeafLink: updateOrderData (fulfilledAction) failed — ${error.message}`, {
                  workflow: "sales-order",
                  action: "leaflink-fulfilled-update-order",
                  context: { orderId: this.orderID, balance: this.order?.balance },
                });
                throw error;
              }
            }
            break;
          default:
            break;
        }
      }

      const oldStatus = store.getters.getOrderStatus.find(
          (option) => option.value === this.order.status,
        ),
        newStatus = store.getters.getOrderStatus.find(
          (option) => option.value === "fulfilled",
        );

      console.log(confirmationMethod);

      // let shipDate = ;
      updates.push(
        `<span class="text-capitalize">status</span>: <span class="text-uppercase">old</span>: <span class="font-weight-bold" style="color: ${oldStatus.color};">${oldStatus.text}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold" style="color: ${newStatus.color};">${newStatus.text}</span>`,
      );
      updates.push(
        `<span class="text-capitalize">ship date</span>: <span class="font-weight-bold">${moment(
          newShipDate,
        ).format("MM/DD/YYYY")}</span>`,
      );
      updates.push(
        `<span class="text-capitalize">confirmed by</span>: <span class="font-weight-bold text-capitalize">${confirmationMethod.text}</span>`,
      );
      updates.push(
        `<span class="text-capitalize">delivery type</span>: <span>${
          store.getters.getDeliveryTypes.find(
            (option) => option.value === deliveryMethod,
          ).text
        }</span>, <span class="text-capitalize">delivery by</span>: <span class="text-capitalize">${
          deliveryMethod !== "account" ? newDeliveredBy?.name ?? "—" : "account"
        }</span>`,
      );
      if (notes?.length > 0) {
        updates.push(
          `<span class="text-capitalize">confirmation notes</span>: <span class="font-weight-bold">"${notes}"</span>`,
        );
      }
      if (manifest) {
        updates.push(
          `<span class="text-capitalize">manifest #</span>: <span class="font-weight-bold text-uppercase">${manifest}</span>`,
        );
      }

      // Create activity for order fulfillment completion
      const fulfillmentDetails = [];

      // Get proper delivery method text
      const deliveryType = store.getters.getDeliveryTypes.find(
        (option) => option.value === deliveryMethod,
      );
      if (deliveryType) {
        fulfillmentDetails.push(`Delivery: ${deliveryType.text}`);
      }

      // Get proper delivered by name
      let deliveredByName = "Account";
      if (deliveryMethod !== "account" && newDeliveredBy) {
        deliveredByName = newDeliveredBy.name;
      }
      fulfillmentDetails.push(`Delivered by: ${deliveredByName}`);

      if (manifest) fulfillmentDetails.push(`Manifest: ${manifest}`);
      if (deliveryFee > 0) fulfillmentDetails.push(`Fee: $${deliveryFee}`);

      await createActivity({
        type: "logistics",
        action: "Order fulfilled",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} fulfilled - ${fulfillmentDetails.join(
          ", ",
        )}`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "fulfillmentCompletion",
          deliveryLogistics: deliveryLogistics,
          deliveryMethod: deliveryMethod,
          deliveredBy: deliveredBy,
          shipDate: shipDate,
          confirmationMethod: confirmationMethod,
          manifest: manifest,
          notes: notes,
          deliveryFee: deliveryFee,
          orderNumber: this.order.orderNumber.internal,
          updateType: "fulfillment_completion",
          monetaryValue: deliveryFee || 0,
          fulfillmentDate: new Date(),
          status: "fulfilled",
        },
      });

      // Create activity for status change
      const fulfillmentOldStatus = store.getters.getOrderStatus.find(
        (option) => option.value === this.order.status,
      );
      const fulfillmentNewStatus = store.getters.getOrderStatus.find(
        (option) => option.value === "fulfilled",
      );

      await createActivity({
        type: "status",
        action: "Order status updated",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} status changed from ${
          fulfillmentOldStatus?.text || this.order.status
        } to ${fulfillmentNewStatus?.text || "fulfilled"}`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "status",
          oldValue: {
            text: fulfillmentOldStatus?.text || this.order.status,
            value: this.order.status,
            color: fulfillmentOldStatus?.color || "#000000",
          },
          newValue: {
            text: fulfillmentNewStatus?.text || "fulfilled",
            value: "fulfilled",
            color: fulfillmentNewStatus?.color || "#000000",
          },
          orderNumber: this.order.orderNumber.internal,
          fulfillmentContext: "order_fulfillment",
          externalOrderUpdated:
            this.externalUpdates.leaflink.includes("statusUpdated"),
        },
      });

      // Ledger
      updates = updates.join(" | ");
      addBreadcrumb("fulfilledAction: committing batch", {
        workflow: "sales-order",
        data: { orderId: this.orderID, balance: this.order?.balance },
      });
      await batch.commit();

      // Search for contact that matches buyerInformation name and email with active === true
      const contactsQuery = query(
        collection(firestore, "contacts"),
        // where("name", "==", accountCloseData.buyerInformation.name),
        where("email", "==", accountCloseData.buyerInformation.email),
        where("active", "==", true),
      );
      const contactsSnapshot = await getDocs(contactsQuery);
      const contactData = contactsSnapshot.empty
        ? null
        : contactsSnapshot.docs[0].data();

      // Add new account close logic after the batch commit
      if (
        accountCloseData &&
        accountCloseData.isNewAccount &&
        accountCloseData.payableToTriggersOnboarding &&
        !accountCloseData.flags.sample
      ) {
        const sendgridEmailCompany = httpsCallable(
          functions,
          "sendgridEmailCompany",
        );

        // Create the JSON data for the email
        const jsonData = {
          accountId: accountCloseData.account.id,
          licenseNumber: accountCloseData.account.license.number.toUpperCase(),
          licenseExpiration:
            accountCloseData.account.license.expiration || null,
          salesRep: {
            id: accountCloseData.user.uid,
            name: `${accountCloseData.user.name}`,
            email: accountCloseData.user.email || null,
          },
          order: {
            orderId: accountCloseData.order.id,
            closeDate: moment().format("MMMM D, YYYY [at] h:mm:ss A [UTC]Z"),
            deliveryDate: accountCloseData.order.deliveryDate
              ? moment(
                  accountCloseData.order.deliveryDate,
                  "MM/DD/YYYY",
                ).format("MMMM D, YYYY [at] h:mm:ss A [UTC]Z")
              : null,
          },
          website: accountCloseData.account.website || null,
          storefrontName:
            accountCloseData.account.name.charAt(0).toUpperCase() +
            accountCloseData.account.name.slice(1),
          licenseName:
            accountCloseData.account.name.charAt(0).toUpperCase() +
            accountCloseData.account.name.slice(1),
          storeType: accountCloseData.account.storeType || "Retail",
          shippingAddress: {
            line1: `${accountCloseData.account.location.shipping.street.number} ${accountCloseData.account.location.shipping.street.name}`,
            city: accountCloseData.account.location.shipping.city.name,
            state:
              accountCloseData.account.location.shipping.state.abbreviation,
            postalCode: accountCloseData.account.location.shipping.zipCode,
          },
          storeContact: {
            name: accountCloseData.account.name,
            phone: accountCloseData.account.phoneNumber,
            email: accountCloseData.account.email,
          },
          buyerInformation: {
            ...accountCloseData.buyerInformation,
            phoneNumber: contactData?.phoneNumber,
          },
          accountContacts: accountCloseData.account.contacts,
          previousOrder: {
            date:
              accountCloseData.timeSinceLastOrder === "No previous orders"
                ? null
                : moment(accountCloseData.timeSinceLastOrder).format(
                    "YYYY-MM-DD",
                  ),
            totalOrders:
              accountCloseData.brandMetrics.metrics?.orders?.total || 0,
          },
        };

        await sendgridEmailCompany({
          sendgridOrderTemplate: "d-0285e0366fe14dff999f35bf5aa356c4",
          cc: [],
          dynamic_template_data: {
            subject: `New Account Close - ${
              accountCloseData.account.name.charAt(0).toUpperCase() +
              accountCloseData.account.name.slice(1)
            }`,
            toEmail: "automation@dime.industries",
            name: accountCloseData.user.name || "N/A",
            storeName:
              accountCloseData.account.name.charAt(0).toUpperCase() +
              accountCloseData.account.name.slice(1),
            disposition:
              accountCloseData.timeSinceLastOrder === "No previous orders"
                ? "This is the first order for this account."
                : `Last order was on ${accountCloseData.timeSinceLastOrder}`,
            brand:
              accountCloseData.brand.name.charAt(0).toUpperCase() +
              accountCloseData.brand.name.slice(1),
            orderTotal: `$${accountCloseData.order.total.toFixed(2)}`,
            accountData: JSON.stringify(jsonData, null, 2),
          },
        });

        console.log("email sent");
      }
    } catch (error) {
      // Revert statuses: order was set to fulfilled in transaction; Leaflink may have been transitioned
      try {
        const salesOrderDocRef = doc(firestore, "salesOrders", this.order.id);
        await updateDoc(salesOrderDocRef, {
          status: "deliveryConfirmation",
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
        });
      } catch (revertError) {
        console.error(
          "Failed to revert order status to deliveryConfirmation:",
          revertError,
        );
        logWorkflowWarning(`fulfilledAction: status revert to deliveryConfirmation failed — ${revertError.message}`, {
          workflow: "sales-order",
          action: "fulfilled-action-revert",
          context: { orderId: this.orderID, balance: this.order?.balance },
        });
      }
      if (this.orderIsExternal && this.externalOrderType === "leaflink") {
        try {
          if (this.externalUpdates.leaflink.includes("statusUpdated")) {
            await this.leaflinkClient.transitionOrder("accept");
          }
        } catch (revertError) {
          const msg =
            revertError?.message ?? revertError?.details ?? String(revertError);
          const alreadyAccepted =
            typeof msg === "string" &&
            msg.toLowerCase().includes("accepted state");
          if (!alreadyAccepted) {
            console.error(
              "Failed to revert Leaflink order to accepted:",
              revertError,
            );
            logWorkflowWarning(`fulfilledAction: LeafLink revert to accepted failed — ${revertError.message}`, {
              workflow: "sales-order",
              action: "leaflink-fulfilled-action-revert",
              context: { orderId: this.orderID, balance: this.order?.balance },
            });
          }
        }
      }

      // Log error to Firestore "errorLogs" collection for fulfilledAction
      try {
        const errorLogRef = doc(collection(firestore, "errorLogs"));
        const timestamp = serverTimestamp();
        await setDoc(errorLogRef, {
          error: {
            message: error.message || String(error),
            stack: error.stack || null,
            name: error.name || null,
            context: {
              function: "fulfilledAction",
              additionalInfo: {
                orderId: this?.order?.id || null,
                brandId: this?.order?.brand?.id || null,
                accountId: this?.order?.account?.id || null,
                lineItemCount: Array.isArray(arguments[0])
                  ? arguments[0].length
                  : null, // argument for lineItems if present
              },
            },
          },
          createdBy: {
            uid: store.getters.getUser?.uid || null,
            email: store.getters.getUser?.email || null,
            name: store.getters.getUser?.name || null,
          },
          created: timestamp,
          updated: timestamp,
          lastUpdated: {
            uid: store.getters.getUser?.uid || null,
            name: store.getters.getUser?.name || null,
          },
          relatedOrder: {
            id: this?.order?.id || null,
            orderNumber: this?.order?.orderNumber || null,
          },
          relatedBrand: {
            id: this?.order?.brand?.id || null,
            name: this?.order?.brand?.name || null,
          },
          relatedAccount: {
            id: this?.order?.account?.id || null,
            name: this?.order?.account?.name || null,
          },
          triggeredFrom: "SalesOrderClient/fulfilledAction",
          data: {
            deliveryLogistics: deliveryLogistics || null,
            deliveryMethod: deliveryMethod || null,
            deliveredBy: deliveredBy || null,
            shipDate: shipDate || null,
            confirmationMethod: confirmationMethod || null,
            manifest: manifest || null,
            notes: notes || null,
            deliveryFee: deliveryFee || null,
            accountCloseData: accountCloseData || null,
          },
        });
      } catch (logError) {
        console.error("Failed to log error to Firestore errorLogs:", logError);
        logWorkflowWarning(`fulfilledAction: errorLogs Firestore write failed — ${logError.message}`, {
          workflow: "sales-order",
          action: "fulfilled-action-errorlog",
          context: { orderId: this.orderID },
        });
      }
      throw error;
    }
  }

  // [DELIVERY CONFIRMATION]
  async deliveryConfirmationAction(lineItems) {
    console.log("deliveryConfirmationAction");

    await this.getOrder();
    await this.getLineItems();
    addBreadcrumb("deliveryConfirmationAction: start", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        fromStatus: this.order?.status,
        balance: this.order?.balance,
        fulfilledCount: lineItems.filter((i) => i.fulfilled).length,
        removedCount: lineItems.filter((i) => !i.fulfilled).length,
      },
    });

    if (lineItems.length === 0) {
      throw new Error(
        "No line items were provided. Please refresh the page and try again."
      );
    }

    try {
      const batch = writeBatch(firestore);
      const brandStatsDoc = doc(
        firestore,
        "brandStats",
        `${this.order.account.id}_${this.order.brand.id}`,
      );

      const salesOrderDoc = doc(firestore, "salesOrders", this.order.id);

      let status = "accepted";
      let updates = [];
      const stateDoc = doc(
        firestore,
        "states",
        this.order.location.shipping.state.stateID,
      );
      const stateResponse = await getDoc(stateDoc);
      let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

      if (this.order.fulfillmentLocation) {
        fulfillmentLocation = this.order.fulfillmentLocation.id;
      }
      console.log("545");
      const locationRef = doc(
        firestore,
        "locationManufacturing",
        `${fulfillmentLocation}_${this.order.brand.id}`,
      );
      let fulfilled = lineItems.filter((item) => item.fulfilled);
      let removed = lineItems.filter((item) => !item.fulfilled);

      if (fulfilled.length) {
        let fulfilledUpdates = [];
        status = lineItems.every((item) => item.fulfilled)
          ? "deliveryConfirmation"
          : "accepted";
        lineItems.forEach((item) => {
          console.log("lineItem", item);
          const lineItemDoc = doc(firestore, "salesOrders_lineItems", item.id);

          batch.set(
            lineItemDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              notes: item.notes,
              flags: {
                fulfilled: true,
              },
            },
            { merge: true },
          );
          fulfilledUpdates.push(
            `<span class="text-capitalize font-weight-bold">${
              item.name
            }</span> (${item.sku.toUpperCase()}) - <span class="text-uppercase">qty</span>: ${
              item.qty
            }`,
          );
        });
        updates.push(
          `<span class="text-capitalize">fulfilled items</span>: ${fulfilledUpdates.join(
            " / ",
          )}`,
        );
      }
      if (removed.length) {
        let removedUpdates = [];

        // Get current inventory for snapshots
        const inventoryCheck = await getDoc(locationRef);
        let manufacturing = inventoryCheck.exists()
          ? inventoryCheck.data().manufacturing
          : {};

        for (const item of removed) {
          const lineItemDoc = doc(firestore, "salesOrders_lineItems", item.id);
          batch.delete(lineItemDoc);

          // Reset inventory
          batch.set(
            locationRef,
            {
              manufacturing: {
                [item.categoryID]: {
                  reserved: {
                    [item.productID]: increment(-item.qty),
                  },
                },
              },
            },
            { merge: true },
          );

          // Track inventory unreservation for this SKU
          const skuID = `${fulfillmentLocation}_${this.order.brand.id}_${item.categoryID}_${item.productID}`;
          const currentAvailable =
            manufacturing?.[item.categoryID]?.available?.[item.productID] ?? 0;
          const currentReserved =
            manufacturing?.[item.categoryID]?.reserved?.[item.productID] ?? 0;
          const currentPending =
            manufacturing?.[item.categoryID]?.pending?.[item.productID] ?? 0;
          const currentTransfer =
            manufacturing?.[item.categoryID]?.transfer?.[item.productID] ?? 0;
          const currentInTesting =
            manufacturing?.[item.categoryID]?.inTesting?.[item.productID] ?? 0;

          // Use granular line-removed reasons so reporting is consistent with other flows.
          // Treat this path as an accepted/pre-fulfillment removal.
          const lineRemovedReason = "salesOrderLineRemovedAccepted";

          await createInventoryActivity({
            action: "Inventory unreserved",
            item: `${item.qty} units of ${item.name} (${item.sku}) unreserved - Order #${this.order.orderNumber.internal} line item removed`,
            skuID,
            itemData: item,
            movement: {
              type: "unreservation",
              from: "reserved",
              to: "available",
              quantity: item.qty,
              reason: lineRemovedReason,
              reasonID: this.orderID,
            },
            snapshot: {
              before: {
                available: currentAvailable,
                reserved: currentReserved,
                pending: currentPending,
                transfer: currentTransfer,
                inTesting: currentInTesting,
              },
              // "after" is calculated dynamically from "before" + movement.quantity in the display
            },
            location: {
              id: fulfillmentLocation,
              name:
                this.order.fulfillmentLocation?.name ||
                this.order.location.shipping.name,
            },
            order: this.order,
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });

          let productLots = item.productLots ?? [];
          for (const productLot of productLots) {
            const productLotDoc = doc(firestore, "productLots", productLot.id);
            const productLotResponse = await getDoc(productLotDoc);

            if (productLotResponse.exists()) {
              const productLotData = productLotResponse.data();
              let availableUnits = productLotResponse.data().units;

              if (productLotData.hasOwnProperty("available")) {
                availableUnits =
                  Number(productLotResponse.data().available) +
                  productLot.units;
              }

              const newRelatedLineItems =
                productLotData.relatedLineItems?.filter(
                  (lineItem) => lineItem.id !== item.id,
                ) ?? [];

              await updateDoc(
                productLotDoc,
                {
                  active: availableUnits > 0,
                  available: availableUnits,
                  relatedLineItems: newRelatedLineItems,
                  updated: serverTimestamp(),
                  lastUpdated: {
                    name: store.getters.getUser.name,
                    uid: store.getters.getUser.uid,
                  },
                },
                { merge: true },
              );
            }
          }
          removedUpdates.push(
            `<span class="text-capitalize font-weight-bold">${
              item.name
            }</span> (${item.sku.toUpperCase()}) - <span class="text-uppercase">qty</span>: ${
              item.qty
            }`,
          );

          if (this.orderIsExternal) {
            switch (this.externalOrderType) {
              case "leaflink":
                if (this.externalUpdates.leaflink.includes("orderUpdated")) {
                  let externalItem = null;
                  if (item.externalIDs?.leaflinkLineItemID) {
                    externalItem = {
                      id: item.externalIDs.leaflinkLineItemID,
                    };
                  } else {
                    if (this.leaflinkClient.lineItems?.length === 0) {
                      await this.leaflinkClient.getLineItems();
                    }
                    externalItem = this.leaflinkClient.lineItems.find(
                      (external) =>
                        external.productID === item.externalIDs.leaflink,
                    );
                  }
                  if (externalItem) {
                    addBreadcrumb("leaflink: deleteLineItem (deliveryConfirmation)", {
                      workflow: "sales-order",
                      data: { orderId: this.orderID, itemId: item.id, externalItemId: externalItem.id },
                    });
                    try {
                      await this.leaflinkClient.deleteLineItem(externalItem);
                    } catch (error) {
                      logWorkflowWarning(`LeafLink: deleteLineItem (deliveryConfirmation) failed — ${error.message}`, {
                        workflow: "sales-order",
                        action: "leaflink-delivery-confirmation-delete-item",
                        context: { orderId: this.orderID, itemId: item.id, balance: this.order?.balance },
                      });
                      throw error;
                    }
                  }
                }
                break;
              default:
                break;
            }
          }
        }
        updates.push(
          `<span class="text-capitalize">removed items</span>: ${removedUpdates.join(
            " / ",
          )}`,
        );
      }

      console.log("666");

      status = "deliveryConfirmation";
      const oldStatus = store.getters.getOrderStatus.find(
          (option) => option.value === this.order.status,
        ),
        newStatus = store.getters.getOrderStatus.find(
          (option) => option.value === status,
        );

      updates.push(
        `<span class="text-capitalize">status</span> - <span class="text-uppercase">old</span>: <span class="font-weight-bold" style="color: ${oldStatus.color};">${oldStatus.text}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold" style="color: ${newStatus.color};">${newStatus.text}</span>`,
      );

      batch.set(
        salesOrderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          records: {
            [status]: moment().startOf("day").add(12, "hours").toDate(),
          },
        },
        { merge: true },
      );

      console.log("695");

      // Recalculate order balance & QTY

      const units = fulfilled.reduce((a, b) => a + b.qty, 0);
      const total = fulfilled.reduce((a, b) => a + b.amount * b.qty, 0);

      let removedAmount = removed.reduce((a, b) => a + b.amount * b.qty, 0);
      console.log("removedAmount", removedAmount);

      let submittedPayments =
        Number(this.order.balance.total) - Number(this.order.balance.due);

      let totalDiscountValue = () => {
        let isPercentage = this.order.discount?.type === "%";
        let discountValue = this.order.discount?.value ?? 0;
        let totalValue = total;
        if (isPercentage) {
          return (discountValue / 100) * totalValue;
        } else {
          return discountValue;
        }
      };

      let balanceDue = parseFloat(
        (
          total -
          totalDiscountValue() -
          submittedPayments +
          Number(this.order.balance.shipping)
        ).toFixed(2),
      );
      let balanceTotal = parseFloat(
        (
          total -
          totalDiscountValue() +
          Number(this.order.balance.shipping)
        ).toFixed(2),
      );

      addBreadcrumb("deliveryConfirmationAction: balance recalculated", {
        workflow: "sales-order",
        data: {
          orderId: this.orderID,
          balanceDue,
          balanceTotal,
          fulfilledCount: fulfilled.length,
          removedCount: removed.length,
        },
      });

      batch.set(
        salesOrderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          balance: {
            due: parseFloat(balanceDue).toFixed(2),
            total: parseFloat(balanceTotal).toFixed(2),
          },
          lineItems: fulfilled,
          status,
          units,
          discount: {
            value: this.order.discount?.value ?? 0,
            type: this.order.discount?.type ?? "%",
            total: totalDiscountValue(),
          },
        },
        { merge: true },
      );

      // Create activity for delivery confirmation
      await createActivity({
        type: "status",
        action: "Delivery confirmation updated",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} delivery confirmation processed - ${
          fulfilled.length
        } items fulfilled, ${removed.length} items removed`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        changeData: {
          field: "deliveryConfirmation",
          oldValue: {
            text: oldStatus.text,
            value: oldStatus.value,
            color: oldStatus.color,
          },
          newValue: {
            text: newStatus.text,
            value: newStatus.value,
            color: newStatus.color,
          },
          fulfilledItems: fulfilled.map((item) => ({
            name: item.name,
            sku: item.sku,
            qty: item.qty,
            notes: item.notes,
            amount: item.amount,
            totalAmount: (item.amount * item.qty).toFixed(2),
            category: item.category,
            productID: item.productID,
            categoryID: item.categoryID,
          })),
          removedItems: removed.map((item) => ({
            name: item.name,
            sku: item.sku,
            qty: item.qty,
            amount: item.amount,
            totalAmount: (item.amount * item.qty).toFixed(2),
            category: item.category,
            productID: item.productID,
            categoryID: item.categoryID,
            reason: "Removed during delivery confirmation",
          })),
          summary: {
            totalFulfilledItems: fulfilled.length,
            totalRemovedItems: removed.length,
            totalFulfilledQty: fulfilled.reduce(
              (sum, item) => sum + item.qty,
              0,
            ),
            totalRemovedQty: removed.reduce((sum, item) => sum + item.qty, 0),
            totalFulfilledAmount: fulfilled
              .reduce((sum, item) => sum + item.amount * item.qty, 0)
              .toFixed(2),
            totalRemovedAmount: removed
              .reduce((sum, item) => sum + item.amount * item.qty, 0)
              .toFixed(2),
            balanceChange: removedAmount.toFixed(2),
          },
          orderNumber: this.order.orderNumber.internal,
          processType: "delivery_confirmation",
        },
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
      });

      // Ledger
      updates = updates.join(" | ");

      addBreadcrumb("deliveryConfirmationAction: committing batch", {
        workflow: "sales-order",
        data: { orderId: this.orderID, balanceDue, balanceTotal },
      });
      await batch.commit();

      await this._checkBalanceAnomaly("delivery-confirmation", {
        balanceDue,
        balanceTotal,
      });
      await this.checkOverpaymentCredits(balanceDue);
    } catch (error) {
      throw error;
    }
  }

  // [APPROVAL]
  async approvalAction(approved, reason) {
    addBreadcrumb("approvalAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, approved, balance: this.order?.balance },
    });
    await this.getOrder();
    await this.getLineItems();

    const targetStatus = approved ? "submitted" : "cancelled";
    if (
      this.order?.status === "submitted" ||
      this.order?.status === "cancelled"
    ) {
      return;
    }

    // Atomic claim: only one concurrent request can transition to submitted/cancelled.
    const orderDocRef = doc(firestore, "salesOrders", this.order.id);
    const applied = await runTransaction(firestore, async (transaction) => {
      const orderSnap = await transaction.get(orderDocRef);
      const current = orderSnap.exists() ? orderSnap.data().status : null;
      if (current === "submitted" || current === "cancelled") {
        return false;
      }
      transaction.update(orderDocRef, {
        status: targetStatus,
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
      });
      return true;
    });

    if (!applied) {
      return;
    }

    // Store pending credit IDs to apply after batch commit (if approved)
    // Support both old single credit field and new multiple credits field
    const pendingCreditIds = [];
    if (approved) {
      if (this.order?.pendingPromotionCredits?.length > 0) {
        pendingCreditIds.push(...this.order.pendingPromotionCredits);
      } else if (this.order?.pendingPromotionCredit) {
        // Backwards compatibility with old single credit field
        pendingCreditIds.push(this.order.pendingPromotionCredit);
      }
    }

    try {
      const batch = writeBatch(firestore);
      const orderDoc = orderDocRef;
      const stateDoc = doc(
        firestore,
        "states",
        this.order.location.shipping.state.stateID,
      );
      const stateResponse = await getDoc(stateDoc);
      let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

      if (this.order.fulfillmentLocation) {
        fulfillmentLocation = this.order.fulfillmentLocation.id;
      }

      const locationRef = doc(
        firestore,
        "locationManufacturing",
        `${fulfillmentLocation}_${this.order.brand.id}`,
      );

      let manufacturing = {};

      const oldValue = store.getters.getOrderStatus.find(
          (status) => status.value === this.order.status,
        ),
        newValue = store.getters.getOrderStatus.find(
          (status) =>
            status.value === `${approved ? "submitted" : "cancelled"}`,
        );

      // Update order
      batch.set(
        orderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          status: newValue.value,
          records: {
            [newValue.value]: moment().startOf("day").add(12, "hours").toDate(),
          },
        },
        { merge: true },
      );
      // External - Update Status
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (!approved) {
              if (this.externalUpdates.leaflink.includes("statusUpdated")) {
                addBreadcrumb("leaflink: transitionOrder reject (approvalAction)", {
                  workflow: "sales-order",
                  data: { orderId: this.orderID, fromStatus: this.order?.status },
                });
                try {
                  await this.leaflinkClient.transitionOrder("reject");
                } catch (error) {
                  logWorkflowWarning(`LeafLink: transitionOrder("reject") (approvalAction) failed — ${error.message}`, {
                    workflow: "sales-order",
                    action: "leaflink-approval-reject-transition",
                    context: { orderId: this.orderID, balance: this.order?.balance },
                  });
                  throw error;
                }
              }
            }
            break;
          default:
            break;
        }
      }

      if (approved) {
        // Clear the pending credit fields in the batch (will be applied after commit)
        if (pendingCreditIds.length > 0) {
          batch.set(
            orderDoc,
            {
              pendingPromotionCredit: null,
              pendingPromotionCredits: [],
            },
            { merge: true },
          );
        }
      } else {
        // Read current inventory before updating (for activity snapshot)
        const locationSnap = await getDoc(locationRef);
        manufacturing =
          locationSnap.exists() && locationSnap.data().manufacturing
            ? locationSnap.data().manufacturing
            : {};

        // Aggregate by product so same product (e.g. regular + promo) gets one location update and one activity
        const rejectActivityMap = {};
        for (const item of this.lineItems) {
          const key = `${item.categoryID}_${item.productID}`;
          if (!rejectActivityMap[key]) {
            rejectActivityMap[key] = { totalQty: 0, lineItem: item };
          }
          rejectActivityMap[key].totalQty += item.qty ?? 0;
        }
        for (const key of Object.keys(rejectActivityMap)) {
          const agg = rejectActivityMap[key];
          batch.set(
            locationRef,
            {
              manufacturing: {
                [agg.lineItem.categoryID]: {
                  reserved: {
                    [agg.lineItem.productID]: increment(-agg.totalQty),
                  },
                },
              },
            },
            { merge: true },
          );
        }

        const brandStatsDoc = doc(
          firestore,
          "brandStats",
          `${this.order.account.id}_${this.order.brand.id}`,
        );
        const brandStats = await getDoc(brandStatsDoc);
        if (brandStats.data().metrics.closeDate?.order === orderDoc.id) {
          batch.set(
            brandStatsDoc,
            {
              metrics: {
                closeDate: null,
              },
            },
            { merge: true },
          );
        }
      }

      let updateString = `<span class="text-uppercase">${
        approved ? "approved" : "rejected"
      }</span>`;
      let rejectionNotes = reason.length > 0 ? ` | Notes: ${reason}` : "";

      // Create activity for order approval/rejection
      await createActivity({
        type: "status",
        action: approved ? "Order approved" : "Order rejected",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} ${
          approved ? "approved" : "rejected"
        }${reason ? ` - Reason: ${reason}` : ""}`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "approvalStatus",
          approved: approved,
          oldValue: {
            text: oldValue.text,
            value: oldValue.value,
            color: oldValue.color,
          },
          newValue: {
            text: newValue.text,
            value: newValue.value,
            color: newValue.color,
          },
          reason: reason,
          orderNumber: this.order.orderNumber.internal,
          inventoryReset: !approved,
          externalOrderUpdated:
            this.externalUpdates.leaflink.includes("statusUpdated"),
        },
      });

      addBreadcrumb("approvalAction: committing batch", {
        workflow: "sales-order",
        data: { orderId: this.orderID, approved, balance: this.order?.balance },
      });
      await batch.commit();

      // Record inventory activities for rejection (reserved → available); one per product
      if (!approved && this.lineItems.length > 0) {
        const fulfillmentLocationName =
          this.order.fulfillmentLocation?.name ||
          this.order.location?.shipping?.name ||
          "";
        const rejectActivityMap = {};
        for (const item of this.lineItems) {
          const key = `${item.categoryID}_${item.productID}`;
          if (!rejectActivityMap[key]) {
            rejectActivityMap[key] = { totalQty: 0, lineItem: item };
          }
          rejectActivityMap[key].totalQty += item.qty ?? 0;
        }
        for (const key of Object.keys(rejectActivityMap)) {
          const agg = rejectActivityMap[key];
          const { categoryID, productID } = agg.lineItem;
          const mfg = manufacturing[categoryID] || {};
          const currentAvailable = mfg.available?.[productID] ?? 0;
          const currentReserved = mfg.reserved?.[productID] ?? 0;
          const currentPending = mfg.pending?.[productID] ?? 0;
          const currentTransfer = mfg.transfer?.[productID] ?? 0;
          const currentInTesting = mfg.inTesting?.[productID] ?? 0;
          const skuID = `${fulfillmentLocation}_${this.order.brand.id}_${categoryID}_${productID}`;
          try {
            await createInventoryActivity({
              action: "Inventory released",
              item: `${agg.totalQty} units of ${agg.lineItem.name} (${agg.lineItem.sku}) released from rejected Order #${this.order.orderNumber.internal}`,
              skuID,
              itemData: agg.lineItem,
              movement: {
                type: "release",
                from: "reserved",
                to: "available",
                quantity: agg.totalQty,
                reason: "salesOrderRejected",
                reasonID: this.orderID,
              },
              snapshot: {
                before: {
                  available: currentAvailable,
                  reserved: currentReserved,
                  pending: currentPending,
                  transfer: currentTransfer,
                  inTesting: currentInTesting,
                },
              },
              location: {
                id: fulfillmentLocation,
                name: fulfillmentLocationName,
              },
              order: this.order,
              user: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              extraSalesOrderData: {
                orderID: this.orderID,
                rejectionReason: reason,
              },
            });
          } catch (err) {
            console.error("createInventoryActivity (order rejected):", err);
            logWorkflowWarning(`approvalAction: inventory activity creation failed — ${err.message}`, {
              workflow: "sales-order",
              action: "approval-inventory-activity",
              context: { orderId: this.orderID, productKey: key, balance: this.order?.balance },
            });
          }
        }
      }

      // Apply pending promotion credits after batch commit (only if approved)
      if (approved && pendingCreditIds.length > 0) {
        try {
          // Refresh order data to get updated balance after status change
          await this.getOrder();
          let remainingOrderTotal = parseFloat(
            this.order.balance.due || this.order.balance.total || 0,
          );

          if (remainingOrderTotal > 0) {
            // Apply credits in the order they were provided (should be oldest first)
            for (const pendingCreditId of pendingCreditIds) {
              if (remainingOrderTotal <= 0) break;

              try {
                // Re-validate credit availability before applying
                const promotionCreditDoc = doc(
                  firestore,
                  "promotionCredits",
                  pendingCreditId,
                );
                const creditDoc = await getDoc(promotionCreditDoc);

                if (creditDoc.exists()) {
                  const creditData = creditDoc.data();
                  const currentAvailable = Number(
                    parseFloat(creditData.available || 0).toFixed(2),
                  );
                  const isActive = creditData.active === true;

                  if (isActive && currentAvailable > 0) {
                    const creditAmount = Math.min(
                      currentAvailable,
                      remainingOrderTotal,
                    );

                    // Apply the credit as payment
                    await this.applyPayment({
                      amount: creditAmount,
                      checkNumber: "",
                      date: moment().startOf("day").add(12, "hours").toDate(),
                      notes: `Credit applied on order approval`,
                      promotionCredit: pendingCreditId,
                      type: {
                        value: "promotionCredit",
                        text: "Promotion Credit",
                      },
                    });

                    // Refresh order to get updated balance after payment
                    await this.getOrder();
                    remainingOrderTotal = parseFloat(
                      this.order.balance.due || this.order.balance.total || 0,
                    );
                  } else {
                    logWorkflowWarning(`approvalAction: pending credit no longer available or active`, {
                      workflow: "sales-order",
                      action: "approval-pending-credit-stale",
                      context: { orderId: this.orderID, pendingCreditId, balance: this.order?.balance },
                    });
                  }
                } else {
                  logWorkflowWarning(`approvalAction: pending credit no longer exists`, {
                    workflow: "sales-order",
                    action: "approval-pending-credit-missing",
                    context: { orderId: this.orderID, pendingCreditId, balance: this.order?.balance },
                  });
                }
              } catch (error) {
                console.error(
                  `Error applying pending credit ${pendingCreditId} on order approval:`,
                  error,
                );
                logWorkflowWarning(`approvalAction: error applying pending credit — ${error.message}`, {
                  workflow: "sales-order",
                  action: "approval-pending-credit-apply-error",
                  context: { orderId: this.orderID, pendingCreditId, balance: this.order?.balance },
                });
                // Continue with other credits if one fails
              }
            }
          }
        } catch (error) {
          console.error(
            "Error applying pending credits on order approval:",
            error,
          );
          logWorkflowWarning(`approvalAction: pending credits block failed — ${error.message}`, {
            workflow: "sales-order",
            action: "approval-pending-credits-failed",
            context: { orderId: this.orderID, pendingCreditIds, balance: this.order?.balance },
          });
          // Don't throw - order approval should succeed even if credit application fails
        }
      }

      const notificationsRef = collection(firestore, "notifications");
      const related = where("relatedTo.id", "==", orderDoc.id);
      const notes = where("notes", "==", "Order Approval Required");
      const viewed = where("viewed", "==", false);
      const q = query(notificationsRef, related, notes, viewed);
      const response = await getDocs(q);

      if (!response.empty) {
        await updateDoc(
          doc(firestore, "notifications", response.docs[0].id),
          {
            updated: serverTimestamp(),
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            viewed: true,
          },
          { merge: true },
        );
      }
    } catch (error) {
      throw error;
    }
  }

  // [ACCEPTED]
  async acceptedAction() {
    addBreadcrumb("acceptedAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });
    console.log("acceptedAction");
    await this.getOrder();
    await this.getLineItems();

    try {
      const batch = writeBatch(firestore);
      const orderDoc = doc(firestore, "salesOrders", this.order.id);
      console.log(this.order);
      const oldValue = store.getters.getOrderStatus.find(
          (status) => status.value === this.order.status,
        ),
        newValue = store.getters.getOrderStatus.find(
          (status) => status.value === "accepted",
        );

      console.log(oldValue, newValue);

      // Update order
      batch.set(
        orderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          status: newValue.value,
          records: {
            [newValue.value]: moment().startOf("day").add(12, "hours").toDate(),
          },
        },
        { merge: true },
      );

      // Create activity for order acceptance
      await createActivity({
        type: "status",
        action: "Order accepted",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} status updated to accepted`,
        relatedTo: "salesOrders",
        relatedID: [orderDoc.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "acceptanceStatus",
          oldValue: {
            text: oldValue.text,
            value: oldValue.value,
            color: oldValue.color,
          },
          newValue: {
            text: newValue.text,
            value: newValue.value,
            color: newValue.color,
          },
          orderNumber: this.order.orderNumber.internal,
          externalOrderUpdated:
            this.externalUpdates.leaflink.includes("statusUpdated"),
          updateType: "manual_acceptance",
        },
      });

      // Try MyTrace operations first
      await batch.commit();
      addBreadcrumb("acceptedAction: committed", {
        workflow: "sales-order",
        data: { orderId: this.orderID },
      });

      console.log("this.orderIsExternal", this.orderIsExternal);

      // External - Update Status (only after MyTrace commit succeeds)
      if (this.orderIsExternal) {
        try {
          switch (this.externalOrderType) {
            case "leaflink":
              if (this.externalUpdates.leaflink.includes("statusUpdated")) {
                let orderData = await this.leaflinkClient.getOrder();
                addBreadcrumb("leaflink: acceptedAction — checking status before transition", {
                  workflow: "sales-order",
                  data: { orderId: this.orderID, leaflinkStatus: orderData?.status ?? null },
                });
                if (orderData?.status?.toLowerCase() === "submitted") {
                  await this.leaflinkClient.transitionOrder("accept");
                }
              }
              break;
            default:
              break;
          }
        } catch (e) {
          console.log("Leaflink transition failed:", e);
          // Note: MyTrace has already been committed successfully at this point
          // Consider adding rollback logic or notification here if needed
          logWorkflowWarning(`LeafLink: acceptedAction transitionOrder failed — ${e.message}`, {
            workflow: "sales-order",
            action: "leaflink-accepted-transition",
            context: { orderId: this.orderID, balance: this.order?.balance },
          });
          throw e;
        }
      }
    } catch (error) {
      throw error;
    }
  }

  // Separate method for external API operations - follows Firebase best practices
  async processExternalLineItems(addedItems, allLineItems) {
    addBreadcrumb("leaflink: processExternalLineItems start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, addedItemCount: addedItems?.length },
    });
    await this.getOrder();
    await this.getLineItems();

    for (const cartItem of addedItems) {
      try {
        if (this.orderIsExternal && this.externalOrderType === "leaflink") {
          // Find the corresponding processed line item using proper identification
          // Match by productID + promo + amount to differentiate regular vs promo items
          const processedLineItem = allLineItems.find(
            (item) =>
              item.id === cartItem.id &&
              Boolean(item.promo) === Boolean(cartItem.promo) &&
              item.amount === cartItem.amount,
          );

          console.log("processedLineItem", processedLineItem);

          if (
            processedLineItem &&
            this.externalUpdates.leaflink.includes("orderUpdated")
          ) {
            const data = await this.leaflinkClient.createLineItem(
              processedLineItem,
            );

            // Update the line item with external ID
            await updateDoc(
              doc(firestore, "salesOrders_lineItems", processedLineItem.id),
              {
                "externalIDs.leaflinkLineItemID": data.id,
              },
            );
          }
        }
      } catch (error) {
        console.error("Failed to process external line item:", error);
        logWorkflowWarning(`LeafLink: processExternalLineItems failed — ${error.message}`, {
          workflow: "sales-order",
          action: "leaflink-line-items-sync",
          context: { orderId: this.orderID, balance: this.order?.balance },
        });
        // Continue with other items even if one fails
      }
    }
  }
  // Separate method for updating external line items
  async processExternalLineItemUpdates(existingItems) {
    addBreadcrumb("leaflink: processExternalLineItemUpdates start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, existingItemCount: existingItems?.length },
    });
    await this.getOrder();
    await this.getLineItems();

    for (const item of existingItems) {
      try {
        if (
          this.orderIsExternal &&
          this.externalOrderType === "leaflink" &&
          item.needsQTYPatch &&
          item.externalIDs?.leaflinkLineItemID &&
          this.externalUpdates.leaflink.includes("orderUpdated")
        ) {
          const externalItem = {
            id: item.externalIDs.leaflinkLineItemID,
          };

          const payloadData = {
            ordered_unit_price: {
              amount: item.amount,
              currency: "USD",
            },
            sale_price: {
              amount: item.amount,
              currency: "USD",
            },
            notes: item.notes,
            quantity: item.qty,
            is_sample: item.sample ?? false,
          };

          await this.leaflinkClient.updateLineItem(externalItem, payloadData);
        }
      } catch (error) {
        console.error("Failed to update Leaflink line item:", error);
        logWorkflowWarning(`LeafLink: processExternalLineItemUpdates failed — ${error.message}`, {
          workflow: "sales-order",
          action: "leaflink-line-items-update-sync",
          context: { orderId: this.orderID, balance: this.order?.balance },
        });
      }
    }
  }

  async checkOverpaymentCredits(updatedBalance = null) {
    addBreadcrumb("checkOverpaymentCredits: evaluating", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        updatedBalance,
        currentDue: this.order?.balance?.due,
        paymentCount: this.payments?.length,
      },
    });
    const batch = writeBatch(firestore);

    await this.getOrder();
    await this.getPayments();

    const brandStatsDoc = doc(
      firestore,
      "brandStats",
      `${this.order.account.id}_${this.order.brand.id}`,
    );

    const orderDoc = doc(firestore, "salesOrders", this.order.id);

    // Use passed balance if available, otherwise fetch
    let balanceDue;
    if (updatedBalance !== null) {
      // Neccessary to fix the race condition where this.getOrder() is ran before the
      // transaction in updateLineItems is committed
      balanceDue = Number(updatedBalance);
    } else {
      balanceDue = Number(this.order.balance.due);
    }

    let filteredPayments = this.payments.filter(
      (payment) =>
        !payment.flags?.reversed && payment.paymentType !== "promotionCredit",
    );

    let runningBalance = Math.abs(balanceDue);

    if (balanceDue < 0) {
      addBreadcrumb("checkOverpaymentCredits: negative balance — creating/updating credits", {
        workflow: "sales-order",
        data: {
          orderId: this.orderID,
          balanceDue,
          overpaymentPaymentCount: filteredPayments.filter((p) => p.overpaymentCreditID).length,
        },
      });
      // Check if there is already existing overpayment credits related to a payment
      if (filteredPayments.find((item) => item.overpaymentCreditID)) {
        // Get all overpayment payments

        let overpaymentPayments = filteredPayments.filter(
          (item) => item.overpaymentCreditID,
        );

        let sortedPayments = overpaymentPayments.sort(
          (a, b) =>
            moment(b.created.seconds * 1000).toDate() -
            moment(a.created.seconds * 1000).toDate(), // Sort newest to oldest
        );

        while (runningBalance > 0 && sortedPayments.length > 0) {
          let overpaymentPayment = sortedPayments.shift();

          if (!overpaymentPayment) break;

          // Overpayment amount should never be greater than payment amount
          let amountToAdd = Math.min(
            runningBalance,
            Number(
              parseFloat(
                overpaymentPayment.amount -
                  (overpaymentPayment.creditsApplied ?? 0),
              ).toFixed(2),
            ),
          );
          // Skip payments that already have their entire amount as overpayment credit
          if (amountToAdd === 0) continue;

          // Get the overpayment credit document
          let overpaymentCreditDoc = doc(
            firestore,
            "promotionCredits",
            overpaymentPayment.overpaymentCreditID,
          );
          let paymentDoc = doc(
            firestore,
            "salesOrders_payments",
            overpaymentPayment.id,
          );
          let overpaymentCredit = await getDoc(overpaymentCreditDoc);
          let overpaymentCreditData = overpaymentCredit.data();

          let newTotal = Number(
            parseFloat(overpaymentCreditData.total + amountToAdd).toFixed(2),
          );
          let newAvailable = Number(
            parseFloat(overpaymentCreditData.available + amountToAdd).toFixed(
              2,
            ),
          );
          let newCreditsApplied = Number(
            parseFloat(overpaymentPayment.creditsApplied + amountToAdd).toFixed(
              2,
            ),
          );

          batch.set(
            overpaymentCreditDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              active: true,
              total: newTotal,
              available: newAvailable,
            },
            {
              merge: true,
            },
          );

          // Brand Stats - Metrics
          batch.set(
            brandStatsDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              metrics: {
                promotionCredits: {
                  total: increment(amountToAdd),
                  available: increment(amountToAdd),
                },
              },
            },
            { merge: true },
          );
          // Payment Doc
          batch.set(
            paymentDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              creditsApplied: newCreditsApplied,
            },
            { merge: true },
          );

          runningBalance = Number(
            parseFloat(runningBalance - amountToAdd).toFixed(2),
          );
        }
      }
      if (runningBalance > 0) {
        // Create new overpayment credit(s) related to the most recent payment(s) that is not a credit
        let sortedPayments = filteredPayments
          .filter((payment) => !payment.overpaymentCreditID)
          .sort(
            (a, b) =>
              moment(b.created.seconds * 1000).toDate() -
              moment(a.created.seconds * 1000).toDate(),
          );

        while (runningBalance > 0 && sortedPayments.length > 0) {
          let mostRecentPayment = sortedPayments.shift();

          if (!mostRecentPayment) break;

          let creditDoc = doc(collection(firestore, "promotionCredits"));

          const payableToRef = collection(firestore, "payableTo");
          const state = where(
              "location.state.stateID",
              "==",
              this.order.location.shipping.state.stateID,
            ),
            brand = where("brandID", "==", this.order.brand.id);
          const q = query(payableToRef, state, brand);
          const response = await getDocs(q, limit(1));

          let payableTo = {
            id: response.docs[0].id,
            ...response.docs[0].data(),
          };

          const brandAbbreviation = this.order.brand.name
            .split(" ")
            .map((word) => word.charAt(0))
            .join("");

          let stateAbbrevation =
            this.order.location.shipping.state.abbreviation;
          let existingCredits = payableTo.records?.creditMemos ?? 0;

          let accountDoc = doc(firestore, "accounts", this.order.account.id);
          let accountResponse = await getDoc(accountDoc);

          let countryID = accountResponse.data().location.shipping.countryID;

          let creditsApplied = Math.min(
            runningBalance,
            mostRecentPayment.amount,
          );

          runningBalance = Number(
            parseFloat(runningBalance - creditsApplied).toFixed(2),
          );

          batch.set(
            creditDoc,
            {
              created: serverTimestamp(),
              updated: serverTimestamp(),
              createdBy: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              account: {
                name: this.order.account.name,
                id: this.order.account.id,
              },
              brand: {
                id: this.order.brand.id,
                name: this.order.brand.name,
              },
              location: {
                countryID: countryID,
                state: this.order.location.shipping.state.stateID,
              },
              active: true,
              reversed: false,
              date: moment(mostRecentPayment.paymentDate.seconds * 1000)
                .startOf("day")
                .add(9, "hours")
                .tz("America/Los_Angeles")
                .toDate(),
              orderNumber: {
                internal: `${brandAbbreviation}-cm-${stateAbbrevation}-${
                  existingCredits + 1
                }`,
                external: "",
              },
              total: creditsApplied,
              available: creditsApplied,
              type: "overpayment",
              relatedID: this.order.id,
              notes: "",
              details: "",
              promotion: null,
            },
            {
              merge: true,
            },
          );

          // Payment Doc
          batch.set(
            doc(firestore, "salesOrders_payments", mostRecentPayment.id),
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              creditsApplied: creditsApplied,
              overpaymentCreditID: creditDoc.id,
            },
            { merge: true },
          );

          // Brand Stats - Metrics
          batch.set(
            brandStatsDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              metrics: {
                promotionCredits: {
                  total: increment(creditsApplied),
                  available: increment(creditsApplied),
                },
              },
            },
            { merge: true },
          );

          batch.set(
            doc(firestore, "payableTo", payableTo.id),
            {
              records: {
                creditMemos: increment(1),
              },
            },
            { merge: true },
          );
        }
      }

      let leaflinkEmail = null;
      let staff = [];
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
              let data = await this.leaflinkClient.getUsers();
              staff = data.results;
              const integrationsRef = collection(firestore, "integrations");
              const platform = where("platform", "==", "leaflink");
              const integrationsQ = query(integrationsRef, platform);
              const integrationsResponse = await getDocs(
                integrationsQ,
                limit(1),
              );

              if (!integrationsResponse.empty) {
                leaflinkEmail = integrationsResponse.docs[0].data().email;
              }

              if (!leaflinkEmail) {
                logWorkflowWarning("checkOverpaymentCredits: aborted — LeafLink integration email not found, batch not committed", {
                  workflow: "sales-order",
                  action: "leaflink-overpayment-email-missing",
                  context: { orderId: this.orderID, balanceDue, balance: this.order?.balance },
                });
                return;
              }
            }
            break;
          default:
            break;
        }
      }

      let userLeaflinkInfo = staff.find(
        (staffMember) => staffMember.email === leaflinkEmail,
      );

      if (this.orderIsExternal) {
        try {
          switch (this.externalOrderType) {
            case "leaflink":
              if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
                let paymentDateMoment = moment();
                let actualPaymentDate = moment(
                  paymentDateMoment.clone().format("MM/DD/YYYY"),
                )
                  .add(9, "hours")
                  .tz("America/Los_Angeles");
                let paymentData = {
                  order: this.order.externalIDs.leaflink,
                  recorded_by: userLeaflinkInfo.id,
                  total: {
                    amount: balanceDue,
                    currency: "usd",
                  },
                  payment_date: moment(actualPaymentDate).toISOString(),
                  reason: "Automatic overpayment credit(s) creation",
                  payment_type: "other",
                  check_number: "",
                };
                await this.leaflinkClient.createPayment(paymentData);
              }
              break;
            default:
              break;
          }
        } catch (e) {
          logWorkflowWarning(`LeafLink: createPayment (checkOverpaymentCredits credit creation) failed — ${e.message}`, {
            workflow: "sales-order",
            action: "leaflink-overpayment-create-payment",
            context: { orderId: this.orderID, balance: this.order?.balance },
          });
          shouldReturn = true;
          return;
        }
      }

      // Update order
      batch.set(
        orderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          balance: {
            due: parseFloat(0).toFixed(2),
          },
        },
        { merge: true },
      );
    } else {
      addBreadcrumb("checkOverpaymentCredits: positive balance — decrementing credits", {
        workflow: "sales-order",
        data: {
          orderId: this.orderID,
          balanceDue,
          overpaymentPaymentCount: filteredPayments.filter((p) => p.overpaymentCreditID).length,
        },
      });
      // Check if there is an existing overpayment credit related to a payment
      //      - If so, decrement the credit amount the minimum of: the running balance and the credit amount
      if (filteredPayments.find((item) => item.overpaymentCreditID)) {
        // Get the overpayment payments
        let overpaymentPayments = filteredPayments.filter(
          (item) => item.overpaymentCreditID,
        );

        let sortedPayments = overpaymentPayments.sort(
          (a, b) =>
            moment(a.created.seconds * 1000).toDate() -
            moment(b.created.seconds * 1000).toDate(), // Sort oldest to newest
        );

        let newBalance = Number(parseFloat(balanceDue).toFixed(2));

        while (runningBalance > 0 && sortedPayments.length > 0) {
          let overpaymentPayment = sortedPayments.shift();

          // Get the overpayment credit document
          let overpaymentCreditDoc = doc(
            firestore,
            "promotionCredits",
            overpaymentPayment.overpaymentCreditID,
          );
          let paymentDoc = doc(
            firestore,
            "salesOrders_payments",
            overpaymentPayment.id,
          );
          let overpaymentCredit = await getDoc(overpaymentCreditDoc);
          let overpaymentCreditData = overpaymentCredit.data();

          let amountToSubtract = Math.min(
            runningBalance,
            overpaymentCreditData.total,
          );

          if (amountToSubtract === 0) continue;

          let newTotal = Number(
            parseFloat(overpaymentCreditData.total - amountToSubtract).toFixed(
              2,
            ),
          );
          let newAvailable = Number(
            parseFloat(
              overpaymentCreditData.available - amountToSubtract,
            ).toFixed(2),
          );
          let newCreditsApplied = Number(
            parseFloat(
              overpaymentPayment.creditsApplied - amountToSubtract,
            ).toFixed(2),
          );

          if (newTotal <= 0) {
            batch.delete(overpaymentCreditDoc);
            batch.set(
              paymentDoc,
              {
                updated: serverTimestamp(),
                lastUpdated: {
                  name: store.getters.getUser.name,
                  uid: store.getters.getUser.uid,
                },
                overpaymentCreditID: null,
              },
              { merge: true },
            );
          } else {
            batch.set(
              overpaymentCreditDoc,
              {
                updated: serverTimestamp(),
                lastUpdated: {
                  name: store.getters.getUser.name,
                  uid: store.getters.getUser.uid,
                },
                active: true,
                total: newTotal,
                available: newAvailable,
              },
              {
                merge: true,
              },
            );
          }

          newBalance = Number(
            parseFloat(newBalance - amountToSubtract).toFixed(2),
          );

          // Update order
          batch.set(
            orderDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              balance: {
                due: parseFloat(newBalance).toFixed(2),
              },
            },
            { merge: true },
          );

          // Brand Stats - Metrics
          batch.set(
            brandStatsDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              metrics: {
                promotionCredits: {
                  total: increment(-amountToSubtract),
                  available: increment(-amountToSubtract),
                },
              },
            },
            { merge: true },
          );
          // Payment Doc
          batch.set(
            paymentDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              creditsApplied: newCreditsApplied,
            },
            { merge: true },
          );

          runningBalance = Number(
            parseFloat(runningBalance - amountToSubtract).toFixed(2),
          );

          let leaflinkEmail = null;
          let staff = [];
          if (this.orderIsExternal) {
            switch (this.externalOrderType) {
              case "leaflink":
                if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
                  let data = await this.leaflinkClient.getUsers();
                  staff = data.results;
                  const integrationsRef = collection(firestore, "integrations");
                  const platform = where("platform", "==", "leaflink");
                  const integrationsQ = query(integrationsRef, platform);
                  const integrationsResponse = await getDocs(
                    integrationsQ,
                    limit(1),
                  );

                  if (!integrationsResponse.empty) {
                    leaflinkEmail = integrationsResponse.docs[0].data().email;
                  }

                  if (!leaflinkEmail) {
                    logWorkflowWarning("checkOverpaymentCredits: aborted mid-loop — LeafLink integration email not found, batch not committed", {
                      workflow: "sales-order",
                      action: "leaflink-overpayment-credit-reversal-email-missing",
                      context: { orderId: this.orderID, balanceDue, balance: this.order?.balance },
                    });
                    return;
                  }
                }
                break;
              default:
                break;
            }
          }

          let userLeaflinkInfo = staff.find(
            (staffMember) => staffMember.email === leaflinkEmail,
          );

          if (this.orderIsExternal) {
            try {
              switch (this.externalOrderType) {
                case "leaflink":
                  if (
                    this.externalUpdates.leaflink.includes("paymentsApplied")
                  ) {
                    let paymentDateMoment = moment();
                    let actualPaymentDate = moment(
                      paymentDateMoment.clone().format("MM/DD/YYYY"),
                    )
                      .add(9, "hours")
                      .tz("America/Los_Angeles");
                    let paymentData = {
                      order: this.order.externalIDs.leaflink,
                      recorded_by: userLeaflinkInfo.id,
                      total: {
                        amount: Number(amountToSubtract),
                        currency: "usd",
                      },
                      payment_date: moment(actualPaymentDate).toISOString(),
                      reason: "Automatic overpayment credit reversal",
                      payment_type: "other",
                      check_number: "",
                    };
                    await this.leaflinkClient.createPayment(paymentData);
                    break;
                  }
                default:
                  break;
              }
            } catch (e) {
              logWorkflowWarning(`LeafLink: createPayment (checkOverpaymentCredits credit reversal) failed — ${e.message}`, {
                workflow: "sales-order",
                action: "leaflink-overpayment-credit-reversal",
                context: { orderId: this.orderID, balance: this.order?.balance },
              });
              shouldReturn = true;
              return;
            }
          }
        }
      }
    }

    await batch.commit();
    addBreadcrumb("checkOverpaymentCredits: committed", {
      workflow: "sales-order",
      data: { orderId: this.orderID, balanceDue },
    });
  }
}

export default SalesOrderClient;
